import React, { useState, Fragment, useContext, useEffect, useRef, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import moment from 'moment';
import LoadingInForm from '../../components/LoadingInForm';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import DatePicker, { registerLocale } from 'react-datepicker';
import { _generateDate } from '../../util/convert';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import ptBR from 'date-fns/locale/pt-BR';
import { Form, Col } from 'react-bootstrap';
import AddButton from '../../components/AddButton';
import Button from '../../components/Button';
import DeleteButton from '../../components/DeleteButton';
import DuplicateButton from '../../components/DuplicateButton';
import EditButton from '../../components/EditButton';
import If from '../../components/If';
import Fieldset from '../../components/Fieldset';
import AlertError from '../../components/AlertError';
import AlertSuccess from '../../components/AlertSuccess';
import ConfirmationModal from '../../components/ConfirmationModal';
import PlantioMilhoModal from './PlantioModal';
import PlantioSojaModal from './PlantioSojaModal';
import CultivoModal from './CultivoModal';
import ColheitaModalComponent from './ColheitaModal';
import MonitoramentoModal from './MonitoramentoModal';
import DestruicaoDescarteModal from './DestruicaoDescarteModal';
import AmostragemModal from './AmostragemModal';
import AvaliacaoAgronomicaModal from './AvaliacaoAgronomicaModal';
import DestruicaoBordaduraModal from './DestruicaoBordaduraModal';
import PlantioOutraCulturaModal from './PlantioOutraCulturaModal';
import ExtensaoMonitoramentoModal from './ExtensaoMonitoramentoModal';
import ReportesRelatoriosModal from './ReportesRelatoriosModal';
import TransporteModal from './TransporteModal';
import RegistroVisitasModal from './RegistroVisitasModal';
import AssinaturaModal from './AssinaturaModal';
import FilesUpload from '../../components/form/FilesUpload';
import { Context } from '../../context/AuthContext'
import { DecodedToken } from '../../models/user';
import { STRINGS } from '../../util/strings';
import jwtDecode from 'jwt-decode';
import {
	CadernoLPMA, Cultura, Enum, TecnicoPrincipal, CadernoLPMALocalLista, CadernoLPMATransporte, CadernoLPMARegistroVisitas, CadernoLPMAPlantio, CadernoLPMAPlantioSoja,
	CadernoLPMACultivo, CadernoLPMAColheita, CadernoLPMAMonitoramento, CadernoLPMADestruicaoDescarte, CadernoLPMAAmostragem,
	CadernoLPMAAvaliacaoAgronomica, CadernoLPMADestruicaoBordadura, CadernoLPMAPlantioOutraCultura, CadernoLPMAExtensaoMonitoramento,
	CadernoLPMAReportesRelatorios, CadernoLPMAAnotacoes, CadernoLPMAAssinatura, CadernoLPMADocumentoPrincipal
} from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import { traduzirErroCadernoLPMA } from '../../util/cadernoLPMA/erros';
import { getCadernoLpmaFormEditFlags } from '../../users/CadernoLPMA/cadernoLpmaFormPermissions';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/core/Alert';
import './typography.scss';

import api from '../../services/api';
import Modal from '../../components/Modal';

registerLocale('pt-BR', ptBR);

type DocumentoUploadDefaultValue = {
	guid: string;
	name: string;
	desc: string;
	legenda: string;
};

type DocumentoPrincipalUploadItem = {
	blob?: File | null;
	guid?: string | null;
	name?: string;
};

type CadernoLPMAColecoes = Partial<Pick<
	CadernoLPMA,
	| 'transportes'
	| 'registroVisitas'
	| 'plantios'
	| 'plantiosSoja'
	| 'cultivos'
	| 'colheitas'
	| 'monitoramentos'
	| 'destruicaoDescartes'
	| 'amostragens'
	| 'avaliacoesAgronomicas'
	| 'destruicaoBordadura'
	| 'plantiosOutraCultura'
	| 'extensaoMonitoramentos'
	| 'reportesRelatorios'
	| 'anotacoes'
	| 'assinaturas'
>>;

type SalvarRascunhoOptions = {
	redirecionarAoCriar?: boolean;
	rolarAoTopo?: boolean;
};

const arquivoParaBase64 = (arquivo: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const resultado = reader.result;
			if (typeof resultado !== 'string') {
				reject(new Error('Falha ao converter arquivo para base64.'));
				return;
			}
			const base64 = resultado.includes(',') ? resultado.split(',')[1] : resultado;
			resolve(base64);
		};
		reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler arquivo.'));
		reader.readAsDataURL(arquivo);
	});

const obterColecaoEntidade = <T,>(fonte: any, ...nomes: string[]): T[] => {
	if (!fonte) {
		return [];
	}

	for (const nome of nomes) {
		const valor = fonte[nome];
		if (Array.isArray(valor)) {
			return valor as T[];
		}
	}

	return [];
};

const normalizarDocumentoPrincipal = (doc: any): CadernoLPMADocumentoPrincipal => ({
	id: doc?.id ?? doc?.Id,
	cadernoLPMAId: doc?.cadernoLPMAId ?? doc?.CadernoLPMAId,
	nomeArquivo: doc?.nomeArquivo ?? doc?.NomeArquivo ?? '',
	arquivoGuid: doc?.arquivoGuid ?? doc?.ArquivoGuid ?? '',
	contentType: doc?.contentType ?? doc?.ContentType,
	caminhoBlob: doc?.caminhoBlob ?? doc?.CaminhoBlob,
});

const obterDocumentosPrincipaisDaEntidade = (fonte: any): CadernoLPMADocumentoPrincipal[] =>
	obterColecaoEntidade<any>(fonte, 'documentosPrincipais', 'DocumentosPrincipais').map(normalizarDocumentoPrincipal);

const mapearDocumentosPrincipaisParaDefaultValue = (
	fonte?: CadernoLPMA | CadernoLPMADocumentoPrincipal[] | null,
): DocumentoUploadDefaultValue[] => {
	const documentos = Array.isArray(fonte)
		? fonte.map(normalizarDocumentoPrincipal)
		: obterDocumentosPrincipaisDaEntidade(fonte);

	return documentos
		.filter((doc) => !!doc?.arquivoGuid || !!doc?.nomeArquivo)
		.map((doc) => ({
			guid: doc.arquivoGuid ?? '',
			name: doc.nomeArquivo ?? '',
			desc: '',
			legenda: '',
		}));
};

const estiloContainerGradeLpma: React.CSSProperties = { height: '260px', width: '100%' };

const gridOptionsGradeLpma = {
	headerHeight: 42,
	rowHeight: 42,
	tooltipShowDelay: 400,
};

const defaultColDefGradeLpma = {
	sortable: true,
	resizable: true,
	wrapText: false,
	autoHeight: false,
	flex: 1,
	minWidth: 130,
	tooltipValueGetter: (params: any) => {
		const valor = params?.valueFormatted ?? params?.value;
		if (valor === null || valor === undefined) {
			return '';
		}
		return String(valor);
	},
	cellStyle: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
};

const textoGradeLpma = (valor: any): string => {
	if (valor === null || valor === undefined) {
		return '';
	}
	return String(valor).trim();
};

const dataGradeLpma = (valor: any): string => (valor ? _generateDate(valor, 'Pt') : '');

const primeiroTextoGradeLpma = (...valores: any[]): string => {
	for (const valor of valores) {
		const texto = textoGradeLpma(valor);
		if (texto) {
			return texto;
		}
	}
	return '';
};

const opcaoMarcadaGradeLpma = (valor: any): boolean =>
	valor === true || valor === 1 || valor === '1' || valor === 'true' || valor === 'True' || valor === 'Sim';

const formatBooleanDisplay = (valor: any): string => {
	if (valor === null || valor === undefined || valor === '') {
		return '';
	}
	if (opcaoMarcadaGradeLpma(valor)) {
		return 'Sim';
	}
	if (
		valor === false
		|| valor === 0
		|| valor === '0'
		|| valor === 'false'
		|| valor === 'False'
		|| valor === 'Não'
		|| valor === 'Nao'
	) {
		return 'Não';
	}
	return valor ? 'Sim' : 'Não';
};

const rotuloComDetalheGradeLpma = (marcado: any, rotulo: string, detalhe?: any): [any, string] => {
	const extra = textoGradeLpma(detalhe);
	return [marcado, extra ? `${rotulo}: ${extra}` : rotulo];
};

const juntarMarcadosGradeLpma = (pares: Array<[any, string]>): string =>
	pares
		.filter(([valor]) => opcaoMarcadaGradeLpma(valor))
		.map(([, rotulo]) => rotulo)
		.join(', ');

const lerListaJsonGradeLpma = (json: any): any[] => {
	const texto = textoGradeLpma(json);
	if (!texto) {
		return [];
	}
	try {
		const arr = JSON.parse(texto);
		return Array.isArray(arr) ? arr : [];
	} catch {
		return [];
	}
};

const responsaveisLimpezaGradeLpma = (...jsons: any[]): string => {
	const nomes: string[] = [];
	jsons.flatMap((json) => lerListaJsonGradeLpma(json)).forEach((item) => {
		const nome = textoGradeLpma(item?.responsavel ?? item?.Responsavel);
		if (nome && !nomes.includes(nome)) {
			nomes.push(nome);
		}
	});
	return nomes.join(', ');
};

const formatarAmisTransporte = (data: any): string => {
	const lista: string[] = [];
	const principal = textoGradeLpma(data?.ami);
	if (principal) {
		lista.push(principal);
	}
	lerListaJsonGradeLpma(data?.amisAdicionaisJson).forEach((item) => {
		if (typeof item === 'string' && item.trim()) {
			lista.push(item.trim());
		} else if (item && typeof item === 'object' && typeof item.ami === 'string' && item.ami.trim()) {
			lista.push(item.ami.trim());
		}
	});
	return lista.join(', ');
};

const formatarOrigemTransporte = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.multiplicadaCampo, 'Campo/LPMA'],
		[data?.multiplicadaCage, 'Casa de Vegetação/CAGE'],
		[data?.importada, 'Importada'],
	]);

const formatarDestinoTransporte = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.destruicaoDescarte, 'Destruição e descarte'],
		[data?.armazenamento, 'Armazenamento'],
		[data?.devolucao, 'Devolução ou envio'],
	]);

const formatTipoColheita = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.tipoColheitaMecanica, 'Mecânica'],
		[data?.tipoColheitaManual, 'Manual'],
	]).replace(/, /g, ' / ');

const formatarDestinosColheita = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.destinoMovimentacaoMesmaUnidade, 'Movimentação na mesma unidade'],
		[data?.destinoEnvioOutraUnidade, 'Envio para outra unidade'],
		[data?.destinoDestruicaoAreaExperimental, 'Destruição na área experimental'],
		[data?.destinoDestruicaoAreaDescarteProprio, 'Destruição na área de descarte'],
		rotuloComDetalheGradeLpma(data?.destinoDestruicaoAreaOutroSite, 'Destruição em outro site', data?.destinoDescricaoOutroSite),
		rotuloComDetalheGradeLpma(data?.destinoOutro, 'Outro', data?.destinoOutroDescricao),
	]);

const formatarAtividadesCultivo = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.placaBiosseguranca, 'Placa de biossegurança'],
		[data?.areaDelimitada, 'Área delimitada'],
		[data?.sojaBordadura, 'Bordadura (soja)'],
		[data?.algodaoBordadura, 'Bordadura (algodão)'],
		[data?.sojaIsolamento, 'Isolamento (soja)'],
		[data?.algodaoIsolamento, 'Isolamento (algodão)'],
		[data?.inspecaoRotina, 'Inspeção de rotina'],
		[data?.cercasCultivo, 'Cercas'],
		[data?.montagemIrrigacao, 'Montagem da irrigação'],
		[data?.irrigacaoPosPlantio, 'Irrigação após o plantio'],
		[data?.avaliacoesCultivo, 'Avaliações'],
		[data?.coleta, 'Coleta'],
		[data?.capinaRocagem, 'Capina/roçagem'],
		[data?.aplicacaoPulverizacao, 'Aplicação/pulverização'],
		[data?.aplicacaoPulverizacaoFungicida, 'Fungicida'],
		[data?.aplicacaoPulverizacaoHerbicida, 'Herbicida'],
		[data?.aplicacaoPulverizacaoInseticida, 'Inseticida'],
		rotuloComDetalheGradeLpma(data?.aplicacaoPulverizacaoOutros, 'Outros (pulverização)', data?.aplicacaoPulverizacaoOutrosDescricao),
		[data?.adubacao, 'Adubação'],
		[data?.adubacaoCobertura, 'Adubação de cobertura'],
		[data?.adubacaoFoliar, 'Adubação foliar'],
		rotuloComDetalheGradeLpma(data?.adubacaoOutros, 'Outros (adubação)', data?.adubacaoOutrosDescricao),
		[data?.destruicaoBordadura, 'Destruição da bordadura'],
		[data?.colheitaDestruicao, 'Colheita/destruição'],
		rotuloComDetalheGradeLpma(data?.outrasAtividades, 'Outras atividades', data?.observacoesOutrasAtividades),
	]);

const mapaMetodoMecanicoMonitoramento: Record<string, string> = {
	'1': 'Grade',
	'2': 'Enxada rotativa',
	'3': 'Roçadeira',
	'4': 'Triton',
	'5': 'Arranquio / capina manual',
};

const rotuloMapaGradeLpma = (valor: any, mapa: Record<string, string>): string => {
	const texto = textoGradeLpma(valor);
	return mapa[texto] ?? texto;
};

const formatarMetodoDestruicaoMonitoramento = (data: any): string => {
	const partes = juntarMarcadosGradeLpma([
		[data?.metodoDestruicaoQuimico, 'Químico/Herbicida'],
		[data?.metodoDestruicaoMecanico, 'Mecânico'],
		rotuloComDetalheGradeLpma(data?.metodoDestruicaoOutros, 'Outros', data?.observacoesMetodoDestruicaoOutros),
	]);
	const mecanico = opcaoMarcadaGradeLpma(data?.metodoDestruicaoMecanico)
		? rotuloMapaGradeLpma(data?.metodoDestruicaoMecanicoTipo, mapaMetodoMecanicoMonitoramento)
		: '';
	return [partes, mecanico].filter(Boolean).join(' | ');
};

const formatarTipoMaterialDestruicao = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.tipoMaterialDestruidoEmbalagens, 'Embalagens/envelopes'],
		[data?.tipoMaterialDestruidoSementes, 'Sementes/Grãos'],
		[data?.tipoMaterialDestruidoCapulho, 'Capulho/fibra/caroço'],
		[data?.tipoMaterialDestruidoPlantulas, 'Plântulas/Plantas'],
		rotuloComDetalheGradeLpma(data?.tipoMaterialDestruidoOutros, 'Outros', data?.outrosTipoMaterialDestruido),
	]);

const formatarLocalDestruicao = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.localDestruicaoAreaExperimental, 'Área experimental'],
		[data?.localDestruicaoAreaDescarte, 'Área de descarte'],
		[data?.localDestruicaoSalaManipulacao, 'Sala de Manipulação/OGM'],
		rotuloComDetalheGradeLpma(data?.localDestruicaoOutros, 'Outros', data?.outrosLocalDestruicao),
	]);

const formatarMetodoDestruicaoDescarte = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.metodoDestruicaoEnterrio, 'Enterrio profundo'],
		[data?.metodoDestruicaoAutoclavagem, 'Autoclavagem'],
		[data?.metodoDestruicaoMoagem, 'Moagem'],
		[data?.metodoDestruicaoIncineracao, 'Incineração'],
		[data?.metodoDestruicaoIncorporacao, 'Incorporação/grade'],
		rotuloComDetalheGradeLpma(data?.metodoDestruicaoOutros, 'Outros', data?.outrosMetodoDestruicao),
	]);

const formatarTipoMaterialAmostragem = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.tipoMaterialAmostradoFolhas, 'Folhas'],
		[data?.tipoMaterialAmostradoRaizes, 'Raízes'],
		[data?.tipoMaterialAmostradoGraos, 'Grãos / sementes'],
		rotuloComDetalheGradeLpma(data?.tipoMaterialAmostradoOutros, 'Outros', data?.outrosTipoMaterialAmostrado),
	]);

const formatarTipoMaterialAvaliacao = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.tipoMaterialAvaliadoGraos, 'Sementes / grãos'],
		[data?.tipoMaterialAvaliadoRaizes, 'Raiz'],
		[data?.tipoMaterialAvaliadoFolhas, 'Folha'],
		[data?.tipoMaterialAvaliadoVagem, 'Vagem'],
		[data?.tipoMaterialAvaliadoCapulho, 'Capulho e/ou caroço'],
		rotuloComDetalheGradeLpma(data?.tipoMaterialAvaliadoOutros, 'Outros', data?.outrosTipoMaterialAvaliado),
	]);

const formatarTipoAvaliacaoAgronomica = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.tipoAvaliacaoPesagem, 'Pesagem'],
		[data?.tipoAvaliacaoDeterminacaoUmidade, 'Determinação umidade grão/semente'],
		[data?.tipoAvaliacaoContagem, 'Contagem sementes'],
		[data?.tipoAvaliacaoDescarocamento, 'Descaroçamento'],
		[data?.tipoAvaliacaoDeterminacaoPluma, 'Determinação % de pluma'],
		[data?.tipoAvaliacaoPreparoAmostras, 'Preparo de amostras'],
		[data?.tipoAvaliacaoTratamento, 'Tratamento de sementes'],
		[data?.tipoAvaliacaoMoagem, 'Moagem'],
		rotuloComDetalheGradeLpma(data?.tipoAvaliacaoOutros, 'Outras', data?.outrosTipoAvaliacao),
	]);

const formatarMetodoDestruicaoBordadura = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.metodoDestruicaoMecanico, 'Mecânico'],
		[data?.metodoDestruicaoMecanicoGrade, 'Grade'],
		[data?.metodoDestruicaoMecanicoRocadeira, 'Roçadeira'],
		[data?.metodoDestruicaoMecanicoEnxada, 'Enxada rotativa'],
		[data?.metodoDestruicaoMecanicoTriton, 'Triton'],
		[data?.metodoDestruicaoMecanicoCapina, 'Capina manual'],
		[data?.metodoDestruicaoQuimico, 'Químico'],
		[data?.metodoDestruicaoQuimicoHerbicida, 'Herbicida'],
		rotuloComDetalheGradeLpma(data?.metodoDestruicaoQuimicoOutros, 'Outro', data?.outrosMetodoDestruicaoQuimico),
	]);

const formatarMotivoReporte = (data: any): string =>
	juntarMarcadosGradeLpma([
		[data?.motivoTempestades, 'Tempestades, enxuradas e vendavais'],
		[data?.motivoAnimais, 'Presença de animais'],
		[data?.motivoPessoas, 'Presença de pessoas não autorizadas'],
		rotuloComDetalheGradeLpma(data?.motivoOutros, 'Outros', data?.motivoOutrosDescricao),
	]);

const formatarTipoPlantio = (mecanico: any, manual: any): string =>
	juntarMarcadosGradeLpma([
		[mecanico, 'Mecânico'],
		[manual, 'Manual'],
	]).replace(/, /g, ' / ');

type CadernoLPMAFormMode = 'create' | 'edit';

interface CadernoLPMAFormProps {
	initialEntity?: CadernoLPMA;
	mode?: CadernoLPMAFormMode;
}

export default function Caderno_LPMA({ initialEntity, mode = 'create' }: CadernoLPMAFormProps = {}) {
	const MODO_VIEW = {
		DADOS_PRINCIPAIS: 1,
		TRANSPORTE: 2,
		REGISTROVISITAS: 3,
		PLANTIO: 4,
		CULTIVO: 5,
		COLHEITA: 6,
		MONITORAMENTO: 7,
		DESTRUICAO: 8,
		AMOSTRAGEM: 9,
		AVALIACAOAGRONOMICA: 10,
		DESTRUICAOBORDADURA: 11,
		PLANTIOOUTRACULTURA: 12,
		EXTENSAOMONITORAMENTO: 13,
		REPORTES: 14,
		ANOTACOES: 15,
		ASSINATURA: 16
	}

	const history = useHistory();
	const location = useLocation<{ initialEntity?: CadernoLPMA }>();
	const locationInitialEntity = location.state?.initialEntity;
	const { token } = useContext(Context);
	const [loading, setLoading] = useState<boolean>(false);
	const [success, setSuccess] = useState<boolean>(false);
	const [error, setError] = useState<any>(null);
	const [modoFluxo, setModoFluxo] = useState<Number>(MODO_VIEW.DADOS_PRINCIPAIS);
	const isEditMode = mode === 'edit';
	const [entity, setEntity] = useState<CadernoLPMA | undefined>(initialEntity ?? locationInitialEntity);
	const decoded: DecodedToken = jwtDecode(token.access_token);
	const { canEditDadosPrincipais, canEditOutrasAbas, canView } = useMemo(
		() => getCadernoLpmaFormEditFlags(decoded?.roles),
		[token.access_token],
	);
	const [isConfirmation, setIsConfirmation] = useState<boolean>(false);
	const [loadingDraft, setLoadingDraft] = useState<boolean>(false);
	const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
	const [successSubmit, setSuccessSubmit] = useState<boolean>(false);
	const [errorSubmit, setErrorSubmit] = useState<any>(null);
	const [toast, setToast] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>(
		{ open: false, severity: 'success', message: '' },
	);

	const [culturas, setCulturas] = useState<Cultura[]>([]);
	const [enumNomeCientifico, setEnumNomeCientifico] = useState<Enum[]>([]);
	const [tecnicosPrincipais, setTecnicosPrincipais] = useState<TecnicoPrincipal[]>([]);
	const [tecnicosResponsaveisCampo, setTecnicosResponsaveisCampo] = useState<TecnicoPrincipal[]>([]);
const [dadosPrincipaisCarregados, setDadosPrincipaisCarregados] = useState<boolean>(false);
const [opcoesLocais, setOpcoesLocais] = useState<LocalOption[]>([]);
const bloquearAbasPorCarregamentoDadosPrincipais =
	isEditMode && canEditDadosPrincipais && canEditOutrasAbas && !dadosPrincipaisCarregados;

	const documentosPrincipaisDefaultValue = useMemo(
		() => mapearDocumentosPrincipaisParaDefaultValue(entity),
		[
			entity?.id,
			obterDocumentosPrincipaisDaEntidade(entity)
				.map((doc) => `${doc.id ?? ''}:${doc.arquivoGuid ?? ''}:${doc.nomeArquivo ?? ''}`)
				.join('|'),
		],
	);
	const [documentosPrincipaisUpload, setDocumentosPrincipaisUpload] = useState<DocumentoPrincipalUploadItem[]>([]);

	const [modalTransporte, setModalTransporte] = useState<boolean>(false);
	const [selectedTransportes, setSelectedTransportes] = useState<CadernoLPMATransporte[]>([]);
const [transporteEmEdicao, setTransporteEmEdicao] = useState<CadernoLPMATransporte | null>(null);
const [indiceTransporteEdicao, setIndiceTransporteEdicao] = useState<number | null>(null);
	const [processoCTNBioReferenciaTransporte, setProcessoCTNBioReferenciaTransporte] = useState('');

	const [modalRegistroVisitas, setModalRegistroVisitas] = useState<boolean>(false);
	const [selectedRegistroVisitas, setSelectedRegistroVisitas] = useState<CadernoLPMARegistroVisitas[]>([]);
	const [registroVisitaEmEdicao, setRegistroVisitaEmEdicao] = useState<CadernoLPMARegistroVisitas | null>(null);
	const [indiceRegistroVisitaEdicao, setIndiceRegistroVisitaEdicao] = useState<number | null>(null);

	const [modalPlantiosMilho, setModalPlantiosMilho] = useState<boolean>(false);
	const [modalPlantiosSoja, setModalPlantiosSoja] = useState<boolean>(false);
	const [selectedPlantiosMilho, setSelectedPlantiosMilho] = useState<CadernoLPMAPlantio[]>([]);
	const [selectedPlantiosSoja, setSelectedPlantiosSoja] = useState<CadernoLPMAPlantioSoja[]>([]);
const [plantioMilhoEmEdicao, setPlantioMilhoEmEdicao] = useState<CadernoLPMAPlantio | null>(null);
const [indicePlantioMilhoEdicao, setIndicePlantioMilhoEdicao] = useState<number | null>(null);
const [plantioSojaEmEdicao, setPlantioSojaEmEdicao] = useState<CadernoLPMAPlantioSoja | null>(null);
const [indicePlantioSojaEdicao, setIndicePlantioSojaEdicao] = useState<number | null>(null);

	const [modalCultivos, setModalCultivos] = useState<boolean>(false);
	const [selectedCultivos, setSelectedCultivos] = useState<CadernoLPMACultivo[]>([]);
	const [cultivoEmEdicao, setCultivoEmEdicao] = useState<CadernoLPMACultivo | null>(null);
	const [indiceCultivoEdicao, setIndiceCultivoEdicao] = useState<number | null>(null);

	const [modalColheitas, setModalColheitas] = useState<boolean>(false);
	const [selectedColheitas, setSelectedColheitas] = useState<CadernoLPMAColheita[]>([]);
	const [colheitaEmEdicao, setColheitaEmEdicao] = useState<CadernoLPMAColheita | null>(null);
	const [indiceColheitaEdicao, setIndiceColheitaEdicao] = useState<number | null>(null);

	const [modalMonitoramentos, setModalMonitoramentos] = useState<boolean>(false);
	const [selectedMonitoramentos, setSelectedMonitoramentos] = useState<CadernoLPMAMonitoramento[]>([]);
	const [monitoramentoEmEdicao, setMonitoramentoEmEdicao] = useState<CadernoLPMAMonitoramento | null>(null);
	const [indiceMonitoramentoEdicao, setIndiceMonitoramentoEdicao] = useState<number | null>(null);

	const [modalDestruicaoDescartes, setModalDestruicaoDescartes] = useState<boolean>(false);
	const [selectedDestruicaoDescartes, setSelectedDestruicaoDescartes] = useState<CadernoLPMADestruicaoDescarte[]>([]);
	const [destruicaoDescarteEmEdicao, setDestruicaoDescarteEmEdicao] = useState<CadernoLPMADestruicaoDescarte | null>(null);
	const [indiceDestruicaoDescarteEdicao, setIndiceDestruicaoDescarteEdicao] = useState<number | null>(null);

	const [modalAmostragens, setModalAmostragens] = useState<boolean>(false);
	const [selectedAmostragens, setSelectedAmostragens] = useState<CadernoLPMAAmostragem[]>([]);
	const [amostragemEmEdicao, setAmostragemEmEdicao] = useState<CadernoLPMAAmostragem | null>(null);
	const [indiceAmostragemEdicao, setIndiceAmostragemEdicao] = useState<number | null>(null);

	const [modalDestruicaoBordaduras, setModalDestruicaoBordaduras] = useState<boolean>(false);
	const [selectedDestruicaoBordaduras, setSelectedDestruicaoBordaduras] = useState<CadernoLPMADestruicaoBordadura[]>([]);
	const [destruicaoBordaduraEmEdicao, setDestruicaoBordaduraEmEdicao] = useState<CadernoLPMADestruicaoBordadura | null>(null);
	const [indiceDestruicaoBordaduraEdicao, setIndiceDestruicaoBordaduraEdicao] = useState<number | null>(null);

	const [modalAvaliacoesAgronomicas, setModalAvaliacoesAgronomicas] = useState<boolean>(false);
	const [selectedAvaliacoesAgronomicas, setSelectedAvaliacoesAgronomicas] = useState<CadernoLPMAAvaliacaoAgronomica[]>([]);
	const [avaliacaoAgronomicaEmEdicao, setAvaliacaoAgronomicaEmEdicao] = useState<CadernoLPMAAvaliacaoAgronomica | null>(null);
	const [indiceAvaliacaoAgronomicaEdicao, setIndiceAvaliacaoAgronomicaEdicao] = useState<number | null>(null);

	const [modalPlantiosOutraCultura, setModalPlantiosOutraCultura] = useState<boolean>(false);
	const [selectedPlantiosOutraCultura, setSelectedPlantiosOutraCultura] = useState<CadernoLPMAPlantioOutraCultura[]>([]);
	const [plantioOutraCulturaEmEdicao, setPlantioOutraCulturaEmEdicao] = useState<CadernoLPMAPlantioOutraCultura | null>(null);
	const [indicePlantioOutraCulturaEdicao, setIndicePlantioOutraCulturaEdicao] = useState<number | null>(null);

	const [modalExtensaoMonitoramentos, setModalExtensaoMonitoramentos] = useState<boolean>(false);
	const [selectedExtensaoMonitoramentos, setSelectedExtensaoMonitoramentos] = useState<CadernoLPMAExtensaoMonitoramento[]>([]);
	const [extensaoMonitoramentoEmEdicao, setExtensaoMonitoramentoEmEdicao] = useState<CadernoLPMAExtensaoMonitoramento | null>(null);
	const [indiceExtensaoMonitoramentoEdicao, setIndiceExtensaoMonitoramentoEdicao] = useState<number | null>(null);

	const [modalReportesRelatorios, setModalReportesRelatorios] = useState<boolean>(false);
	const [selectedReportesRelatorios, setSelectedReportesRelatorios] = useState<CadernoLPMAReportesRelatorios[]>([]);
	const [reporteRelatorioEmEdicao, setReporteRelatorioEmEdicao] = useState<CadernoLPMAReportesRelatorios | null>(null);
	const [indiceReporteRelatorioEdicao, setIndiceReporteRelatorioEdicao] = useState<number | null>(null);

	const [modalAnotacoes, setModalAnotacoes] = useState<boolean>(false);
	const [selectedAnotacoes, setSelectedAnotacoes] = useState<CadernoLPMAAnotacoes[]>([]);
	const [anotacaoEmEdicao, setAnotacaoEmEdicao] = useState<CadernoLPMAAnotacoes | null>(null);
	const [indiceAnotacaoEdicao, setIndiceAnotacaoEdicao] = useState<number | null>(null);

	const [modalAssinaturas, setModalAssinaturas] = useState<boolean>(false);
	const [selectedAssinaturas, setSelectedAssinaturas] = useState<CadernoLPMAAssinatura[]>([]);
	const [assinaturaEmEdicao, setAssinaturaEmEdicao] = useState<CadernoLPMAAssinatura | null>(null);
	const [indiceAssinaturaEdicao, setIndiceAssinaturaEdicao] = useState<number | null>(null);
	const entityInitialized = useRef(false);
	const initialEntityIdAplicado = useRef<number | undefined>(undefined);
	const selectedTransportesRef = useRef<CadernoLPMATransporte[]>(selectedTransportes);
	selectedTransportesRef.current = selectedTransportes;

	const defaultForm = useForm({
		defaultValues: entity,
		mode: "onSubmit",
	});

	const sincronizarDocumentosPrincipaisNoFormulario = (fonte?: CadernoLPMA | null) => {
		const arquivosFormulario =
			(defaultForm.getValues('documentosPrincipaisFilesTemp') as (File | null | undefined)[] | undefined) ?? [];
		if (Array.isArray(arquivosFormulario) && arquivosFormulario.some((arquivo) => arquivo != null)) {
			return;
		}

		const documentos = mapearDocumentosPrincipaisParaDefaultValue(fonte);
		defaultForm.setValue(
			'documentosPrincipaisFilesTemp',
			documentos.map(() => null),
		);
		defaultForm.setValue(
			'documentosPrincipaisGuids',
			documentos.map((doc) => doc.guid),
		);
		defaultForm.setValue(
			'documentosPrincipaisNomes',
			documentos.map((doc) => doc.name),
		);
		setDocumentosPrincipaisUpload(
			documentos.map((doc) => ({
				blob: null,
				guid: doc.guid,
				name: doc.name,
			})),
		);
	};

	useEffect(() => {
		if (!entity?.id || documentosPrincipaisDefaultValue.length === 0) {
			return;
		}

		sincronizarDocumentosPrincipaisNoFormulario(entity);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entity?.id, documentosPrincipaisDefaultValue]);

type SelectOption = { label: string; value: any } | null;
type LocalOption = { label: string; value: string };

const getInitialSelectValue = (campo: string, fallback: SelectOption) =>
	(defaultForm.getValues(campo) as SelectOption | undefined) ?? fallback ?? null;

const [culturaSelecionada, setCulturaSelecionada] = useState<SelectOption>(
	getInitialSelectValue('cultura', (entity?.cultura as SelectOption | undefined) ?? null),
);
const [nomeCientificoSelecionado, setNomeCientificoSelecionado] = useState<SelectOption>(
	getInitialSelectValue('nomeCientifico', (entity?.nomeCientifico as SelectOption | undefined) ?? null),
);
const [tecnicoPrincipalSelecionado, setTecnicoPrincipalSelecionado] = useState<SelectOption>(
	getInitialSelectValue('tecnicoPrincipal', (entity?.tecnicoPrincipal as SelectOption | undefined) ?? null),
);
const [responsavelTecnicoEnsaioCampoSelecionado, setResponsavelTecnicoEnsaioCampoSelecionado] = useState<SelectOption>(
	getInitialSelectValue('responsavelTecnicoEnsaioCampo', (entity?.responsavelTecnicoEnsaioCampo as SelectOption | undefined) ?? null),
);
const [assistenteTecnicoEnsaioCampoSelecionado, setAssistenteTecnicoEnsaioCampoSelecionado] = useState<SelectOption>(
	getInitialSelectValue('assistenteTecnicoEnsaioCampo', (entity?.assistenteTecnicoEnsaioCampo as SelectOption | undefined) ?? null),
);

const sincronizarSelectsComFormulario = () => {
	setCulturaSelecionada(
		getInitialSelectValue('cultura', (entity?.cultura as SelectOption | undefined) ?? null),
	);
	setNomeCientificoSelecionado(
		getInitialSelectValue('nomeCientifico', (entity?.nomeCientifico as SelectOption | undefined) ?? null),
	);
	setTecnicoPrincipalSelecionado(
		getInitialSelectValue('tecnicoPrincipal', (entity?.tecnicoPrincipal as SelectOption | undefined) ?? null),
	);
	setResponsavelTecnicoEnsaioCampoSelecionado(
		getInitialSelectValue('responsavelTecnicoEnsaioCampo', (entity?.responsavelTecnicoEnsaioCampo as SelectOption | undefined) ?? null),
	);
	setAssistenteTecnicoEnsaioCampoSelecionado(
		getInitialSelectValue('assistenteTecnicoEnsaioCampo', (entity?.assistenteTecnicoEnsaioCampo as SelectOption | undefined) ?? null),
	);
	};

const formatarLocalParaLabel = (local: CadernoLPMALocalLista): string => {
	const nomeCompleto = local.nomeCompleto?.trim();
	const endereco = local.endereco?.trim();
	const cidade = local.cidade?.trim();
	const uf = local.uf?.trim();
	const cidadeUf = [cidade, uf].filter(Boolean).join('/');
	const principal = [nomeCompleto, endereco].filter(Boolean).join(' - ');
	const partes = [principal, cidadeUf].filter(Boolean);
	return partes.join(' - ');
};

const deduplicarOpcoesLocais = (lista: LocalOption[]): LocalOption[] => {
	const mapa = new Map<string, LocalOption>();
	lista.forEach((opcao) => {
		if (opcao.value) {
			mapa.set(opcao.value, opcao);
		}
	});

	return Array.from(mapa.values());
};

const atualizarOpcoesLocaisComLista = (locaisLista: CadernoLPMALocalLista[]) => {
	const novasOpcoes = locaisLista
		.map((item) => {
			const label = formatarLocalParaLabel(item);
			return label ? { label, value: label } : null;
		})
		.filter((opcao): opcao is LocalOption => Boolean(opcao));

	if (!novasOpcoes.length) {
		return;
	}

	setOpcoesLocais((prev) => deduplicarOpcoesLocais([...prev, ...novasOpcoes]));
};

const adicionarOpcaoLocalSeNecessario = (label?: string | null) => {
	if (!label) {
		return;
	}

	setOpcoesLocais((prev) => {
		if (prev.some((opcao) => opcao.value === label)) {
			return prev;
		}
		return [...prev, { label, value: label }];
	});
};

const baixarDocumentoPrincipal = async (documento: Partial<CadernoLPMADocumentoPrincipal>) => {
	if (!entity?.id || !documento.id) {
		return;
	}

	try {
		const resposta = await api.get(
			`api/v1/cadernoLPMA/cadernoLPMA/${entity.id}/documentos-principais/${documento.id}/download`,
			{ responseType: 'blob' },
		);
		const nomeArquivo = documento.nomeArquivo ?? `documento-principal-${documento.id}`;
		const blob = new Blob([resposta.data], { type: documento.contentType ?? 'application/octet-stream' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = nomeArquivo;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	} catch (error) {
		console.error('Erro ao baixar documento principal:', error);
	}
};

	useEffect(() => {
		if (!canView) {
			history.replace('/');
			return;
		}
		if (mode === 'create' && !canEditDadosPrincipais) {
			history.replace('/cadernoLPMA/consultar');
		}
	}, [mode, canEditDadosPrincipais, canView, history]);

	useEffect(() => {
	sincronizarSelectsComFormulario();
}, [
	entity?.cultura?.value,
	entity?.cultura?.label,
	entity?.nomeCientifico?.value,
	entity?.nomeCientifico?.label,
	entity?.tecnicoPrincipal?.value,
	entity?.tecnicoPrincipal?.label,
	entity?.responsavelTecnicoEnsaioCampo?.value,
	entity?.responsavelTecnicoEnsaioCampo?.label,
	entity?.assistenteTecnicoEnsaioCampo?.value,
	entity?.assistenteTecnicoEnsaioCampo?.label,
	modoFluxo,
]);

useEffect(() => {
	adicionarOpcaoLocalSeNecessario(entity?.local ?? null);
}, [entity?.local]);

useEffect(() => {
	if (!entity) {
		return;
	}

	let entidadeAtualizada = entity;
	let houveAtualizacao = false;

	if (!entidadeAtualizada.cultura && entidadeAtualizada.culturaId && culturas.length) {
		const culturaEncontrada = culturas.find((cultura) => cultura.id === entidadeAtualizada.culturaId);
		if (culturaEncontrada) {
			entidadeAtualizada = {
				...entidadeAtualizada,
				cultura: { label: culturaEncontrada.nome, value: culturaEncontrada.id },
			};
			houveAtualizacao = true;
		}
	}

	if (!entidadeAtualizada.cultura && entidadeAtualizada.culturaId && culturas.length) {
		const culturaEncontrada = culturas.find((cultura) => cultura.id === entidadeAtualizada.culturaId);
		if (culturaEncontrada) {
			entidadeAtualizada = {
				...entidadeAtualizada,
				cultura: { label: culturaEncontrada.nome, value: culturaEncontrada.id },
			};
			houveAtualizacao = true;
		}
	}

	if (!entidadeAtualizada.nomeCientifico && entidadeAtualizada.nomeCientificoId && enumNomeCientifico.length) {
		const nomeCientificoEncontrado = enumNomeCientifico.find(
			(item) => item.key === entidadeAtualizada.nomeCientificoId,
		);
		if (nomeCientificoEncontrado) {
			entidadeAtualizada = {
				...entidadeAtualizada,
				nomeCientifico: { label: nomeCientificoEncontrado.value, value: nomeCientificoEncontrado.key },
			};
			houveAtualizacao = true;
		}
	}

	if (!entidadeAtualizada.tecnicoPrincipal && entidadeAtualizada.tecnicoPrincipalId && tecnicosPrincipais.length) {
		const tecnicoEncontrado = tecnicosPrincipais.find((tecnico) => tecnico.id === entidadeAtualizada.tecnicoPrincipalId);
		if (tecnicoEncontrado) {
			entidadeAtualizada = {
				...entidadeAtualizada,
				tecnicoPrincipal: { label: tecnicoEncontrado.nome, value: tecnicoEncontrado.id },
			};
			houveAtualizacao = true;
		}
	}

	if (!entidadeAtualizada.responsavelTecnicoEnsaioCampo && entidadeAtualizada.responsavelTecnicoEnsaioCampoId && tecnicosResponsaveisCampo.length) {
		const responsavelEncontrado = tecnicosResponsaveisCampo.find((t) => t.id === entidadeAtualizada.responsavelTecnicoEnsaioCampoId);
		if (responsavelEncontrado) {
			entidadeAtualizada = {
				...entidadeAtualizada,
				responsavelTecnicoEnsaioCampo: { label: responsavelEncontrado.nome, value: responsavelEncontrado.id },
			};
			houveAtualizacao = true;
		}
	}

	if (!entidadeAtualizada.assistenteTecnicoEnsaioCampo && entidadeAtualizada.assistenteTecnicoEnsaioCampoId && tecnicosResponsaveisCampo.length) {
		const assistenteEncontrado = tecnicosResponsaveisCampo.find((t) => t.id === entidadeAtualizada.assistenteTecnicoEnsaioCampoId);
		if (assistenteEncontrado) {
			entidadeAtualizada = {
				...entidadeAtualizada,
				assistenteTecnicoEnsaioCampo: { label: assistenteEncontrado.nome, value: assistenteEncontrado.id },
			};
			houveAtualizacao = true;
		}
	}

	if (houveAtualizacao) {
		setEntity(entidadeAtualizada);
		return;
	}

	if (!culturaSelecionada && entidadeAtualizada.cultura) {
		setCulturaSelecionada(entidadeAtualizada.cultura);
		defaultForm.setValue('cultura', entidadeAtualizada.cultura);
	}

	if (!nomeCientificoSelecionado && entidadeAtualizada.nomeCientifico) {
		setNomeCientificoSelecionado(entidadeAtualizada.nomeCientifico);
		defaultForm.setValue('nomeCientifico', entidadeAtualizada.nomeCientifico);
	}

	if (!tecnicoPrincipalSelecionado && entidadeAtualizada.tecnicoPrincipal) {
		setTecnicoPrincipalSelecionado(entidadeAtualizada.tecnicoPrincipal);
		defaultForm.setValue('tecnicoPrincipal', entidadeAtualizada.tecnicoPrincipal);
	}

	if (!responsavelTecnicoEnsaioCampoSelecionado && entidadeAtualizada.responsavelTecnicoEnsaioCampo) {
		setResponsavelTecnicoEnsaioCampoSelecionado(entidadeAtualizada.responsavelTecnicoEnsaioCampo);
		defaultForm.setValue('responsavelTecnicoEnsaioCampo', entidadeAtualizada.responsavelTecnicoEnsaioCampo);
	}

	if (!assistenteTecnicoEnsaioCampoSelecionado && entidadeAtualizada.assistenteTecnicoEnsaioCampo) {
		setAssistenteTecnicoEnsaioCampoSelecionado(entidadeAtualizada.assistenteTecnicoEnsaioCampo);
		defaultForm.setValue('assistenteTecnicoEnsaioCampo', entidadeAtualizada.assistenteTecnicoEnsaioCampo);
	}

	// Marca como carregado quando todos os dados principais estiverem prontos (modo edição)
	if (
		entidadeAtualizada.cultura &&
		entidadeAtualizada.nomeCientifico &&
		entidadeAtualizada.tecnicoPrincipal &&
		!dadosPrincipaisCarregados
	) {
		setDadosPrincipaisCarregados(true);
	}
}, [
	entity?.id,
	entity?.culturaId,
	entity?.nomeCientificoId,
	entity?.tecnicoPrincipalId,
	entity?.responsavelTecnicoEnsaioCampoId,
	entity?.assistenteTecnicoEnsaioCampoId,
	culturas.length,
	enumNomeCientifico.length,
	tecnicosPrincipais.length,
	tecnicosResponsaveisCampo.length,
	culturaSelecionada,
	nomeCientificoSelecionado,
	tecnicoPrincipalSelecionado,
	responsavelTecnicoEnsaioCampoSelecionado,
	assistenteTecnicoEnsaioCampoSelecionado,
	dadosPrincipaisCarregados,
]);

// Em modo de criação, marca como carregado quando as listas estiverem disponíveis
useEffect(() => {
	if (!isEditMode && culturas.length > 0 && enumNomeCientifico.length > 0 && tecnicosPrincipais.length > 0) {
		setDadosPrincipaisCarregados(true);
	}
}, [isEditMode, culturas, enumNomeCientifico, tecnicosPrincipais]);

	const registerDefaultInput = (element: any) => {
		if (element) {
			defaultForm.register(element);
		}
	};

const normalizarInteiroOuNulo = (valor: unknown): number | null => {
	if (valor === null || valor === undefined) {
		return null;
	}

	if (typeof valor === 'number') {
		return Number.isNaN(valor) ? null : valor;
	}

	if (typeof valor === 'string') {
		const texto = valor.trim();
		if (!texto) {
			return null;
		}

		const numero = Number(texto);
		return Number.isNaN(numero) ? null : numero;
	}

	return null;
};

const normalizarColecao = <T,>(colecao?: T[] | null): T[] => (Array.isArray(colecao) ? colecao : []);
const clonarColecaoProfunda = <T,>(colecao?: T[] | null): T[] =>
	JSON.parse(JSON.stringify(Array.isArray(colecao) ? colecao : []));

const obterIdNumericoLPMA = (item: { id?: number; Id?: number } | null | undefined): number => {
	if (!item) {
		return 0;
	}
	const v = item.id ?? item.Id;
	return typeof v === 'number' && v > 0 ? v : 0;
};

const gerarClientRowKeyLpma = (): string => {
	const c = globalThis.crypto as Crypto & { randomUUID?: () => string };
	if (typeof c?.randomUUID === 'function') {
		return c.randomUUID();
	}
	return `lpma-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const resolverIndiceEdicaoGridLinha = <T extends { id?: number; Id?: number; clientRowKey?: string }>(
	rows: T[],
	linha: T,
	rowIndex?: number | null,
): number => {
	const keyLinha = linha?.clientRowKey;
	if (typeof keyLinha === 'string' && keyLinha.length > 0) {
		const porKey = rows.findIndex((item) => item.clientRowKey === keyLinha);
		if (porKey >= 0) {
			return porKey;
		}
	}
	const dataId = obterIdNumericoLPMA(linha);
	if (dataId > 0) {
		const porId = rows.findIndex((item) => obterIdNumericoLPMA(item) === dataId);
		if (porId >= 0) {
			return porId;
		}
	}
	if (typeof rowIndex === 'number' && rowIndex >= 0 && rowIndex < rows.length) {
		return rowIndex;
	}
	return rows.findIndex((item) => item === linha);
};

const resolverIndiceSubmitLpma = <T extends { id?: number; Id?: number; clientRowKey?: string }>(
	rows: T[],
	indiceAtual: number | null,
	emEdicao: T | null | undefined,
): number | null => {
	if (emEdicao == null) {
		return indiceAtual;
	}
	let idx: number | null = indiceAtual;
	if (idx !== null && idx >= 0 && idx < rows.length) {
		return idx;
	}
	const idE = obterIdNumericoLPMA(emEdicao);
	if (idE > 0) {
		const porId = rows.findIndex((r) => obterIdNumericoLPMA(r) === idE);
		if (porId >= 0) {
			return porId;
		}
	}
	const ck = emEdicao.clientRowKey;
	if (typeof ck === 'string' && ck.length > 0) {
		const porKey = rows.findIndex((r) => r.clientRowKey === ck);
		if (porKey >= 0) {
			return porKey;
		}
	}
	return idx;
};

const mesclarClientRowKeySeNecessario = <T extends { id?: number; Id?: number; clientRowKey?: string }>(
	emEdicao: T | null | undefined,
	registro: T,
): T => {
	const idR = obterIdNumericoLPMA(registro);
	if (idR > 0) {
		return registro;
	}
	const key =
		emEdicao != null
			? obterIdNumericoLPMA(emEdicao) === 0
				? emEdicao.clientRowKey ?? gerarClientRowKeyLpma()
				: undefined
			: registro.clientRowKey ?? gerarClientRowKeyLpma();
	if (key) {
		return { ...registro, clientRowKey: key };
	}
	return registro;
};

const colecaoLpmaParaPayload = <T,>(arr: T[]): T[] =>
	clonarColecaoProfunda(arr).map((item) => {
		const { clientRowKey: _crk, ...rest } = item as T & { clientRowKey?: string };
		return rest as T;
	});

const clonarTransporte = (registro: CadernoLPMATransporte): CadernoLPMATransporte =>
	JSON.parse(JSON.stringify(registro ?? {}));
const clonarRegistroVisita = (registro: CadernoLPMARegistroVisitas): CadernoLPMARegistroVisitas =>
	JSON.parse(JSON.stringify(registro ?? {}));
const normalizarTransporteParaEstado = (
	info: CadernoLPMATransporte,
	registroAnterior?: CadernoLPMATransporte | null,
	cadernoIdFallback?: number | null,
): CadernoLPMATransporte => {
	const base = registroAnterior ? clonarTransporte(registroAnterior) : ({} as CadernoLPMATransporte);
	const atual = clonarTransporte(info);

	return {
		...base,
		...atual,
		id: base.id ?? atual.id ?? 0,
		cadernoLPMAId: base.cadernoLPMAId ?? atual.cadernoLPMAId ?? cadernoIdFallback ?? 0,
	};
};
const normalizarRegistroVisitaParaEstado = (
	info: CadernoLPMARegistroVisitas,
	registroAnterior?: CadernoLPMARegistroVisitas | null,
	cadernoIdFallback?: number | null,
): CadernoLPMARegistroVisitas => {
	const base = registroAnterior ? clonarRegistroVisita(registroAnterior) : ({} as CadernoLPMARegistroVisitas);
	const atual = clonarRegistroVisita(info);
	const cadernoLPMAId = base.cadernoLPMAId ?? atual.cadernoLPMAId ?? cadernoIdFallback ?? 0;

	return {
		...base,
		...atual,
		id: base.id ?? atual.id ?? 0,
		cadernoLPMAId,
	};
};
const obterColecao = <T,>(fonte: any, ...nomes: string[]): T[] => {
	if (!fonte) {
		return [];
	}

	for (const nome of nomes) {
		const valor = fonte[nome];
		if (Array.isArray(valor)) {
			return valor as T[];
		}
	}

	return [];
};

const clonarPlantioMilho = (registro: CadernoLPMAPlantio): CadernoLPMAPlantio =>
	JSON.parse(JSON.stringify(registro ?? {}));

const normalizarPlantioMilhoParaEstado = (
	info: CadernoLPMAPlantio,
	registroAnterior?: CadernoLPMAPlantio | null,
	cadernoIdFallback?: number | null,
): CadernoLPMAPlantio => {
	const base = registroAnterior ? clonarPlantioMilho(registroAnterior) : ({} as CadernoLPMAPlantio);
	const valoresAtuais = clonarPlantioMilho(info);

	return {
		...base,
		...valoresAtuais,
		id: base.id ?? valoresAtuais.id ?? 0,
		cadernoLPMAId: base.cadernoLPMAId ?? valoresAtuais.cadernoLPMAId ?? cadernoIdFallback ?? 0,
	};
};

const clonarPlantioSoja = (registro: CadernoLPMAPlantioSoja): CadernoLPMAPlantioSoja =>
	JSON.parse(JSON.stringify(registro ?? {}));

const normalizarPlantioSojaParaEstado = (
	info: CadernoLPMAPlantioSoja,
	registroAnterior?: CadernoLPMAPlantioSoja | null,
	cadernoIdFallback?: number | null,
): CadernoLPMAPlantioSoja => {
	const base = (registroAnterior ? clonarPlantioSoja(registroAnterior) : {}) as CadernoLPMAPlantioSoja;
	const cadernoLPMAId = base.cadernoLPMAId ?? info.cadernoLPMAId ?? cadernoIdFallback ?? 0;

	return {
		...base,
		...clonarPlantioSoja(info),
		id: base.id ?? info.id ?? 0,
		cadernoLPMAId,
	};
};

	const ultimaEntidadeProcessada = useRef<CadernoLPMA | undefined>();

	const arraysAreEqual = <T extends { id?: number }>(a: T[], b: T[]) => {
		if (a.length !== b.length) {
			return false;
		}

		// Comparação profunda usando JSON para detectar mudanças no conteúdo (como assinaturas)
		// Ordena por ID para garantir comparação correta mesmo se a ordem mudar
		const aSorted = [...a].sort((x, y) => (x.id ?? 0) - (y.id ?? 0));
		const bSorted = [...b].sort((x, y) => (x.id ?? 0) - (y.id ?? 0));
		
		try {
			return JSON.stringify(aSorted) === JSON.stringify(bSorted);
		} catch {
			// Fallback para comparação simples por ID se JSON.stringify falhar
			return a.every((item, index) => {
				const nextItem = b[index];
				return item.id === nextItem?.id;
			});
		}
	};

	const sincronizarEntityColecao = <T extends { id?: number }>(
		campoPrincipal: keyof CadernoLPMA,
		valor: T[],
		alias?: keyof CadernoLPMA,
	) => {
		setEntity((prevEntity) => {
			if (!prevEntity) {
				return prevEntity;
			}

			const nomes = [String(campoPrincipal)];
			if (alias) {
				nomes.push(String(alias));
			}

			const colecaoAtual = obterColecao<T>(prevEntity, ...nomes);
			if (!valor.length && colecaoAtual.length) {
				return prevEntity;
			}
			if (arraysAreEqual(colecaoAtual, valor)) {
				return prevEntity;
			}

			return {
				...prevEntity,
				[campoPrincipal]: valor,
				...(alias ? { [alias]: valor } : {}),
			} as CadernoLPMA;
		});
	};

	// useEffect removido - causava loop infinito de logs

	useEffect(() => {
		setEntity((prevEntity) => {
			if (!prevEntity) {
				return prevEntity;
			}

			if (prevEntity.plantiosSoja === selectedPlantiosSoja) {
				return prevEntity;
			}

			return {
				...prevEntity,
				plantiosSoja: selectedPlantiosSoja,
			} as CadernoLPMA;
		});
	}, [selectedPlantiosSoja]);

	const logController = (contexto: string, value: SelectOption) => {
		return value;
	};

	const obterNomeCulturaAtual = (): string => {
		if (culturaSelecionada?.label) {
			return culturaSelecionada.label;
		}

		const culturaEntidade = entity?.cultura as SelectOption | undefined;
		if (culturaEntidade?.label) {
			return culturaEntidade.label as string;
		}

		const culturaPorId = culturas.find((item) => item.id === entity?.culturaId);
		return culturaPorId?.nome ?? '';
	};

	const culturaSelecionadaLabel = obterNomeCulturaAtual()
		.toString()
		.toLowerCase();
	const culturaSelecionadaLabelNormalizada = culturaSelecionadaLabel
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');
	const culturaEhSoja = culturaSelecionadaLabelNormalizada.includes('soja');
	const culturaEhMilho = culturaSelecionadaLabelNormalizada.includes('milho');
	const culturaEhAlgodao = culturaSelecionadaLabelNormalizada.includes('algodao');
	const culturaUsaPlantioSojaLayout = culturaEhSoja || culturaEhAlgodao || culturaEhMilho;

	// useEffect removido - causava loop infinito de logs

	useEffect(() => {
		if (!entity) {
			return;
		}

		if (!culturaSelecionada && !obterNomeCulturaAtual()) {
			return;
		}

		if (culturaUsaPlantioSojaLayout) {
			const plantiosSojaEntidade = entity.plantiosSoja ?? [];

			setSelectedPlantiosSoja((prev) =>
				arraysAreEqual(prev, plantiosSojaEntidade) ? prev : [...plantiosSojaEntidade],
			);

			setSelectedPlantiosMilho((prev) => (prev.length ? [] : prev));
		} else if (culturaEhMilho) {
			const plantiosMilhoEntidade = entity.plantios ?? [];

			setSelectedPlantiosMilho((prev) =>
				arraysAreEqual(prev, plantiosMilhoEntidade) ? prev : [...plantiosMilhoEntidade],
			);

			setSelectedPlantiosSoja((prev) => (prev.length ? [] : prev));
		} else {
			setSelectedPlantiosSoja((prev) => (prev.length ? [] : prev));
			setSelectedPlantiosMilho((prev) => (prev.length ? [] : prev));
		}
	}, [culturaEhSoja, culturaEhMilho, culturaEhAlgodao, entity?.id]);

	useEffect(() => {
		if (!entity) {
			setSelectedTransportes([]);
			setSelectedRegistroVisitas([]);
			setSelectedPlantiosMilho([]);
			setSelectedPlantiosSoja([]);
			setPlantioMilhoEmEdicao(null);
			setIndicePlantioMilhoEdicao(null);
			setPlantioSojaEmEdicao(null);
			setIndicePlantioSojaEdicao(null);
			setSelectedCultivos([]);
			setSelectedColheitas([]);
			setSelectedMonitoramentos([]);
			setSelectedDestruicaoDescartes([]);
			setSelectedAmostragens([]);
			setSelectedAvaliacoesAgronomicas([]);
			setSelectedDestruicaoBordaduras([]);
			setSelectedPlantiosOutraCultura([]);
			setSelectedExtensaoMonitoramentos([]);
			setSelectedReportesRelatorios([]);
			setSelectedAnotacoes([]);
			setSelectedAssinaturas([]);
			return;
		}

		const aplicarColecaoComFallback = <T extends { id?: number },>(
			nomes: string[],
			setState: React.Dispatch<React.SetStateAction<T[]>>,
		) => {
			const colecao = obterColecao<T>(entity, ...nomes);
			if (colecao.length) {
				setState((prev) => {
					// Compara profundamente para evitar atualizações desnecessárias
					if (arraysAreEqual(prev, colecao)) {
						return prev; // Retorna a referência anterior se os dados são iguais
					}
					return [...colecao];
				});
			}
		};

		aplicarColecaoComFallback<CadernoLPMATransporte>(['transportes'], setSelectedTransportes);
		aplicarColecaoComFallback<CadernoLPMARegistroVisitas>(['registroVisitas'], setSelectedRegistroVisitas);

		const plantiosMilhoEntidade = obterColecao<CadernoLPMAPlantio>(entity, 'plantios');
		if (plantiosMilhoEntidade.length) {
			setSelectedPlantiosMilho([...plantiosMilhoEntidade]);
		}
		const plantiosSojaEntidade = obterColecao<CadernoLPMAPlantioSoja>(entity, 'plantiosSoja');
		if (plantiosSojaEntidade.length) {
			setSelectedPlantiosSoja([...plantiosSojaEntidade]);
		}

		aplicarColecaoComFallback<CadernoLPMACultivo>(['cultivos'], setSelectedCultivos);
		aplicarColecaoComFallback<CadernoLPMAColheita>(['colheitas'], setSelectedColheitas);
		aplicarColecaoComFallback<CadernoLPMAMonitoramento>(['monitoramentos'], setSelectedMonitoramentos);
		aplicarColecaoComFallback<CadernoLPMADestruicaoDescarte>(
			['destruicaoDescartes'],
			setSelectedDestruicaoDescartes,
		);
		aplicarColecaoComFallback<CadernoLPMAAmostragem>(['amostragens'], setSelectedAmostragens);
		aplicarColecaoComFallback<CadernoLPMAAvaliacaoAgronomica>(
			['avaliacoesAgronomicas'],
			setSelectedAvaliacoesAgronomicas,
		);
		aplicarColecaoComFallback<CadernoLPMADestruicaoBordadura>(
			['destruicaoBordadura'],
			setSelectedDestruicaoBordaduras,
		);
		aplicarColecaoComFallback<CadernoLPMAPlantioOutraCultura>(
			['plantiosOutraCultura'],
			setSelectedPlantiosOutraCultura,
		);
		aplicarColecaoComFallback<CadernoLPMAExtensaoMonitoramento>(
			['extensaoMonitoramentos'],
			setSelectedExtensaoMonitoramentos,
		);
		aplicarColecaoComFallback<CadernoLPMAReportesRelatorios>(
			['reportesRelatorios'],
			setSelectedReportesRelatorios,
		);
		aplicarColecaoComFallback<CadernoLPMAAnotacoes>(['anotacoes'], setSelectedAnotacoes);
		aplicarColecaoComFallback<CadernoLPMAAssinatura>(['assinaturas'], setSelectedAssinaturas);
		
	}, [entity?.id]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMATransporte>('transportes', selectedTransportes);
	}, [selectedTransportes]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMARegistroVisitas>('registroVisitas', selectedRegistroVisitas);
	}, [selectedRegistroVisitas]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAPlantio>('plantios', selectedPlantiosMilho);
	}, [selectedPlantiosMilho]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAPlantioSoja>('plantiosSoja', selectedPlantiosSoja);
	}, [selectedPlantiosSoja]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMACultivo>('cultivos', selectedCultivos);
	}, [selectedCultivos]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAColheita>('colheitas', selectedColheitas);
	}, [selectedColheitas]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAMonitoramento>('monitoramentos', selectedMonitoramentos);
	}, [selectedMonitoramentos]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMADestruicaoDescarte>(
			'destruicaoDescartes',
			selectedDestruicaoDescartes,
		);
	}, [selectedDestruicaoDescartes]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAAmostragem>('amostragens', selectedAmostragens);
	}, [selectedAmostragens]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAAvaliacaoAgronomica>(
			'avaliacoesAgronomicas',
			selectedAvaliacoesAgronomicas,
		);
	}, [selectedAvaliacoesAgronomicas]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMADestruicaoBordadura>(
			'destruicaoBordadura',
			selectedDestruicaoBordaduras,
		);
	}, [selectedDestruicaoBordaduras]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAPlantioOutraCultura>(
			'plantiosOutraCultura',
			selectedPlantiosOutraCultura,
		);
	}, [selectedPlantiosOutraCultura]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAExtensaoMonitoramento>(
			'extensaoMonitoramentos',
			selectedExtensaoMonitoramentos,
		);
	}, [selectedExtensaoMonitoramentos]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAReportesRelatorios>(
			'reportesRelatorios',
			selectedReportesRelatorios,
		);
	}, [selectedReportesRelatorios]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAAnotacoes>('anotacoes', selectedAnotacoes);
	}, [selectedAnotacoes]);

	useEffect(() => {
		sincronizarEntityColecao<CadernoLPMAAssinatura>('assinaturas', selectedAssinaturas);
	}, [selectedAssinaturas]);

	const initialValues: CadernoLPMA = {
		id: 0,
		processoCTNBio: '',
		codigo: '',
		projeto: '',
		municipio: '',
		uf: '',
		culturaId: '',
		//cultura: { label: '', value: 0 },
		nomeCientificoId: '',
		//nomeCientifico: { label: '', value: 0 },
		local: '',
		ensaios: '',
		totalEnsaios: null,
		safra: false,
		safrinha: false,
		tecnicoPrincipalId: '',
		//tecnicoPrincipal: { label: '', value: 0 },
	}

	const { register, errors, handleSubmit, getValues, control, watch, formState: { isDirty } } = useForm({
		defaultValues: initialValues,
		mode: 'onTouched',
	})

	const { getValues: getValuesPlantio, control: controlPlantio, register: registerPlantio, errors: errorsPlantio, handleSubmit: handleSubmitPlantio, trigger: triggerPlantio, watch: watchPlantio } = useForm({
		mode: 'all',
	})

	const { getValues: getValuesCultivo, control: controlCultivo, register: registerCultivo, errors: errorsCultivo, handleSubmit: handleSubmitCultivo, trigger: triggerCultivo, watch: watchCultivo } = useForm({
		mode: 'all',
	})

	const {
		getValues: getValuesAnotacoes,
		control: controlAnotacoes,
		register: registerAnotacoes,
		errors: errorsAnotacoes,
		handleSubmit: handleSubmitAnotacoes,
		trigger: triggerAnotacoes,
		watch: watchAnotacoes,
		reset: resetAnotacoes,
	} = useForm<{
		dataAnotacao: Date | null;
		usuarioAnotacao: string;
		textoAnotacao: string;
	}>({
		mode: 'all',
		defaultValues: {
			dataAnotacao: null,
			usuarioAnotacao: '',
			textoAnotacao: '',
		},
	});

const abrirModalTransporte = (registro?: CadernoLPMATransporte, indice?: number) => {
	// Usar ref para ter sempre o array mais recente (evita closure desatualizada no clique da grid)
	const transportesAtuais = selectedTransportesRef.current;
	const indiceValido = typeof indice === 'number' && !Number.isNaN(indice) && indice >= 0;
	const itemAtual =
		typeof indice === 'number' && indice >= 0 && indice < transportesAtuais.length && transportesAtuais[indice] != null
			? transportesAtuais[indice]
			: null;
	const registroParaEdicao = itemAtual ?? registro ?? null;
	const registroClonado = registroParaEdicao ? clonarTransporte(registroParaEdicao) : null;
	if (!registroClonado) {
		const fromEntity = String(entity?.processoCTNBio ?? '').trim();
		const fromForm = String(defaultForm.getValues('processoCTNBio') ?? '').trim();
		setProcessoCTNBioReferenciaTransporte(fromEntity || fromForm);
	}
	setTransporteEmEdicao(registroClonado);
	setIndiceTransporteEdicao(indiceValido ? indice ?? null : null);
	setModalTransporte(true);
};
const fecharModalTransporte = () => {
	setModalTransporte(false);
	setTransporteEmEdicao(null);
	setIndiceTransporteEdicao(null);
};

const abrirModalRegistroVisita = (registro?: CadernoLPMARegistroVisitas, indice?: number) => {
	const registroClonado = registro ? clonarRegistroVisita(registro) : null;
	setRegistroVisitaEmEdicao(registroClonado);
	setIndiceRegistroVisitaEdicao(
		typeof indice === 'number' && !Number.isNaN(indice) && indice >= 0 ? indice : null,
	);
	setModalRegistroVisitas(true);
};

const fecharModalRegistroVisita = () => {
	setModalRegistroVisitas(false);
	setRegistroVisitaEmEdicao(null);
	setIndiceRegistroVisitaEdicao(null);
};

	const registerAnotacoesInput = (element: any) => {
		if (element) {
			registerAnotacoes(element);
		}
	};

	const fecharModalAnotacoes = () => {
		setModalAnotacoes(false);
		setAnotacaoEmEdicao(null);
		setIndiceAnotacaoEdicao(null);
	};

	const fecharModalAssinaturas = () => {
		setModalAssinaturas(false);
		setAssinaturaEmEdicao(null);
		setIndiceAssinaturaEdicao(null);
	};

	const abrirModalAnotacoes = (registro?: CadernoLPMAAnotacoes, indice?: number) => {
		const clonado = registro ? (JSON.parse(JSON.stringify(registro)) as CadernoLPMAAnotacoes) : null;
		setAnotacaoEmEdicao(clonado);
		let indiceEfetivo: number | null =
			typeof indice === 'number' && !Number.isNaN(indice) && indice >= 0 ? indice : null;
		const idClonado = obterIdNumericoLPMA(clonado as { id?: number; Id?: number });
		if (indiceEfetivo === null && idClonado > 0) {
			const porId = selectedAnotacoes.findIndex(
				(a) => obterIdNumericoLPMA(a as { id?: number; Id?: number }) === idClonado,
			);
			if (porId >= 0) {
				indiceEfetivo = porId;
			}
		}
		if (indiceEfetivo === null && idClonado === 0 && clonado?.clientRowKey) {
			const porKey = selectedAnotacoes.findIndex((a) => a.clientRowKey === clonado.clientRowKey);
			if (porKey >= 0) {
				indiceEfetivo = porKey;
			}
		}
		setIndiceAnotacaoEdicao(indiceEfetivo);
		setModalAnotacoes(true);
	};

	useEffect(() => {
		if (!modalAnotacoes) {
			return;
		}
		if (anotacaoEmEdicao) {
			const d = anotacaoEmEdicao.dataAnotacao;
			let dataVal: Date | null = null;
			if (d != null) {
				if (d instanceof Date) {
					dataVal = !Number.isNaN(d.getTime()) ? d : null;
				} else {
					const parsed = new Date(d as string);
					dataVal = !Number.isNaN(parsed.getTime()) ? parsed : null;
				}
			}
			resetAnotacoes({
				dataAnotacao: dataVal,
				usuarioAnotacao: (anotacaoEmEdicao.usuarioAnotacao as string) ?? '',
				textoAnotacao: (anotacaoEmEdicao.textoAnotacao as string) ?? '',
			});
		} else {
			resetAnotacoes({
				dataAnotacao: null,
				usuarioAnotacao: decoded.unique_name ?? '',
				textoAnotacao: '',
			});
		}
	}, [modalAnotacoes, anotacaoEmEdicao, resetAnotacoes, decoded.unique_name]);

	const getData = async () => {
		setLoading(true);

		try {
			const culturas = await api.get<Cultura[]>('api/v1/autorizacoes-cibio/Cultura/obterporfiltro?Ativo=true');

			if (culturas.status === 200)
				setCulturas(culturas.data);

			const nomeCientifico = await api.get<Enum[]>('api/v1/cadernoLPMA/Enum/nome-cientifico');

			if (nomeCientifico.status === 200) {
				setEnumNomeCientifico(nomeCientifico.data);
			}

			const tecnicosPrincipais = await api.get<TecnicoPrincipal[]>('api/v1/cadernoLPMA/cadernoLPMA/tecnicos-principais');
			if (tecnicosPrincipais.status === 200) {
				setTecnicosPrincipais(tecnicosPrincipais.data);
			}

			const tecnicosResponsaveis = await api.get<TecnicoPrincipal[]>('api/v1/cadernoLPMA/cadernoLPMA/tecnicos-responsaveis-campo');
			if (tecnicosResponsaveis.status === 200) {
				setTecnicosResponsaveisCampo(tecnicosResponsaveis.data);
			}

			const locais = await api.get<CadernoLPMALocalLista[]>('api/v1/cadernoLPMA/cadernoLPMA/locais-lista');
			if (locais.status === 200) {
				atualizarOpcoesLocaisComLista(locais.data ?? []);
			}

		}
		catch (error: any) {
			console.error('Erro ao carregar dados iniciais do Caderno LPMA:', error);
		}

		setLoading(false);

	}

const abrirModalPlantioMilho = (registro?: CadernoLPMAPlantio, indice?: number) => {
	setPlantioMilhoEmEdicao(registro ? clonarPlantioMilho(registro) : null);
	setIndicePlantioMilhoEdicao(
		typeof indice === 'number' && !Number.isNaN(indice) && indice >= 0 ? indice : null,
	);
	setModalPlantiosMilho(true);
};

const fecharModalPlantioMilho = () => {
	setModalPlantiosMilho(false);
	setPlantioMilhoEmEdicao(null);
	setIndicePlantioMilhoEdicao(null);
};

const abrirModalPlantioSoja = (registro?: CadernoLPMAPlantioSoja, indice?: number) => {
	setPlantioSojaEmEdicao(registro ? clonarPlantioSoja(registro) : null);
	setIndicePlantioSojaEdicao(
		typeof indice === 'number' && !Number.isNaN(indice) && indice >= 0 ? indice : null,
	);
	setModalPlantiosSoja(true);
};

const fecharModalPlantioSoja = () => {
	setModalPlantiosSoja(false);
	setPlantioSojaEmEdicao(null);
	setIndicePlantioSojaEdicao(null);
};

	useEffect(() => {
		getData();

		setModalTransporte(false);
		setModalRegistroVisitas(false);
		fecharModalPlantioMilho();
		fecharModalPlantioSoja();
		setModalCultivos(false);
	}, []);

	useEffect(() => {
		if (isEditMode) {
			if (initialEntity) {
				const idAtual = initialEntity.id ?? undefined;
				if (initialEntityIdAplicado.current !== idAtual) {
					initialEntityIdAplicado.current = idAtual;
					setEntity(initialEntity);
					entityInitialized.current = true;
				}
			}
		} else if (!entityInitialized.current) {
			setEntity(initialEntity ?? locationInitialEntity ?? {});
			entityInitialized.current = true;
			initialEntityIdAplicado.current = undefined;
		}
	}, [initialEntity, isEditMode]);


	const onSubmit = async () => {
		setLoading(true);
		setError(null);

		try {
		} catch (error: any) {
			setError(error?.response?.data);
		}
		setLoading(false);
	}

	const aplicarDadosRascunhoSalvo = (dados: CadernoLPMA) => {
		setEntity(dados);

		if (Array.isArray(dados.transportes)) {
			setSelectedTransportes([...dados.transportes]);
		}
		if (Array.isArray(dados.registroVisitas)) {
			setSelectedRegistroVisitas([...dados.registroVisitas]);
		}
		if (Array.isArray(dados.plantios)) {
			setSelectedPlantiosMilho([...dados.plantios]);
		}
		if (Array.isArray(dados.plantiosSoja)) {
			setSelectedPlantiosSoja([...dados.plantiosSoja]);
		}
		if (Array.isArray(dados.cultivos)) {
			setSelectedCultivos([...dados.cultivos]);
		}
		if (Array.isArray(dados.colheitas)) {
			setSelectedColheitas([...dados.colheitas]);
		}
		if (Array.isArray(dados.monitoramentos)) {
			setSelectedMonitoramentos([...dados.monitoramentos]);
		}
		if (Array.isArray(dados.destruicaoDescartes)) {
			setSelectedDestruicaoDescartes([...dados.destruicaoDescartes]);
		}
		if (Array.isArray(dados.amostragens)) {
			setSelectedAmostragens([...dados.amostragens]);
		}
		if (Array.isArray(dados.avaliacoesAgronomicas)) {
			setSelectedAvaliacoesAgronomicas([...dados.avaliacoesAgronomicas]);
		}
		if (Array.isArray(dados.destruicaoBordadura)) {
			setSelectedDestruicaoBordaduras([...dados.destruicaoBordadura]);
		}
		if (Array.isArray(dados.plantiosOutraCultura)) {
			setSelectedPlantiosOutraCultura([...dados.plantiosOutraCultura]);
		}
		if (Array.isArray(dados.extensaoMonitoramentos)) {
			setSelectedExtensaoMonitoramentos([...dados.extensaoMonitoramentos]);
		}
		if (Array.isArray(dados.reportesRelatorios)) {
			setSelectedReportesRelatorios([...dados.reportesRelatorios]);
		}
		if (Array.isArray(dados.anotacoes)) {
			setSelectedAnotacoes([...dados.anotacoes]);
		}
		if (Array.isArray(dados.assinaturas)) {
			setSelectedAssinaturas([...dados.assinaturas]);
		}

		defaultForm.setValue('documentosPrincipaisFilesTemp', []);
		sincronizarDocumentosPrincipaisNoFormulario(dados);
	};

	const mesclarColecaoPreservandoCampo = <T extends { id?: number | null }>(
		colecaoApi: T[] | null | undefined,
		colecaoEnviada: T[] | null | undefined,
		campo: keyof T,
		fallback?: T[keyof T],
	): T[] | null | undefined => {
		if (!Array.isArray(colecaoApi) || !Array.isArray(colecaoEnviada)) {
			return colecaoApi;
		}

		return colecaoApi.map((itemApi, index) => {
			const enviado =
				colecaoEnviada.find((item) => item.id != null && item.id === itemApi.id)
				?? colecaoEnviada[index];

			const valorApi = itemApi[campo];
			const valorEnviado = enviado?.[campo];

			return {
				...itemApi,
				[campo]: valorApi ?? valorEnviado ?? fallback,
			};
		});
	};

	/** Preserva campos novos ainda não retornados pela API (ex.: QA sem deploy/migration). */
	const mesclarRascunhoPreservandoCamposNovos = (
		dadosApi: CadernoLPMA,
		dadosEnviados: CadernoLPMA,
	): CadernoLPMA => {
		const monitoramentos = mesclarColecaoPreservandoCampo(
			dadosApi.monitoramentos,
			dadosEnviados.monitoramentos,
			'tipoUnidadeEstimativaQuantidade',
			'kg',
		);

		const plantios = mesclarColecaoPreservandoCampo(
			dadosApi.plantios,
			dadosEnviados.plantios,
			'identificacaoPlantio',
		);

		const plantiosSoja = mesclarColecaoPreservandoCampo(
			dadosApi.plantiosSoja,
			dadosEnviados.plantiosSoja,
			'identificacaoPlantio',
		);

		return {
			...dadosApi,
			...(Array.isArray(monitoramentos) ? { monitoramentos } : {}),
			...(Array.isArray(plantios) ? { plantios } : {}),
			...(Array.isArray(plantiosSoja) ? { plantiosSoja } : {}),
		};
	};

	const salvarRascunhoCadernoLPMA = async (
		colecoesSobrescritas: CadernoLPMAColecoes = {},
		options: SalvarRascunhoOptions = {},
	): Promise<boolean> => {
		setErrorSubmit(null);
		setLoadingDraft(true);
		const rolarAoTopo = options.rolarAoTopo ?? false;

		try {
			let valuesToSubmitDraft = await getValuesToSubmit(colecoesSobrescritas);

			if (isEditMode || valuesToSubmitDraft?.id) {
				valuesToSubmitDraft = {
					...valuesToSubmitDraft,
					id: valuesToSubmitDraft?.id ?? entity?.id ?? initialEntity?.id,
				} as CadernoLPMA;

				const { status, data } = await api.put('api/v1/cadernoLPMA/cadernoLPMA/rascunho', valuesToSubmitDraft);

				if (status === 200 || status === 201) {
					setSuccessSubmit(true);
					setToast({
						open: true,
						severity: 'success',
						message: 'Alterações do caderno salvas com sucesso.',
					});
					if (rolarAoTopo) {
						window.scrollTo({ top: 0, behavior: 'smooth' });
					}
					if (data) {
						aplicarDadosRascunhoSalvo(
							mesclarRascunhoPreservandoCamposNovos(data, valuesToSubmitDraft),
						);
					} else {
						setEntity(valuesToSubmitDraft);
					}
				}
			} else {
			const { status, data } = await api.post('api/v1/cadernoLPMA/cadernoLPMA/rascunho', valuesToSubmitDraft);

			if (status === 201) {
				setSuccessSubmit(true);
				setToast({
					open: true,
					severity: 'success',
					message: 'Caderno LPMA criado com sucesso.',
				});
				if (rolarAoTopo) {
					window.scrollTo({ top: 0, behavior: 'smooth' });
				}
				if (data) {
					aplicarDadosRascunhoSalvo(
						mesclarRascunhoPreservandoCamposNovos(data, valuesToSubmitDraft),
					);
				}
				if (options.redirecionarAoCriar) {
					setTimeout(() => {
						history.push('/cadernoLPMA/editar/' + data.id);
					}, 3000);
				}
				}
			}
			return true;
		} catch (error: any) {
			if (rolarAoTopo) {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			}
			const mensagemAmigavel = traduzirErroCadernoLPMA(error);
			setErrorSubmit(mensagemAmigavel);
			setToast({ open: true, severity: 'error', message: mensagemAmigavel });
			return false;
		} finally {
			setLoadingDraft(false);
		}
	}

	const onSubmitDraft = async () => {
		await salvarRascunhoCadernoLPMA({}, { redirecionarAoCriar: true, rolarAoTopo: true });
	}

const montarPayloadCadernoLPMA = async (colecoesSobrescritas: CadernoLPMAColecoes = {}): Promise<CadernoLPMA> => {
	const base = { ...(entity ?? {}) } as CadernoLPMA;

	const normalizarTransporteParaEnvio = (
		registro: CadernoLPMATransporte,
		cadernoIdDestino?: number | null,
	): CadernoLPMATransporte => {
		const clonado = clonarTransporte(registro);
		const cadernoId =
			cadernoIdDestino ?? clonado.cadernoLPMAId ?? base.id ?? entity?.id ?? initialEntity?.id ?? null;

		return {
			...clonado,
			cadernoLPMAId: cadernoId,
		};
	};

	const obterValorFormulario = <T,>(campo: string, fallback: T): T => {
		const valor = defaultForm.getValues(campo) as T;
		return valor !== undefined ? valor : fallback;
	};

	const culturaFormulario = obterValorFormulario<SelectOption | null>(
		'cultura',
		culturaSelecionada ?? (base.cultura as SelectOption | null) ?? null,
	);
	const nomeCientificoFormulario = obterValorFormulario<SelectOption | null>(
		'nomeCientifico',
		nomeCientificoSelecionado ?? (base.nomeCientifico as SelectOption | null) ?? null,
	);
	const tecnicoPrincipalFormulario = obterValorFormulario<SelectOption | null>(
		'tecnicoPrincipal',
		tecnicoPrincipalSelecionado ?? (base.tecnicoPrincipal as SelectOption | null) ?? null,
	);
	const responsavelTecnicoEnsaioCampoFormulario = obterValorFormulario<SelectOption | null>(
		'responsavelTecnicoEnsaioCampo',
		responsavelTecnicoEnsaioCampoSelecionado ?? (base.responsavelTecnicoEnsaioCampo as SelectOption | null) ?? null,
	);
	const assistenteTecnicoEnsaioCampoFormulario = obterValorFormulario<SelectOption | null>(
		'assistenteTecnicoEnsaioCampo',
		assistenteTecnicoEnsaioCampoSelecionado ?? (base.assistenteTecnicoEnsaioCampo as SelectOption | null) ?? null,
	);

	const documentosExistentes = obterDocumentosPrincipaisDaEntidade(base);
	const documentosExistentesPorGuid = new Map(
		documentosExistentes
			.filter((doc) => !!doc?.arquivoGuid)
			.map((doc) => [doc.arquivoGuid as string, doc]),
	);
	const arquivosFormulario = (defaultForm.getValues('documentosPrincipaisFilesTemp') as (File | null | undefined)[] | undefined) ?? [];
	const nomesFormulario = (defaultForm.getValues('documentosPrincipaisNomes') as (string | null | undefined)[] | undefined) ?? [];
	const guidsFormulario = (defaultForm.getValues('documentosPrincipaisGuids') as (string | null | undefined)[] | undefined) ?? [];
	const usarEstadoUpload = documentosPrincipaisUpload.length > 0;
	const arquivos = usarEstadoUpload
		? documentosPrincipaisUpload.map((doc) => doc.blob ?? null)
		: arquivosFormulario;
	const nomes = usarEstadoUpload
		? documentosPrincipaisUpload.map((doc) => doc.name ?? null)
		: nomesFormulario;
	const guids = usarEstadoUpload
		? documentosPrincipaisUpload.map((doc) => doc.guid ?? '')
		: guidsFormulario;
	const totalDocumentos = Math.max(arquivos.length, nomes.length, guids.length);
	const documentosPrincipais: CadernoLPMADocumentoPrincipal[] = [];

	for (let i = 0; i < totalDocumentos; i++) {
		const arquivo = arquivos[i] ?? null;
		const nomeArquivo = (nomes[i] ?? arquivo?.name ?? '').trim();
		const guid = (guids[i] ?? '').trim();
		const existente = guid ? documentosExistentesPorGuid.get(guid) : undefined;

		if (!nomeArquivo && !guid && !arquivo) {
			continue;
		}

		const documento: CadernoLPMADocumentoPrincipal = {
			id: existente?.id,
			cadernoLPMAId: base.id ?? entity?.id ?? initialEntity?.id ?? undefined,
			nomeArquivo: nomeArquivo || existente?.nomeArquivo || arquivo?.name || '',
			arquivoGuid: guid || existente?.arquivoGuid,
			contentType: arquivo?.type || existente?.contentType || 'application/octet-stream',
		};

		if (!arquivo && existente?.caminhoBlob) {
			documento.caminhoBlob = existente.caminhoBlob;
		}

		if (arquivo) {
			documento.conteudoBase64 = await arquivoParaBase64(arquivo);
		}

		documentosPrincipais.push(documento);
	}

	const origemDadosPrincipais = (initialEntity ?? entity ?? base) as CadernoLPMA;

	const payload: CadernoLPMA = {
		...base,
		processoCTNBio: obterValorFormulario('processoCTNBio', base?.processoCTNBio ?? null),
		processoInternoBasf: obterValorFormulario('processoInternoBasf', base?.processoInternoBasf ?? null) ?? undefined,
		tituloLPMA: obterValorFormulario('tituloLPMA', base?.tituloLPMA ?? null) ?? undefined,
		projeto: obterValorFormulario('projeto', base?.projeto ?? null),
		municipio: obterValorFormulario('municipio', base?.municipio ?? null),
		uf: obterValorFormulario('uf', base?.uf ?? null),
		local: obterValorFormulario('local', base?.local ?? null),
		ensaios: obterValorFormulario('ensaios', base?.ensaios ?? null),
		totalEnsaios: normalizarInteiroOuNulo(obterValorFormulario('totalEnsaios', base?.totalEnsaios ?? null)),
		totalEnsaiosAprovados: normalizarInteiroOuNulo(obterValorFormulario('totalEnsaiosAprovados', base?.totalEnsaiosAprovados ?? null)),
		cultura: culturaFormulario ?? undefined,
		culturaId: culturaFormulario?.value ?? base?.culturaId ?? null,
		nomeCientifico: nomeCientificoFormulario ?? undefined,
		nomeCientificoId: nomeCientificoFormulario?.value ?? base?.nomeCientificoId ?? null,
		tecnicoPrincipal: tecnicoPrincipalFormulario ?? undefined,
		tecnicoPrincipalId: tecnicoPrincipalFormulario?.value ?? base?.tecnicoPrincipalId ?? null,
		responsavelTecnicoEnsaioCampo: responsavelTecnicoEnsaioCampoFormulario ?? undefined,
		responsavelTecnicoEnsaioCampoId: responsavelTecnicoEnsaioCampoFormulario?.value ?? base?.responsavelTecnicoEnsaioCampoId ?? null,
		assistenteTecnicoEnsaioCampo: assistenteTecnicoEnsaioCampoFormulario ?? undefined,
		assistenteTecnicoEnsaioCampoId:
			assistenteTecnicoEnsaioCampoFormulario === null
				? null
				: (assistenteTecnicoEnsaioCampoFormulario?.value ?? base?.assistenteTecnicoEnsaioCampoId ?? null),
		transportes: colecaoLpmaParaPayload(colecoesSobrescritas.transportes ?? selectedTransportes).map((registro) =>
			normalizarTransporteParaEnvio(registro, base.id ?? entity?.id ?? initialEntity?.id ?? null),
		),
		registroVisitas: colecaoLpmaParaPayload(colecoesSobrescritas.registroVisitas ?? selectedRegistroVisitas),
		plantios: colecaoLpmaParaPayload(colecoesSobrescritas.plantios ?? selectedPlantiosMilho),
		plantiosSoja: colecaoLpmaParaPayload(colecoesSobrescritas.plantiosSoja ?? selectedPlantiosSoja),
		cultivos: colecaoLpmaParaPayload(colecoesSobrescritas.cultivos ?? selectedCultivos),
		colheitas: colecaoLpmaParaPayload(colecoesSobrescritas.colheitas ?? selectedColheitas),
		monitoramentos: colecaoLpmaParaPayload(colecoesSobrescritas.monitoramentos ?? selectedMonitoramentos),
		destruicaoDescartes: colecaoLpmaParaPayload(colecoesSobrescritas.destruicaoDescartes ?? selectedDestruicaoDescartes),
		amostragens: colecaoLpmaParaPayload(colecoesSobrescritas.amostragens ?? selectedAmostragens),
		avaliacoesAgronomicas: colecaoLpmaParaPayload(colecoesSobrescritas.avaliacoesAgronomicas ?? selectedAvaliacoesAgronomicas),
		destruicaoBordadura: colecaoLpmaParaPayload(colecoesSobrescritas.destruicaoBordadura ?? selectedDestruicaoBordaduras),
		plantiosOutraCultura: colecaoLpmaParaPayload(colecoesSobrescritas.plantiosOutraCultura ?? selectedPlantiosOutraCultura),
		extensaoMonitoramentos: colecaoLpmaParaPayload(colecoesSobrescritas.extensaoMonitoramentos ?? selectedExtensaoMonitoramentos),
		reportesRelatorios: colecaoLpmaParaPayload(colecoesSobrescritas.reportesRelatorios ?? selectedReportesRelatorios),
		anotacoes: colecaoLpmaParaPayload(colecoesSobrescritas.anotacoes ?? selectedAnotacoes),
		assinaturas: colecaoLpmaParaPayload(colecoesSobrescritas.assinaturas ?? selectedAssinaturas),
		documentosPrincipais,
	};

	// Requisitante não edita dados principais: reenvia exatamente o que veio do GET,
	// evitando falso positivo no backend (ex.: assistente técnico virando null).
	if (!canEditDadosPrincipais) {
		payload.processoCTNBio = origemDadosPrincipais.processoCTNBio ?? null;
		payload.processoInternoBasf = origemDadosPrincipais.processoInternoBasf ?? undefined;
		payload.tituloLPMA = origemDadosPrincipais.tituloLPMA ?? undefined;
		payload.projeto = origemDadosPrincipais.projeto ?? null;
		payload.municipio = origemDadosPrincipais.municipio ?? null;
		payload.uf = origemDadosPrincipais.uf ?? null;
		payload.local = origemDadosPrincipais.local ?? null;
		payload.ensaios = origemDadosPrincipais.ensaios ?? null;
		payload.safra = origemDadosPrincipais.safra;
		payload.safrinha = origemDadosPrincipais.safrinha;
		payload.totalEnsaios = origemDadosPrincipais.totalEnsaios ?? null;
		payload.totalEnsaiosAprovados = origemDadosPrincipais.totalEnsaiosAprovados ?? null;
		payload.culturaId = origemDadosPrincipais.culturaId ?? null;
		payload.nomeCientificoId = origemDadosPrincipais.nomeCientificoId ?? null;
		payload.tecnicoPrincipalId = origemDadosPrincipais.tecnicoPrincipalId ?? null;
		payload.responsavelTecnicoEnsaioCampoId = origemDadosPrincipais.responsavelTecnicoEnsaioCampoId ?? null;
		payload.assistenteTecnicoEnsaioCampoId = origemDadosPrincipais.assistenteTecnicoEnsaioCampoId ?? null;
		payload.cultura = origemDadosPrincipais.cultura;
		payload.nomeCientifico = origemDadosPrincipais.nomeCientifico;
		payload.tecnicoPrincipal = origemDadosPrincipais.tecnicoPrincipal;
		payload.responsavelTecnicoEnsaioCampo = origemDadosPrincipais.responsavelTecnicoEnsaioCampo;
		payload.assistenteTecnicoEnsaioCampo = origemDadosPrincipais.assistenteTecnicoEnsaioCampo;
		payload.documentosPrincipais = obterDocumentosPrincipaisDaEntidade(origemDadosPrincipais);
	}

	if (!payload.id && (entity?.id || initialEntity?.id)) {
		payload.id = entity?.id ?? initialEntity?.id ?? payload.id;
	}

	if (!payload.cultura) {
		delete payload.cultura;
	}

	if (!payload.nomeCientifico) {
		delete payload.nomeCientifico;
	}

	if (!payload.tecnicoPrincipal) {
		delete payload.tecnicoPrincipal;
	}

	if (!payload.responsavelTecnicoEnsaioCampo) {
		delete payload.responsavelTecnicoEnsaioCampo;
	}

	if (!payload.assistenteTecnicoEnsaioCampo) {
		delete payload.assistenteTecnicoEnsaioCampo;
	}

	return payload;
};

const getValuesToSubmit = async (colecoesSobrescritas: CadernoLPMAColecoes = {}) =>
	montarPayloadCadernoLPMA(colecoesSobrescritas);

	const alterarFluxo = (fluxoAtual: any) =>
	{
		// Se estiver em modo de edição e tentando sair de DADOS_PRINCIPAIS sem os dados carregados, impede
		if (
			isEditMode &&
			canEditDadosPrincipais &&
			canEditOutrasAbas &&
			fluxoAtual !== MODO_VIEW.DADOS_PRINCIPAIS &&
			!dadosPrincipaisCarregados &&
			(!culturaSelecionada || !nomeCientificoSelecionado || !tecnicoPrincipalSelecionado)
		) {
			console.warn('@@ alterarFluxo -> Aguardando carregamento dos dados principais');
			return;
		}

		let novoFluxo = fluxoAtual;

		let dados = { ...(entity ?? {}) } as CadernoLPMA;

	if (modoFluxo === MODO_VIEW.DADOS_PRINCIPAIS && canEditDadosPrincipais) {
		const culturaFormValue = defaultForm.getValues('cultura') as SelectOption | null | undefined;
		const nomeCientificoFormValue = defaultForm.getValues('nomeCientifico') as SelectOption | null | undefined;
		const tecnicoPrincipalFormValue = defaultForm.getValues('tecnicoPrincipal') as SelectOption | null | undefined;
		const responsavelTecnicoEnsaioCampoFormValue = defaultForm.getValues('responsavelTecnicoEnsaioCampo') as SelectOption | null | undefined;
		const assistenteTecnicoEnsaioCampoFormValue = defaultForm.getValues('assistenteTecnicoEnsaioCampo') as SelectOption | null | undefined;

		const culturaValue =
			culturaFormValue ?? culturaSelecionada ?? ((dados!.cultura as SelectOption | null | undefined) ?? undefined);
		const nomeCientificoValue =
			nomeCientificoFormValue ??
			nomeCientificoSelecionado ??
			((dados!.nomeCientifico as SelectOption | null | undefined) ?? undefined);
		const tecnicoPrincipalValue =
			tecnicoPrincipalFormValue ??
			tecnicoPrincipalSelecionado ??
			((dados!.tecnicoPrincipal as SelectOption | null | undefined) ?? undefined);
		const responsavelTecnicoEnsaioCampoValue =
			responsavelTecnicoEnsaioCampoFormValue ??
			responsavelTecnicoEnsaioCampoSelecionado ??
			((dados!.responsavelTecnicoEnsaioCampo as SelectOption | null | undefined) ?? undefined);
		const assistenteTecnicoEnsaioCampoValue =
			assistenteTecnicoEnsaioCampoFormValue !== undefined
				? assistenteTecnicoEnsaioCampoFormValue
				: (assistenteTecnicoEnsaioCampoSelecionado ??
					((dados!.assistenteTecnicoEnsaioCampo as SelectOption | null | undefined) ?? undefined));

		const processoCTNBioValue = defaultForm.getValues('processoCTNBio') as string | undefined;
		const processoInternoBasfValue = defaultForm.getValues('processoInternoBasf') as string | undefined;
		const tituloLPMAValue = defaultForm.getValues('tituloLPMA') as string | undefined;
		const projetoValue = defaultForm.getValues('projeto') as string | undefined;
		const municipioValue = defaultForm.getValues('municipio') as string | undefined;
		const ufValue = defaultForm.getValues('uf') as string | undefined;
		const localValue = defaultForm.getValues('local') as string | undefined;
		const ensaiosValue = defaultForm.getValues('ensaios') as string | undefined;
		const totalEnsaiosValue = defaultForm.getValues('totalEnsaios') as string | number | undefined;
		const totalEnsaiosAprovadosValue = defaultForm.getValues('totalEnsaiosAprovados') as string | number | undefined;

		if (processoCTNBioValue !== undefined) {
			dados!.processoCTNBio = processoCTNBioValue;
		}

		if (processoInternoBasfValue !== undefined) {
			dados!.processoInternoBasf = processoInternoBasfValue;
		}

		if (tituloLPMAValue !== undefined) {
			dados!.tituloLPMA = tituloLPMAValue;
		}

		if (projetoValue !== undefined) {
			dados!.projeto = projetoValue;
		}

		if (municipioValue !== undefined) {
			dados!.municipio = municipioValue;
		}

		if (ufValue !== undefined) {
			dados!.uf = ufValue;
		}

		if (culturaValue) {
			dados!.cultura = culturaValue;
			dados!.culturaId = culturaValue.value;
		}

		if (localValue !== undefined) {
			dados!.local = localValue;
		}

		if (ensaiosValue !== undefined) {
			dados!.ensaios = ensaiosValue;
		}

		if (nomeCientificoValue) {
			dados!.nomeCientifico = nomeCientificoValue;
			dados!.nomeCientificoId = nomeCientificoValue.value;
		}

		if (tecnicoPrincipalValue) {
			dados!.tecnicoPrincipal = tecnicoPrincipalValue;
			dados!.tecnicoPrincipalId = tecnicoPrincipalValue.value;
		}

		if (responsavelTecnicoEnsaioCampoValue) {
			dados!.responsavelTecnicoEnsaioCampo = responsavelTecnicoEnsaioCampoValue;
			dados!.responsavelTecnicoEnsaioCampoId = responsavelTecnicoEnsaioCampoValue.value;
		}

		if (assistenteTecnicoEnsaioCampoValue) {
			dados!.assistenteTecnicoEnsaioCampo = assistenteTecnicoEnsaioCampoValue;
			dados!.assistenteTecnicoEnsaioCampoId = assistenteTecnicoEnsaioCampoValue.value;
		} else if (assistenteTecnicoEnsaioCampoFormValue === null || assistenteTecnicoEnsaioCampoSelecionado === null) {
			dados!.assistenteTecnicoEnsaioCampo = undefined;
			dados!.assistenteTecnicoEnsaioCampoId = null;
		}

		if (totalEnsaiosValue !== undefined) {
			dados!.totalEnsaios = normalizarInteiroOuNulo(totalEnsaiosValue);
		}

		if (totalEnsaiosAprovadosValue !== undefined) {
			dados!.totalEnsaiosAprovados = normalizarInteiroOuNulo(totalEnsaiosAprovadosValue);
		}
	}
		else if (modoFluxo === MODO_VIEW.TRANSPORTE)
		{
			dados!.transportes = colecaoLpmaParaPayload(selectedTransportes);
		}
		else if (modoFluxo === MODO_VIEW.REGISTROVISITAS)
		{
			dados!.registroVisitas = colecaoLpmaParaPayload(selectedRegistroVisitas);
		}
		else if (modoFluxo === MODO_VIEW.PLANTIO) {
			dados!.plantios = colecaoLpmaParaPayload(selectedPlantiosMilho);
			dados!.plantiosSoja = colecaoLpmaParaPayload(selectedPlantiosSoja);
		}
		else if (modoFluxo === MODO_VIEW.CULTIVO) {
			dados!.cultivos = colecaoLpmaParaPayload(selectedCultivos);
		}
		else if (modoFluxo === MODO_VIEW.COLHEITA) {
			dados!.colheitas = colecaoLpmaParaPayload(selectedColheitas);
		}
		else if (modoFluxo === MODO_VIEW.MONITORAMENTO) {
			dados!.monitoramentos = colecaoLpmaParaPayload(selectedMonitoramentos);
		}
		else if (modoFluxo === MODO_VIEW.DESTRUICAO) {
			dados!.destruicaoDescartes = colecaoLpmaParaPayload(selectedDestruicaoDescartes);
		}
		else if (modoFluxo === MODO_VIEW.AMOSTRAGEM) {
			dados!.amostragens = colecaoLpmaParaPayload(selectedAmostragens);
		}
		else if (modoFluxo === MODO_VIEW.AVALIACAOAGRONOMICA) {
			dados!.avaliacoesAgronomicas = colecaoLpmaParaPayload(selectedAvaliacoesAgronomicas);
		}
		else if (modoFluxo === MODO_VIEW.DESTRUICAOBORDADURA) {
			dados!.destruicaoBordadura = colecaoLpmaParaPayload(selectedDestruicaoBordaduras);
		}
		else if (modoFluxo === MODO_VIEW.PLANTIOOUTRACULTURA) {
			dados!.plantiosOutraCultura = colecaoLpmaParaPayload(selectedPlantiosOutraCultura);
		}
		else if (modoFluxo === MODO_VIEW.EXTENSAOMONITORAMENTO) {
			dados!.extensaoMonitoramentos = colecaoLpmaParaPayload(selectedExtensaoMonitoramentos);
		}
		else if (modoFluxo === MODO_VIEW.REPORTES) {
			dados!.reportesRelatorios = colecaoLpmaParaPayload(selectedReportesRelatorios);
		}
		else if (modoFluxo === MODO_VIEW.ANOTACOES) {
			dados!.anotacoes = colecaoLpmaParaPayload(selectedAnotacoes);
		}
		else if (modoFluxo === MODO_VIEW.ASSINATURA) {
			dados!.assinaturas = colecaoLpmaParaPayload(selectedAssinaturas);
		}


		setEntity(dados);
		setModoFluxo(novoFluxo);
		setarDados(novoFluxo, dados);
	}

	const setarDados = (fluxoAtual: any, fonteEntity: CadernoLPMA | undefined = entity) =>
	{
		if (fluxoAtual === MODO_VIEW.TRANSPORTE) {
		}
		else if (fluxoAtual === MODO_VIEW.REGISTROVISITAS) {
		}
		else if (fluxoAtual === MODO_VIEW.PLANTIO) {
			setSelectedPlantiosMilho(fonteEntity?.plantios ? [...fonteEntity.plantios] : []);
			setSelectedPlantiosSoja(fonteEntity?.plantiosSoja ? [...fonteEntity.plantiosSoja] : []);
		}
		else if (fluxoAtual === MODO_VIEW.CULTIVO) {
		}
		else if (fluxoAtual === MODO_VIEW.COLHEITA) {
		}
		else if (fluxoAtual === MODO_VIEW.MONITORAMENTO) {
		}
		else if (fluxoAtual === MODO_VIEW.DESTRUICAO) {
		}
		else if (fluxoAtual === MODO_VIEW.AMOSTRAGEM) {
		}
		else if (fluxoAtual === MODO_VIEW.AVALIACAOAGRONOMICA) {
		}
		else if (fluxoAtual === MODO_VIEW.DESTRUICAOBORDADURA) {
		}
		else if (fluxoAtual === MODO_VIEW.PLANTIOOUTRACULTURA) {
		}
		else if (fluxoAtual === MODO_VIEW.EXTENSAOMONITORAMENTO) {
		}
		else if (fluxoAtual === MODO_VIEW.REPORTES) {
		}
		else if (fluxoAtual === MODO_VIEW.ANOTACOES) {
		}
		else if (fluxoAtual === MODO_VIEW.ASSINATURA) {
		}

	}

	useEffect(() => {
		if (modoFluxo !== MODO_VIEW.DADOS_PRINCIPAIS || !entity) {
			return;
		}

		if (ultimaEntidadeProcessada.current === entity) {
			return;
		}

		ultimaEntidadeProcessada.current = entity;

		const valoresAtuais = defaultForm.getValues() as Record<string, unknown>;
		const valoresPrincipais: Partial<CadernoLPMA> = {
			...valoresAtuais,
			processoCTNBio: entity.processoCTNBio ?? '',
			processoInternoBasf: entity.processoInternoBasf ?? '',
			tituloLPMA: entity.tituloLPMA ?? '',
			projeto: entity.projeto ?? '',
			municipio: entity.municipio ?? '',
			uf: entity.uf ?? '',
			local: entity.local ?? '',
			ensaios: entity.ensaios ?? '',
			totalEnsaios: entity.totalEnsaios ?? null,
			totalEnsaiosAprovados: entity.totalEnsaiosAprovados ?? null,
		};

		if (entity.cultura) {
			valoresPrincipais.cultura = entity.cultura;
		} else {
			delete valoresPrincipais.cultura;
		}

		if (entity.nomeCientifico) {
			valoresPrincipais.nomeCientifico = entity.nomeCientifico;
		} else {
			delete valoresPrincipais.nomeCientifico;
		}

		if (entity.tecnicoPrincipal) {
			valoresPrincipais.tecnicoPrincipal = entity.tecnicoPrincipal;
		} else {
			delete valoresPrincipais.tecnicoPrincipal;
		}

		if (entity.responsavelTecnicoEnsaioCampo) {
			valoresPrincipais.responsavelTecnicoEnsaioCampo = entity.responsavelTecnicoEnsaioCampo;
		} else {
			delete valoresPrincipais.responsavelTecnicoEnsaioCampo;
		}

		if (entity.assistenteTecnicoEnsaioCampo) {
			valoresPrincipais.assistenteTecnicoEnsaioCampo = entity.assistenteTecnicoEnsaioCampo;
		} else {
			delete valoresPrincipais.assistenteTecnicoEnsaioCampo;
		}

		defaultForm.reset(valoresPrincipais as CadernoLPMA);
		sincronizarSelectsComFormulario();
	}, [modoFluxo, entity?.id]);

	const atualizarListaSubmitLpma = <T extends { id?: number; Id?: number; clientRowKey?: string }>(
		rows: T[],
		registro: T,
		indiceAtual: number | null,
		emEdicao: T | null | undefined,
	): T[] => {
		const indice = resolverIndiceSubmitLpma(rows, indiceAtual, emEdicao);
		if (indice !== null && indice >= 0 && indice < rows.length) {
			const atualizado = [...rows];
			atualizado[indice] = registro;
			return atualizado;
		}
		return [...rows, registro];
	};

	const prepararRegistroModalLpma = <T extends { id?: number; Id?: number; cadernoLPMAId?: number; clientRowKey?: string }>(
		info: T,
		emEdicao: T | null | undefined,
		cadernoIdFallback?: number | null,
	): T => {
		const idEdicao = obterIdNumericoLPMA(emEdicao);
		const cadernoLPMAId =
			emEdicao?.cadernoLPMAId ??
			(emEdicao as { CadernoLPMAId?: number } | null | undefined)?.CadernoLPMAId ??
			info.cadernoLPMAId ??
			cadernoIdFallback ??
			0;

		return mesclarClientRowKeySeNecessario(emEdicao, {
			...info,
			id: idEdicao || info.id || 0,
			cadernoLPMAId,
		});
	};

	// regi�o Submits
	const onSubmitTransporte = async (info: CadernoLPMATransporte): Promise<void> => {
	const registroNormalizado = mesclarClientRowKeySeNecessario(
		transporteEmEdicao,
		normalizarTransporteParaEstado(
			info,
			transporteEmEdicao ?? undefined,
			entity?.id ?? initialEntity?.id ?? null,
		),
	);

	const transportesAtualizados = atualizarListaSubmitLpma(
		selectedTransportesRef.current,
		registroNormalizado,
		indiceTransporteEdicao,
		transporteEmEdicao,
	);
	setSelectedTransportes(transportesAtualizados);

	if (await salvarRascunhoCadernoLPMA({ transportes: transportesAtualizados })) {
		fecharModalTransporte();
	}
};

	const onSubmitRegistroVisitas = async (info: CadernoLPMARegistroVisitas): Promise<void> => {
		const registroNormalizado = mesclarClientRowKeySeNecessario(
			registroVisitaEmEdicao,
			normalizarRegistroVisitaParaEstado(
				info,
				registroVisitaEmEdicao ?? undefined,
				entity?.id ?? initialEntity?.id ?? null,
			),
		);

		const registroVisitasAtualizados = atualizarListaSubmitLpma(
			selectedRegistroVisitas,
			registroNormalizado,
			indiceRegistroVisitaEdicao,
			registroVisitaEmEdicao,
		);
		setSelectedRegistroVisitas(registroVisitasAtualizados);

		if (await salvarRascunhoCadernoLPMA({ registroVisitas: registroVisitasAtualizados })) {
			fecharModalRegistroVisita();
		}
	};

	const abrirModalAssinaturas = (registro?: CadernoLPMAAssinatura, indice?: number) => {
		const clonado = registro ? (JSON.parse(JSON.stringify(registro)) as CadernoLPMAAssinatura) : null;
		setAssinaturaEmEdicao(clonado);
		let indiceEfetivo: number | null =
			typeof indice === 'number' && !Number.isNaN(indice) && indice >= 0 ? indice : null;
		const idClonado = obterIdNumericoLPMA(clonado as { id?: number; Id?: number });
		if (indiceEfetivo === null && idClonado > 0) {
			const porId = selectedAssinaturas.findIndex(
				(a) => obterIdNumericoLPMA(a as { id?: number; Id?: number }) === idClonado,
			);
			if (porId >= 0) {
				indiceEfetivo = porId;
			}
		}
		if (indiceEfetivo === null && idClonado === 0 && clonado?.clientRowKey) {
			const porKey = selectedAssinaturas.findIndex((a) => a.clientRowKey === clonado.clientRowKey);
			if (porKey >= 0) {
				indiceEfetivo = porKey;
			}
		}
		setIndiceAssinaturaEdicao(indiceEfetivo);
		setModalAssinaturas(true);
	};

	const onSubmitPlantioMilho = async (info: CadernoLPMAPlantio): Promise<void> => {
		const registroNormalizado = mesclarClientRowKeySeNecessario(
			plantioMilhoEmEdicao,
			normalizarPlantioMilhoParaEstado(
				info,
				plantioMilhoEmEdicao ?? undefined,
				entity?.id ?? initialEntity?.id ?? null,
			),
		);

		const plantiosAtualizados = atualizarListaSubmitLpma(
			selectedPlantiosMilho,
			registroNormalizado,
			indicePlantioMilhoEdicao,
			plantioMilhoEmEdicao,
		);
		setSelectedPlantiosMilho(plantiosAtualizados);

		if (await salvarRascunhoCadernoLPMA({ plantios: plantiosAtualizados })) {
			fecharModalPlantioMilho();
		}
	};

	const onSubmitPlantioSoja = async (info: CadernoLPMAPlantioSoja): Promise<void> => {

		const registroNormalizado = mesclarClientRowKeySeNecessario(
			plantioSojaEmEdicao,
			normalizarPlantioSojaParaEstado(
				{
					...info,
					id: info.id ?? plantioSojaEmEdicao?.id ?? 0,
					cadernoLPMAId: info.cadernoLPMAId ?? plantioSojaEmEdicao?.cadernoLPMAId ?? entity?.id ?? 0,
				},
				plantioSojaEmEdicao,
				entity?.id ?? initialEntity?.id ?? null,
			),
		);

		const plantiosSojaAtualizados = atualizarListaSubmitLpma(
			selectedPlantiosSoja,
			registroNormalizado,
			indicePlantioSojaEdicao,
			plantioSojaEmEdicao,
		);
		setSelectedPlantiosSoja(plantiosSojaAtualizados);

		if (await salvarRascunhoCadernoLPMA({ plantiosSoja: plantiosSojaAtualizados })) {
			fecharModalPlantioSoja();
		}
	};

	const onSubmitCultivo = async (info: CadernoLPMACultivo): Promise<void> => {
		const idCultivoEdicao = obterIdNumericoLPMA(cultivoEmEdicao as { id?: number; Id?: number });
		const clientRowKeyCultivo =
			idCultivoEdicao === 0
				? cultivoEmEdicao?.clientRowKey ?? gerarClientRowKeyLpma()
				: cultivoEmEdicao?.clientRowKey;
		const cultivoNormalizado: CadernoLPMACultivo = {
			id: idCultivoEdicao,
			...(clientRowKeyCultivo ? { clientRowKey: clientRowKeyCultivo } : {}),
			cadernoLPMAId: cultivoEmEdicao?.cadernoLPMAId ?? entity?.id ?? initialEntity?.id ?? 0,
			dataInicioCultivo: info.dataInicioCultivo !== undefined ? info.dataInicioCultivo : null,
			dataTerminoCultivo: info.dataTerminoCultivo !== undefined ? info.dataTerminoCultivo : null,
			talhaoCultivo: info.talhaoCultivo,
			experimentoCultivo: info.experimentoCultivo,
			responsavelCultivo: info.responsavelCultivo,
			assinaturaEnvolvidosBase64: info.assinaturaEnvolvidosBase64 ?? null,
			nomesEnvolvidosAdicionaisJson: info.nomesEnvolvidosAdicionaisJson ?? null,
			placaBiosseguranca: info.placaBiosseguranca,
			areaDelimitada: info.areaDelimitada,
			sojaBordadura: info.sojaBordadura,
			algodaoBordadura: info.algodaoBordadura,
			sojaIsolamento: info.sojaIsolamento,
			algodaoIsolamento: info.algodaoIsolamento,
			inspecaoRotina: info.inspecaoRotina,
			cercasCultivo: info.cercasCultivo,
			montagemIrrigacao: info.montagemIrrigacao,
			observacoesMontagemIrrigacao: info.observacoesMontagemIrrigacao,
			irrigacaoPosPlantio: info.irrigacaoPosPlantio,
			avaliacoesCultivo: info.avaliacoesCultivo,
			observacoesAvaliacoesCultivo: info.observacoesAvaliacoesCultivo,
			coleta: info.coleta,
			observacoesColeta: info.observacoesColeta,
			capinaRocagem: info.capinaRocagem,
			aplicacaoPulverizacao: info.aplicacaoPulverizacao,
			aplicacaoPulverizacaoFungicida: info.aplicacaoPulverizacaoFungicida,
			aplicacaoPulverizacaoHerbicida: info.aplicacaoPulverizacaoHerbicida,
			aplicacaoPulverizacaoInseticida: info.aplicacaoPulverizacaoInseticida,
			aplicacaoPulverizacaoOutros: info.aplicacaoPulverizacaoOutros,
			aplicacaoPulverizacaoOutrosDescricao: info.aplicacaoPulverizacaoOutrosDescricao,
			adubacao: info.adubacao,
			adubacaoCobertura: info.adubacaoCobertura,
			adubacaoFoliar: info.adubacaoFoliar,
			adubacaoOutros: info.adubacaoOutros,
			adubacaoOutrosDescricao: info.adubacaoOutrosDescricao,
			destruicaoBordadura: info.destruicaoBordadura,
			observacoesDestruicaoBordadura: info.observacoesDestruicaoBordadura,
			colheitaDestruicao: info.colheitaDestruicao,
			observacoesColheitaDestruicao: info.observacoesColheitaDestruicao,
			outrasAtividades: info.outrasAtividades,
			observacoesOutrasAtividades: info.observacoesOutrasAtividades,
			observacoesCultivo: info.observacoesCultivo,
			limpezaMaquinariosCultivoJson: info.limpezaMaquinariosCultivoJson || null,
		};
		
		const cultivosAtualizados = atualizarListaSubmitLpma(
			selectedCultivos,
			cultivoNormalizado,
			indiceCultivoEdicao,
			cultivoEmEdicao,
		);
		setSelectedCultivos(cultivosAtualizados);

		if (await salvarRascunhoCadernoLPMA({ cultivos: cultivosAtualizados })) {
			fecharModalCultivo();
		}
	}

	const prepararDuplicadoLpma = <T extends Record<string, unknown>>(origem: T): T => {
		const copia = JSON.parse(JSON.stringify(origem)) as T & { Id?: number };
		(copia as any).id = 0;
		delete (copia as any).Id;
		(copia as any).cadernoLPMAId = entity?.id ?? initialEntity?.id ?? 0;
		(copia as any).clientRowKey = gerarClientRowKeyLpma();
		const c = copia as Record<string, unknown>;
		if ('comunicadoCibioOficiosExternosNomeArquivo' in c) {
			c.comunicadoCibioOficiosExternosNomeArquivo = null;
			c.comunicadoCibioOficiosExternosContentType = null;
			c.comunicadoCibioOficiosExternosCaminhoBlob = null;
			c.comunicadoCibioOficiosExternosConteudoBase64 = null;
		}
		if ('cartaTransferenciaMaterialColhidoNomeArquivo' in c) {
			c.cartaTransferenciaMaterialColhidoNomeArquivo = null;
			c.cartaTransferenciaMaterialColhidoContentType = null;
			c.cartaTransferenciaMaterialColhidoCaminhoBlob = null;
			c.cartaTransferenciaMaterialColhidoConteudoBase64 = null;
		}
		if ('cartaTransferenciaNomeArquivo' in c) {
			c.cartaTransferenciaNomeArquivo = null;
			c.cartaTransferenciaContentType = null;
			c.cartaTransferenciaCaminhoBlob = null;
			c.cartaTransferenciaConteudoBase64 = null;
		}
		if ('comunicacaoAutorizacaoNomeArquivo' in c) {
			c.comunicacaoAutorizacaoNomeArquivo = null;
			c.comunicacaoAutorizacaoContentType = null;
			c.comunicacaoAutorizacaoCaminhoBlob = null;
			c.comunicacaoAutorizacaoConteudoBase64 = null;
		}
		if ('formularioPreenchidoNomeArquivo' in c) {
			c.formularioPreenchidoNomeArquivo = null;
			c.formularioPreenchidoContentType = null;
			c.formularioPreenchidoCaminhoBlob = null;
			c.formularioPreenchidoConteudoBase64 = null;
		}
		return copia;
	};

	const duplicarLinhaListaLpma = <T extends { id?: number; Id?: number; clientRowKey?: string }>(
		origem: T,
		indiceOrigem: number,
		setRows: React.Dispatch<React.SetStateAction<T[]>>,
	) => {
		const copia = prepararDuplicadoLpma(origem as Record<string, unknown>) as T;
		setRows((prev) => {
			let idx = indiceOrigem;
			if (idx < 0 || idx >= prev.length) {
				const idO = obterIdNumericoLPMA(origem as { id?: number; Id?: number });
				if (idO > 0) {
					idx = prev.findIndex((c) => obterIdNumericoLPMA(c as { id?: number; Id?: number }) === idO);
				}
			}
			if (idx >= 0 && idx < prev.length) {
				return [...prev.slice(0, idx + 1), copia, ...prev.slice(idx + 1)];
			}
			return [...prev, copia];
		});
	};

	const prepararCultivoDuplicado = (origem: CadernoLPMACultivo): CadernoLPMACultivo =>
		prepararDuplicadoLpma(origem as Record<string, unknown>) as CadernoLPMACultivo;

	const duplicarCultivoNaLista = (origem: CadernoLPMACultivo, indiceOrigem: number) => {
		duplicarLinhaListaLpma(origem, indiceOrigem, setSelectedCultivos);
	};

	const abrirModalCultivo = (registro?: CadernoLPMACultivo, indice?: number) => {
		const cultivoClonado = registro ? JSON.parse(JSON.stringify(registro)) : null;
		setCultivoEmEdicao(cultivoClonado);
		let indiceEfetivo: number | null =
			typeof indice === 'number' && !Number.isNaN(indice) && indice >= 0 ? indice : null;
		const idClonado = obterIdNumericoLPMA(cultivoClonado as { id?: number; Id?: number });
		if (indiceEfetivo === null && idClonado > 0) {
			const porId = selectedCultivos.findIndex(
				(c) => obterIdNumericoLPMA(c as { id?: number; Id?: number }) === idClonado,
			);
			if (porId >= 0) {
				indiceEfetivo = porId;
			}
		}
		if (indiceEfetivo === null && idClonado === 0 && cultivoClonado?.clientRowKey) {
			const porKey = selectedCultivos.findIndex((c) => c.clientRowKey === cultivoClonado.clientRowKey);
			if (porKey >= 0) {
				indiceEfetivo = porKey;
			}
		}
		setIndiceCultivoEdicao(indiceEfetivo);
		setModalCultivos(true);
	};

	const fecharModalCultivo = () => {
		setModalCultivos(false);
		setCultivoEmEdicao(null);
		setIndiceCultivoEdicao(null);
	}
	const onSubmitColheita = async (info: CadernoLPMAColheita): Promise<void> => {
		const registroNormalizado = prepararRegistroModalLpma(info, colheitaEmEdicao, entity?.id ?? initialEntity?.id ?? null);
		const colheitasAtualizadas = atualizarListaSubmitLpma(
			selectedColheitas,
			registroNormalizado,
			indiceColheitaEdicao,
			colheitaEmEdicao,
		);
		setSelectedColheitas(colheitasAtualizadas);

		if (await salvarRascunhoCadernoLPMA({ colheitas: colheitasAtualizadas })) {
			setModalColheitas(false);
			setColheitaEmEdicao(null);
			setIndiceColheitaEdicao(null);
		}
	}

	const onSubmitMonitoramento = async (info: CadernoLPMAMonitoramento): Promise<void> => {
		const registroNormalizado = prepararRegistroModalLpma(info, monitoramentoEmEdicao, entity?.id ?? initialEntity?.id ?? null);
		const monitoramentosAtualizados = atualizarListaSubmitLpma(
			selectedMonitoramentos,
			registroNormalizado,
			indiceMonitoramentoEdicao,
			monitoramentoEmEdicao,
		);
		setSelectedMonitoramentos(monitoramentosAtualizados);

		if (await salvarRascunhoCadernoLPMA({ monitoramentos: monitoramentosAtualizados })) {
			setModalMonitoramentos(false);
			setMonitoramentoEmEdicao(null);
			setIndiceMonitoramentoEdicao(null);
		}
	}

	const onSubmitDestruicaoDescarte = async (info: CadernoLPMADestruicaoDescarte): Promise<void> => {
		const registroNormalizado = prepararRegistroModalLpma(info, destruicaoDescarteEmEdicao, entity?.id ?? initialEntity?.id ?? null);
		const destruicaoDescartesAtualizados = atualizarListaSubmitLpma(
			selectedDestruicaoDescartes,
			registroNormalizado,
			indiceDestruicaoDescarteEdicao,
			destruicaoDescarteEmEdicao,
		);
		setSelectedDestruicaoDescartes(destruicaoDescartesAtualizados);

		if (await salvarRascunhoCadernoLPMA({ destruicaoDescartes: destruicaoDescartesAtualizados })) {
			setModalDestruicaoDescartes(false);
			setDestruicaoDescarteEmEdicao(null);
			setIndiceDestruicaoDescarteEdicao(null);
		}
	}

	const onSubmitAmostragem = async (info: CadernoLPMAAmostragem): Promise<void> => {
		const registroNormalizado = prepararRegistroModalLpma(info, amostragemEmEdicao, entity?.id ?? initialEntity?.id ?? null);
		const amostragensAtualizadas = atualizarListaSubmitLpma(
			selectedAmostragens,
			registroNormalizado,
			indiceAmostragemEdicao,
			amostragemEmEdicao,
		);
		setSelectedAmostragens(amostragensAtualizadas);

		if (await salvarRascunhoCadernoLPMA({ amostragens: amostragensAtualizadas })) {
			setModalAmostragens(false);
			setAmostragemEmEdicao(null);
			setIndiceAmostragemEdicao(null);
		}
	}

	const onSubmitAvaliacaoAgronomica = async (info: CadernoLPMAAvaliacaoAgronomica): Promise<void> => {
		const registroNormalizado = prepararRegistroModalLpma(info, avaliacaoAgronomicaEmEdicao, entity?.id ?? initialEntity?.id ?? null);
		const avaliacoesAtualizadas = atualizarListaSubmitLpma(
			selectedAvaliacoesAgronomicas,
			registroNormalizado,
			indiceAvaliacaoAgronomicaEdicao,
			avaliacaoAgronomicaEmEdicao,
		);
		setSelectedAvaliacoesAgronomicas(avaliacoesAtualizadas);

		if (await salvarRascunhoCadernoLPMA({ avaliacoesAgronomicas: avaliacoesAtualizadas })) {
			setModalAvaliacoesAgronomicas(false);
			setAvaliacaoAgronomicaEmEdicao(null);
			setIndiceAvaliacaoAgronomicaEdicao(null);
		}
	}

	const onSubmitDestruicaoBordadura = async (info: CadernoLPMADestruicaoBordadura): Promise<void> => {
		const registroNormalizado = prepararRegistroModalLpma(info, destruicaoBordaduraEmEdicao, entity?.id ?? initialEntity?.id ?? null);
		const destruicaoBordadurasAtualizadas = atualizarListaSubmitLpma(
			selectedDestruicaoBordaduras,
			registroNormalizado,
			indiceDestruicaoBordaduraEdicao,
			destruicaoBordaduraEmEdicao,
		);
		setSelectedDestruicaoBordaduras(destruicaoBordadurasAtualizadas);

		if (await salvarRascunhoCadernoLPMA({ destruicaoBordadura: destruicaoBordadurasAtualizadas })) {
			setModalDestruicaoBordaduras(false);
			setDestruicaoBordaduraEmEdicao(null);
			setIndiceDestruicaoBordaduraEdicao(null);
		}
	}

	const onSubmitPlantioOutraCultura = async (info: CadernoLPMAPlantioOutraCultura): Promise<void> => {
		const previsao =
			info.previsaoLPMA === undefined || info.previsaoLPMA === null || info.previsaoLPMA === ''
				? null
				: info.previsaoLPMA === true || info.previsaoLPMA === 'true';

		const registroNormalizado = prepararRegistroModalLpma(
			{ ...info, previsaoLPMA: previsao },
			plantioOutraCulturaEmEdicao,
			entity?.id ?? initialEntity?.id ?? null,
		);
		const plantiosAtualizados = atualizarListaSubmitLpma(
			selectedPlantiosOutraCultura,
			registroNormalizado,
			indicePlantioOutraCulturaEdicao,
			plantioOutraCulturaEmEdicao,
		);
		setSelectedPlantiosOutraCultura(plantiosAtualizados);

		if (await salvarRascunhoCadernoLPMA({ plantiosOutraCultura: plantiosAtualizados })) {
			setModalPlantiosOutraCultura(false);
			setPlantioOutraCulturaEmEdicao(null);
			setIndicePlantioOutraCulturaEdicao(null);
		}
	}

	const onSubmitExtensaoMonitoramento = async (info: CadernoLPMAExtensaoMonitoramento): Promise<void> => {
		const previsao =
			info.previsaoLPMA === undefined || info.previsaoLPMA === null || info.previsaoLPMA === ''
				? null
				: info.previsaoLPMA === true || info.previsaoLPMA === 'true';

		const registroNormalizado = prepararRegistroModalLpma(
			{ ...info, previsaoLPMA: previsao },
			extensaoMonitoramentoEmEdicao,
			entity?.id ?? initialEntity?.id ?? null,
		);
		const extensoesAtualizadas = atualizarListaSubmitLpma(
			selectedExtensaoMonitoramentos,
			registroNormalizado,
			indiceExtensaoMonitoramentoEdicao,
			extensaoMonitoramentoEmEdicao,
		);
		setSelectedExtensaoMonitoramentos(extensoesAtualizadas);

		if (await salvarRascunhoCadernoLPMA({ extensaoMonitoramentos: extensoesAtualizadas })) {
			setModalExtensaoMonitoramentos(false);
			setExtensaoMonitoramentoEmEdicao(null);
			setIndiceExtensaoMonitoramentoEdicao(null);
		}
	}

	const onSubmitReportesRelatorios = async (info: CadernoLPMAReportesRelatorios): Promise<void> => {
		const registroNormalizado = prepararRegistroModalLpma(info, reporteRelatorioEmEdicao, entity?.id ?? initialEntity?.id ?? null);
		const reportesAtualizados = atualizarListaSubmitLpma(
			selectedReportesRelatorios,
			registroNormalizado,
			indiceReporteRelatorioEdicao,
			reporteRelatorioEmEdicao,
		);
		setSelectedReportesRelatorios(reportesAtualizados);

		if (await salvarRascunhoCadernoLPMA({ reportesRelatorios: reportesAtualizados })) {
			setModalReportesRelatorios(false);
			setReporteRelatorioEmEdicao(null);
			setIndiceReporteRelatorioEdicao(null);
		}
	}

	const onSubmitAnotacoes = async (info: CadernoLPMAAnotacoes): Promise<void> => {
		const em = anotacaoEmEdicao;
		const idxGuardado = indiceAnotacaoEdicao;
		const idx = resolverIndiceSubmitLpma(selectedAnotacoes, idxGuardado, em);
		const registroNormalizado: CadernoLPMAAnotacoes =
			em !== null && idx !== null && idx >= 0 && idx < selectedAnotacoes.length
				? {
					...em,
					dataAnotacao: info.dataAnotacao ?? em.dataAnotacao,
					usuarioAnotacao: info.usuarioAnotacao ?? em.usuarioAnotacao,
					textoAnotacao: info.textoAnotacao,
				}
				: {
					id: 0,
					cadernoLPMAId: entity?.id ?? initialEntity?.id ?? 0,
					clientRowKey: gerarClientRowKeyLpma(),
					dataAnotacao: info.dataAnotacao ?? moment().add(-3, 'h'),
					usuarioAnotacao: info.usuarioAnotacao ?? decoded.unique_name,
					textoAnotacao: info.textoAnotacao,
				};
		const anotacoesAtualizadas = atualizarListaSubmitLpma(
			selectedAnotacoes,
			registroNormalizado,
			idxGuardado,
			em,
		);
		setSelectedAnotacoes(anotacoesAtualizadas);

		if (await salvarRascunhoCadernoLPMA({ anotacoes: anotacoesAtualizadas })) {
			fecharModalAnotacoes();
		}
	};

	const onSubmitAssinatura = async (info: CadernoLPMAAssinatura): Promise<void> => {
		const registroNormalizado = prepararRegistroModalLpma(
			info,
			assinaturaEmEdicao,
			entity?.id ?? initialEntity?.id ?? null,
		);
		const assinaturasAtualizadas = atualizarListaSubmitLpma(
			selectedAssinaturas,
			registroNormalizado,
			indiceAssinaturaEdicao,
			assinaturaEmEdicao,
		);
		setSelectedAssinaturas(assinaturasAtualizadas);

		if (await salvarRascunhoCadernoLPMA({ assinaturas: assinaturasAtualizadas })) {
			fecharModalAssinaturas();
		}
	};

	// Regi�o actions
	const actionsTransportes = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedTransportes, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				abrirModalTransporte(props.data, indice >= 0 ? indice : undefined);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedTransportes);
				}}
			/>
			&nbsp;&nbsp;
			<DeleteButton onClick={async () => {
				const { node } = props;
				const itemData = node.data;

				// Se o item já foi salvo no banco (tem ID), chama a API para deletar
				if (itemData.id && itemData.id > 0) {
					if (window.confirm('Tem certeza que deseja excluir este transporte?')) {
						try {
							await api({
								method: 'delete',
								url: `api/v1/cadernoLPMA/cadernoLPMA/transporte/${itemData.id}`,
							});
							
							// Remove da grid após deletar com sucesso
							node.gridApi.updateRowData({ remove: [itemData] });
							
							let arr: any = [];
							node.gridApi.forEachNode(({ data }: any) => arr.push(data));
							setSelectedTransportes([...arr]);
						} catch (error) {
							console.error('Erro ao excluir transporte:', error);
							alert('Erro ao excluir transporte. Tente novamente.');
						}
					}
				} else {
					// Se ainda não foi salvo (sem ID), apenas remove da grid local
					setSelectedTransportes([]);
					let arr: any = [];

					node.gridApi.updateRowData({ remove: [itemData] })

					node.gridApi.forEachNode(({ data }: any) => arr.push(data));
					setSelectedTransportes([...arr]);
				}
			}} />
		</Fragment>
		);
	};

	const actionsRegistroVisitas = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedRegistroVisitas, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton
				onClick={() => {
					abrirModalRegistroVisita(props.data, indice >= 0 ? indice : undefined);
				}}
			/>
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedRegistroVisitas);
				}}
			/>
			&nbsp;&nbsp;
			<DeleteButton
				onClick={async () => {
					const { node } = props;
					const itemData = node.data;

					// Se o item já foi salvo no banco (tem ID), chama a API para deletar
					if (itemData.id && itemData.id > 0) {
						if (window.confirm('Tem certeza que deseja excluir este registro de visita?')) {
							try {
								await api({
									method: 'delete',
									url: `api/v1/cadernoLPMA/cadernoLPMA/registrovisita/${itemData.id}`,
								});
								
								// Remove da grid após deletar com sucesso
								node.gridApi.updateRowData({ remove: [itemData] });
								
								let arr: any = [];
								node.gridApi.forEachNode(({ data }: any) => arr.push(data));
								setSelectedRegistroVisitas([...arr]);
							} catch (error) {
								console.error('Erro ao excluir registro de visita:', error);
								alert('Erro ao excluir registro de visita. Tente novamente.');
							}
						}
					} else {
						// Se ainda não foi salvo (sem ID), apenas remove da grid local
						setSelectedRegistroVisitas([]);
						let arr: any = [];

						node.gridApi.updateRowData({ remove: [itemData] });

						node.gridApi.forEachNode(({ data }: any) => arr.push(data));
						setSelectedRegistroVisitas([...arr]);
					}
				}}
			/>
		</Fragment>
		);
	};

	const actionsPlantiosMilho = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedPlantiosMilho, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton
				onClick={() => {
					abrirModalPlantioMilho(props.data, indice >= 0 ? indice : undefined);
				}}
			/>
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedPlantiosMilho);
				}}
			/>
			&nbsp;&nbsp;
			<DeleteButton
				onClick={async () => {
					const { node } = props;
					const itemData = node.data;

					// Se o item já foi salvo no banco (tem ID), chama a API para deletar
					if (itemData.id && itemData.id > 0) {
						if (window.confirm('Tem certeza que deseja excluir este plantio?')) {
							try {
								await api({
									method: 'delete',
									url: `api/v1/cadernoLPMA/cadernoLPMA/plantio/${itemData.id}`,
								});
								
								// Remove da grid após deletar com sucesso
								node.gridApi.updateRowData({ remove: [itemData] });
								
								let arr: any = [];
								node.gridApi.forEachNode(({ data }: any) => arr.push(data));
								setSelectedPlantiosMilho([...arr]);
								setPlantioMilhoEmEdicao(null);
								setIndicePlantioMilhoEdicao(null);
							} catch (error) {
								console.error('Erro ao excluir plantio:', error);
								alert('Erro ao excluir plantio. Tente novamente.');
							}
						}
					} else {
						// Se ainda não foi salvo (sem ID), apenas remove da grid local
						setSelectedPlantiosMilho([]);
						let arr: any = [];

						node.gridApi.updateRowData({ remove: [itemData] });

						node.gridApi.forEachNode(({ data }: any) => arr.push(data));
						setSelectedPlantiosMilho([...arr]);
						setPlantioMilhoEmEdicao(null);
						setIndicePlantioMilhoEdicao(null);
					}
				}}
			/>
		</Fragment>
		);
	};

	const actionsPlantiosSoja = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedPlantiosSoja, props.data, props.node?.rowIndex);
		return (
		<Fragment>
		<EditButton
			onClick={() => {
				abrirModalPlantioSoja(props.data, indice >= 0 ? indice : undefined);
			}}
		/>
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedPlantiosSoja);
				}}
			/>
			&nbsp;&nbsp;
			<DeleteButton onClick={async () => {
				const { node } = props;
				const itemData = node.data;

				// Se o item já foi salvo no banco (tem ID), chama a API para deletar
				if (itemData.id && itemData.id > 0) {
					if (window.confirm('Tem certeza que deseja excluir este plantio de soja?')) {
						try {
							await api({
								method: 'delete',
								url: `api/v1/cadernoLPMA/cadernoLPMA/plantiosoja/${itemData.id}`,
							});
							
							// Remove da grid após deletar com sucesso
							node.gridApi.updateRowData({ remove: [itemData] });
							
							let arr: any = [];
							node.gridApi.forEachNode(({ data }: any) => arr.push(data));
							setSelectedPlantiosSoja([...arr]);
							setPlantioSojaEmEdicao(null);
							setIndicePlantioSojaEdicao(null);
						} catch (error) {
							console.error('Erro ao excluir plantio de soja:', error);
							alert('Erro ao excluir plantio de soja. Tente novamente.');
						}
					}
				} else {
					// Se ainda não foi salvo (sem ID), apenas remove da grid local
					setSelectedPlantiosSoja([]);
					let arr: any = [];

					node.gridApi.updateRowData({ remove: [itemData] })

					node.gridApi.forEachNode(({ data }: any) => arr.push(data));
					setSelectedPlantiosSoja([...arr]);
					setPlantioSojaEmEdicao(null);
					setIndicePlantioSojaEdicao(null);
				}
			}} />
		</Fragment>
		);
	};

	const actionsCultivos = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedCultivos, props.data, props.node?.rowIndex);
		
		return (
			<Fragment>
				<EditButton onClick={() => {
					abrirModalCultivo(props.data, indice);
				}} />
				&nbsp;&nbsp;
				<DuplicateButton
					onClick={() => {
						duplicarCultivoNaLista(props.data, indice);
					}}
				/>
				&nbsp;&nbsp;
			<DeleteButton onClick={async () => {
				const { node } = props;
				const itemData = node.data;
				const idExcluir = obterIdNumericoLPMA(itemData as { id?: number; Id?: number });

				// Se o item já foi salvo no banco (tem ID), chama a API para deletar
				if (idExcluir > 0) {
					if (window.confirm('Tem certeza que deseja excluir este cultivo?')) {
						try {
						await api({
							method: 'delete',
							url: `api/v1/cadernoLPMA/cadernoLPMA/cultivo/${idExcluir}`,
						});
							
							// Remove da grid após deletar com sucesso
							node.gridApi.updateRowData({ remove: [itemData] });
							
							let arr: any = [];
							node.gridApi.forEachNode(({ data }: any) => arr.push(data));
							setSelectedCultivos([...arr]);
						} catch (error) {
							console.error('Erro ao excluir cultivo:', error);
							alert('Erro ao excluir cultivo. Tente novamente.');
						}
					}
				} else {
					// Se ainda não foi salvo (sem ID), apenas remove da grid local
					setSelectedCultivos([]);
					let arr: any = [];

					node.gridApi.updateRowData({ remove: [itemData] })

					node.gridApi.forEachNode(({ data }: any) => arr.push(data));
					setSelectedCultivos([...arr]);
				}
			}} />
			</Fragment>
		);
	}

	const actionsColheitas = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedColheitas, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				setColheitaEmEdicao(JSON.parse(JSON.stringify(props.data)));
				setIndiceColheitaEdicao(indice >= 0 ? indice : null);
				setModalColheitas(true);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedColheitas);
				}}
			/>
			&nbsp;&nbsp;
		<DeleteButton onClick={async () => {
			const { node } = props;
			const itemData = node.data;

			// Se o item já foi salvo no banco (tem ID), chama a API para deletar
			const idDelColheita = obterIdNumericoLPMA(itemData as { id?: number; Id?: number });
			if (idDelColheita > 0) {
				if (window.confirm('Tem certeza que deseja excluir esta colheita?')) {
					try {
					await api({
						method: 'delete',
						url: `api/v1/cadernoLPMA/cadernoLPMA/colheita/${idDelColheita}`,
					});
						
						// Remove da grid após deletar com sucesso
						node.gridApi.updateRowData({ remove: [itemData] });
						
						let arr: any = [];
						node.gridApi.forEachNode(({ data }: any) => arr.push(data));
						setSelectedColheitas([...arr]);
					} catch (error) {
						console.error('Erro ao excluir colheita:', error);
						alert('Erro ao excluir colheita. Tente novamente.');
					}
				}
			} else {
				// Se ainda não foi salvo (sem ID), apenas remove da grid local
				setSelectedColheitas([]);
				let arr: any = [];

				node.gridApi.updateRowData({ remove: [itemData] })

				node.gridApi.forEachNode(({ data }: any) => arr.push(data));
				setSelectedColheitas([...arr]);
			}
		}} />
		</Fragment>
		);
	};

	const actionsMonitoramentos = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedMonitoramentos, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				setMonitoramentoEmEdicao(JSON.parse(JSON.stringify(props.data)));
				setIndiceMonitoramentoEdicao(indice >= 0 ? indice : null);
				setModalMonitoramentos(true);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedMonitoramentos);
				}}
			/>
			&nbsp;&nbsp;
		<DeleteButton onClick={async () => {
			const { node } = props;
			const itemData = node.data;

			const idDelMon = obterIdNumericoLPMA(itemData as { id?: number; Id?: number });
			if (idDelMon > 0) {
				if (window.confirm('Tem certeza que deseja excluir este monitoramento?')) {
					try {
					await api({
						method: 'delete',
						url: `api/v1/cadernoLPMA/cadernoLPMA/monitoramento/${idDelMon}`,
					});
						
						// Remove da grid após deletar com sucesso
						node.gridApi.updateRowData({ remove: [itemData] });
						
						let arr: any = [];
						node.gridApi.forEachNode(({ data }: any) => arr.push(data));
						setSelectedMonitoramentos([...arr]);
					} catch (error) {
						console.error('Erro ao excluir monitoramento:', error);
						alert('Erro ao excluir monitoramento. Tente novamente.');
					}
				}
			} else {
				// Se ainda não foi salvo (sem ID), apenas remove da grid local
				setSelectedMonitoramentos([]);
				let arr: any = [];

				node.gridApi.updateRowData({ remove: [itemData] })

				node.gridApi.forEachNode(({ data }: any) => arr.push(data));
				setSelectedMonitoramentos([...arr]);
			}
		}} />
		</Fragment>
		);
	};

	const actionsDestruicaoDescartes = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedDestruicaoDescartes, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				setDestruicaoDescarteEmEdicao(JSON.parse(JSON.stringify(props.data)));
				setIndiceDestruicaoDescarteEdicao(indice >= 0 ? indice : null);
				setModalDestruicaoDescartes(true);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedDestruicaoDescartes);
				}}
			/>
			&nbsp;&nbsp;
		<DeleteButton onClick={async () => {
			const { node } = props;
			const itemData = node.data;

			const idDelDd = obterIdNumericoLPMA(itemData as { id?: number; Id?: number });
			if (idDelDd > 0) {
				if (window.confirm('Tem certeza que deseja excluir esta destruição/descarte?')) {
					try {
					await api({
						method: 'delete',
						url: `api/v1/cadernoLPMA/cadernoLPMA/destruicaodescarte/${idDelDd}`,
					});
						
						// Remove da grid após deletar com sucesso
						node.gridApi.updateRowData({ remove: [itemData] });
						
						let arr: any = [];
						node.gridApi.forEachNode(({ data }: any) => arr.push(data));
						setSelectedDestruicaoDescartes([...arr]);
					} catch (error) {
						console.error('Erro ao excluir destruição/descarte:', error);
						alert('Erro ao excluir destruição/descarte. Tente novamente.');
					}
				}
			} else {
				// Se ainda não foi salvo (sem ID), apenas remove da grid local
				setSelectedDestruicaoDescartes([]);
				let arr: any = [];

				node.gridApi.updateRowData({ remove: [itemData] })

				node.gridApi.forEachNode(({ data }: any) => arr.push(data));
				setSelectedDestruicaoDescartes([...arr]);
			}
		}} />
		</Fragment>
		);
	};

	const actionsAmostragens = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedAmostragens, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				setAmostragemEmEdicao(JSON.parse(JSON.stringify(props.data)));
				setIndiceAmostragemEdicao(indice >= 0 ? indice : null);
				setModalAmostragens(true);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedAmostragens);
				}}
			/>
			&nbsp;&nbsp;
		<DeleteButton onClick={async () => {
			const { node } = props;
			const itemData = node.data;

			const idDelAm = obterIdNumericoLPMA(itemData as { id?: number; Id?: number });
			if (idDelAm > 0) {
				if (window.confirm('Tem certeza que deseja excluir esta amostragem?')) {
					try {
					await api({
						method: 'delete',
						url: `api/v1/cadernoLPMA/cadernoLPMA/amostragem/${idDelAm}`,
					});
						
						// Remove da grid após deletar com sucesso
						node.gridApi.updateRowData({ remove: [itemData] });
						
						let arr: any = [];
						node.gridApi.forEachNode(({ data }: any) => arr.push(data));
						setSelectedAmostragens([...arr]);
					} catch (error) {
						console.error('Erro ao excluir amostragem:', error);
						alert('Erro ao excluir amostragem. Tente novamente.');
					}
				}
			} else {
				// Se ainda não foi salvo (sem ID), apenas remove da grid local
				setSelectedAmostragens([]);
				let arr: any = [];

				node.gridApi.updateRowData({ remove: [itemData] })

				node.gridApi.forEachNode(({ data }: any) => arr.push(data));
				setSelectedAmostragens([...arr]);
			}
		}} />
		</Fragment>
		);
	};

	const actionsAvaliacoesAgronomicas = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedAvaliacoesAgronomicas, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				setAvaliacaoAgronomicaEmEdicao(JSON.parse(JSON.stringify(props.data)));
				setIndiceAvaliacaoAgronomicaEdicao(indice >= 0 ? indice : null);
				setModalAvaliacoesAgronomicas(true);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedAvaliacoesAgronomicas);
				}}
			/>
			&nbsp;&nbsp;
		<DeleteButton onClick={async () => {
			const { node } = props;
			const itemData = node.data;

			const idDelAv = obterIdNumericoLPMA(itemData as { id?: number; Id?: number });
			if (idDelAv > 0) {
				if (window.confirm('Tem certeza que deseja excluir esta avaliação agronômica?')) {
					try {
					await api({
						method: 'delete',
						url: `api/v1/cadernoLPMA/cadernoLPMA/avaliacaoagronomica/${idDelAv}`,
					});
						
						// Remove da grid após deletar com sucesso
						node.gridApi.updateRowData({ remove: [itemData] });
						
						let arr: any = [];
						node.gridApi.forEachNode(({ data }: any) => arr.push(data));
						setSelectedAvaliacoesAgronomicas([...arr]);
					} catch (error) {
						console.error('Erro ao excluir avaliação agronômica:', error);
						alert('Erro ao excluir avaliação agronômica. Tente novamente.');
					}
				}
			} else {
				// Se ainda não foi salvo (sem ID), apenas remove da grid local
				setSelectedAvaliacoesAgronomicas([]);
				let arr: any = [];

				node.gridApi.updateRowData({ remove: [itemData] })

				node.gridApi.forEachNode(({ data }: any) => arr.push(data));
				setSelectedAvaliacoesAgronomicas([...arr]);
			}
		}} />
		</Fragment>
		);
	};

	const actionsDestruicaoBordaduras = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedDestruicaoBordaduras, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				setDestruicaoBordaduraEmEdicao(JSON.parse(JSON.stringify(props.data)));
				setIndiceDestruicaoBordaduraEdicao(indice >= 0 ? indice : null);
				setModalDestruicaoBordaduras(true);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedDestruicaoBordaduras);
				}}
			/>
			&nbsp;&nbsp;
		<DeleteButton onClick={async () => {
			const { node } = props;
			const itemData = node.data;

			const idDelBord = obterIdNumericoLPMA(itemData as { id?: number; Id?: number });
			if (idDelBord > 0) {
				if (window.confirm('Tem certeza que deseja excluir esta destruição de bordadura?')) {
					try {
					await api({
						method: 'delete',
						url: `api/v1/cadernoLPMA/cadernoLPMA/destruicaobordadura/${idDelBord}`,
					});
						
						// Remove da grid após deletar com sucesso
						node.gridApi.updateRowData({ remove: [itemData] });
						
						let arr: any = [];
						node.gridApi.forEachNode(({ data }: any) => arr.push(data));
						setSelectedDestruicaoBordaduras([...arr]);
					} catch (error) {
						console.error('Erro ao excluir destruição de bordadura:', error);
						alert('Erro ao excluir destruição de bordadura. Tente novamente.');
					}
				}
			} else {
				// Se ainda não foi salvo (sem ID), apenas remove da grid local
				setSelectedDestruicaoBordaduras([]);
				let arr: any = [];

				node.gridApi.updateRowData({ remove: [itemData] })

				node.gridApi.forEachNode(({ data }: any) => arr.push(data));
				setSelectedDestruicaoBordaduras([...arr]);
			}
		}} />
		</Fragment>
		);
	};

	const actionsPlantiosOutraCultura = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedPlantiosOutraCultura, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				setPlantioOutraCulturaEmEdicao(JSON.parse(JSON.stringify(props.data)));
				setIndicePlantioOutraCulturaEdicao(indice >= 0 ? indice : null);
				setModalPlantiosOutraCultura(true);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedPlantiosOutraCultura);
				}}
			/>
			&nbsp;&nbsp;
			<DeleteButton onClick={() => {
				setSelectedPlantiosOutraCultura([]);
				let arr: any = [];
				const { node } = props;

				node.gridApi.updateRowData({ remove: [node.data] })

				node.gridApi.forEachNode(({ data }: any) => arr.push(data));
				setSelectedPlantiosOutraCultura([...arr]);
			}} />
		</Fragment>
		);
	};

	const actionsExtensaoMonitoramentos = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedExtensaoMonitoramentos, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				setExtensaoMonitoramentoEmEdicao(JSON.parse(JSON.stringify(props.data)));
				setIndiceExtensaoMonitoramentoEdicao(indice >= 0 ? indice : null);
				setModalExtensaoMonitoramentos(true);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedExtensaoMonitoramentos);
				}}
			/>
			&nbsp;&nbsp;
		<DeleteButton onClick={async () => {
			const { node } = props;
			const itemData = node.data;

			const idDelExt = obterIdNumericoLPMA(itemData as { id?: number; Id?: number });
			if (idDelExt > 0) {
				if (window.confirm('Tem certeza que deseja excluir esta extensão de monitoramento?')) {
					try {
					await api({
						method: 'delete',
						url: `api/v1/cadernoLPMA/cadernoLPMA/extensaomonitoramento/${idDelExt}`,
					});
						
						// Remove da grid após deletar com sucesso
						node.gridApi.updateRowData({ remove: [itemData] });
						
						let arr: any = [];
						node.gridApi.forEachNode(({ data }: any) => arr.push(data));
						setSelectedExtensaoMonitoramentos([...arr]);
					} catch (error) {
						console.error('Erro ao excluir extensão de monitoramento:', error);
						alert('Erro ao excluir extensão de monitoramento. Tente novamente.');
					}
				}
			} else {
				// Se ainda não foi salvo (sem ID), apenas remove da grid local
				setSelectedExtensaoMonitoramentos([]);
				let arr: any = [];

				node.gridApi.updateRowData({ remove: [itemData] })

				node.gridApi.forEachNode(({ data }: any) => arr.push(data));
				setSelectedExtensaoMonitoramentos([...arr]);
			}
		}} />
		</Fragment>
		);
	};

	const actionsReportesRelatorios = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedReportesRelatorios, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				setReporteRelatorioEmEdicao(JSON.parse(JSON.stringify(props.data)));
				setIndiceReporteRelatorioEdicao(indice >= 0 ? indice : null);
				setModalReportesRelatorios(true);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedReportesRelatorios);
				}}
			/>
			&nbsp;&nbsp;
		<DeleteButton onClick={async () => {
			const { node } = props;
			const itemData = node.data;

			const idDelRep = obterIdNumericoLPMA(itemData as { id?: number; Id?: number });
			if (idDelRep > 0) {
				if (window.confirm('Tem certeza que deseja excluir este reporte/relatório?')) {
					try {
					await api({
						method: 'delete',
						url: `api/v1/cadernoLPMA/cadernoLPMA/reporterelatorio/${idDelRep}`,
					});
						
						// Remove da grid após deletar com sucesso
						node.gridApi.updateRowData({ remove: [itemData] });
						
						let arr: any = [];
						node.gridApi.forEachNode(({ data }: any) => arr.push(data));
						setSelectedReportesRelatorios([...arr]);
					} catch (error) {
						console.error('Erro ao excluir reporte/relatório:', error);
						alert('Erro ao excluir reporte/relatório. Tente novamente.');
					}
				}
			} else {
				// Se ainda não foi salvo (sem ID), apenas remove da grid local
				setSelectedReportesRelatorios([]);
				let arr: any = [];

				node.gridApi.updateRowData({ remove: [itemData] })

				node.gridApi.forEachNode(({ data }: any) => arr.push(data));
				setSelectedReportesRelatorios([...arr]);
			}
		}} />
		</Fragment>
		);
	};

const obterDatasColheitaParaGrid = (data: any): { dataInicio: any; dataTermino: any } => {
	let dataInicio = data?.dataInicioColheita;
	let dataTermino = data?.dataTerminoColheita;

	if (dataInicio && dataTermino) {
		return { dataInicio, dataTermino };
	}

	try {
		const detalhamento = data?.detalhamentoColheitaJson
			? JSON.parse(data.detalhamentoColheitaJson)
			: [];
		if (Array.isArray(detalhamento) && detalhamento.length) {
			const inicios = detalhamento
				.map((item: any) => (item?.dataInicio ? new Date(item.dataInicio) : null))
				.filter((d: Date | null): d is Date => !!d && !Number.isNaN(d.getTime()));
			const terminos = detalhamento
				.map((item: any) => (item?.dataTermino ? new Date(item.dataTermino) : null))
				.filter((d: Date | null): d is Date => !!d && !Number.isNaN(d.getTime()));

			if (!dataInicio && inicios.length) {
				dataInicio = new Date(Math.min(...inicios.map((d) => d.getTime())));
			}
			if (!dataTermino && terminos.length) {
				dataTermino = new Date(Math.max(...terminos.map((d) => d.getTime())));
			}
		}
	} catch (e) {
		// JSON inválido: mantém apenas as datas principais
	}

	return { dataInicio, dataTermino };
}

const formatClassificacaoEvento = (valor: string | null | undefined) => {
	if (!valor) {
		return '';
	}

	if (valor === 'ACIDENTE') {
		return 'Acidente';
	}

	if (valor === 'INCIDENTE') {
		return 'Incidente';
	}

	return valor;
}

	const actionsAnotacoes = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedAnotacoes, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				abrirModalAnotacoes(props.data, indice);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedAnotacoes);
				}}
			/>
			&nbsp;&nbsp;
			<DeleteButton onClick={() => {
				setSelectedAnotacoes([]);
				let arr: any = [];
				const { node } = props;

				node.gridApi.updateRowData({ remove: [node.data] })

				node.gridApi.forEachNode(({ data }: any) => arr.push(data));
				setSelectedAnotacoes([...arr]);
			}} />
		</Fragment>
		);
	};

	const actionsAssinaturas = (props: any) => {
		if (!canEditOutrasAbas) {
			return <Fragment />;
		}
		const indice = resolverIndiceEdicaoGridLinha(selectedAssinaturas, props.data, props.node?.rowIndex);
		return (
		<Fragment>
			<EditButton onClick={() => {
				abrirModalAssinaturas(props.data, indice);
			}} />
			&nbsp;&nbsp;
			<DuplicateButton
				onClick={() => {
					duplicarLinhaListaLpma(props.data, indice, setSelectedAssinaturas);
				}}
			/>
			&nbsp;&nbsp;
			<DeleteButton onClick={async () => {
				const { node } = props;
				const itemData = node.data;
				const idDel = obterIdNumericoLPMA(itemData as { id?: number; Id?: number });
				if (idDel > 0 && !window.confirm('Tem certeza que deseja excluir esta assinatura?')) {
					return;
				}

				try {
					if (idDel > 0) {
						await api({
							method: 'delete',
							url: `api/v1/cadernoLPMA/cadernoLPMA/assinatura/${idDel}`,
						});
					}

					node.gridApi.updateRowData({ remove: [itemData] });
					const arr: CadernoLPMAAssinatura[] = [];
					node.gridApi.forEachNode(({ data }: any) => arr.push(data));
					setSelectedAssinaturas([...arr]);
				} catch (error) {
					console.error('Erro ao excluir assinatura:', error);
					alert('Erro ao excluir assinatura. Tente novamente.');
				}
			}} />
		</Fragment>
		);
	};
	let ParseButtonsFluxo1 = <Fragment />
	if (0 === 0)
	{
		ParseButtonsFluxo1 = <div className="button-position-cadernoLpma tab-buttons-local">
			<Button
				title="Dados Principais"
				contained={false}
				className={modoFluxo === MODO_VIEW.DADOS_PRINCIPAIS ? "active" : ''}
				type="button"
				onClick={() => {
					alterarFluxo(MODO_VIEW.DADOS_PRINCIPAIS);
				}}
			/>

			<Button
				title="Sementes para ensaio(s)"
				contained={false}
				className={modoFluxo === MODO_VIEW.TRANSPORTE ? "active" : ''}
				type="button"
				disabled={bloquearAbasPorCarregamentoDadosPrincipais}
				onClick={() => {
					alterarFluxo(MODO_VIEW.TRANSPORTE);
				}}
			/>

			<Button
				title="Visitas"
				contained={false}
				className={modoFluxo === MODO_VIEW.REGISTROVISITAS ? "active" : ''}
				type="button"
				disabled={bloquearAbasPorCarregamentoDadosPrincipais}
				onClick={() => {
					alterarFluxo(MODO_VIEW.REGISTROVISITAS);
				}}
			/>

			<Button
				title="Plantio"
				contained={false}
				className={modoFluxo === MODO_VIEW.PLANTIO ? "active" : ''}
				type="button"
				disabled={bloquearAbasPorCarregamentoDadosPrincipais}
				onClick={() => {
					alterarFluxo(MODO_VIEW.PLANTIO);
				}}
			/>

			<Button
				title="Cultivo"
				contained={false}
				className={modoFluxo === MODO_VIEW.CULTIVO ? "active" : ''}
				type="button"
				disabled={bloquearAbasPorCarregamentoDadosPrincipais}
				onClick={() => {
					alterarFluxo(MODO_VIEW.CULTIVO);
				}}
			/>

			<Button
				title="Colheita"
				contained={false}
				className={modoFluxo === MODO_VIEW.COLHEITA ? "active" : ''}
				type="button"
				disabled={bloquearAbasPorCarregamentoDadosPrincipais}
				onClick={() => {
					alterarFluxo(MODO_VIEW.COLHEITA);
				}}
			/>

			<Button
				title="Monitoramento"
				contained={false}
				className={modoFluxo === MODO_VIEW.MONITORAMENTO ? "active" : ''}
				type="button"
				disabled={bloquearAbasPorCarregamentoDadosPrincipais}
				onClick={() => {
					alterarFluxo(MODO_VIEW.MONITORAMENTO);
				}}
			/>

			<Button
				title="Destrui&ccedil;&atilde;o e Descarte"
				contained={false}
				className={modoFluxo === MODO_VIEW.DESTRUICAO ? "active" : ''}
				type="button"
				disabled={bloquearAbasPorCarregamentoDadosPrincipais}
				onClick={() => {
					alterarFluxo(MODO_VIEW.DESTRUICAO);
				}}
			/>

			<Button
				title="Amostragem"
				contained={false}
				className={modoFluxo === MODO_VIEW.AMOSTRAGEM ? "active" : ''}
				type="button"
				disabled={bloquearAbasPorCarregamentoDadosPrincipais}
				onClick={() => {
					alterarFluxo(MODO_VIEW.AMOSTRAGEM);
				}}
			/>
		</div>
		}

	let ParseButtonsFluxo2 = <Fragment />
	if (0 === 0) {
		ParseButtonsFluxo2 = <div className="button-position-cadernoLpma tab-buttons-local">

				<Button
					title="Avalia&ccedil;&otilde;es Agron&ocirc;micas"
					contained={false}
					className={modoFluxo === MODO_VIEW.AVALIACAOAGRONOMICA ? "active" : ''}
					type="button"
					disabled={bloquearAbasPorCarregamentoDadosPrincipais}
					onClick={() => {
						alterarFluxo(MODO_VIEW.AVALIACAOAGRONOMICA);
					}}
				/>

				<Button
					title="Destrui&ccedil;&atilde;o Bordadura"
					contained={false}
					className={modoFluxo === MODO_VIEW.DESTRUICAOBORDADURA ? "active" : ''}
					type="button"
					disabled={bloquearAbasPorCarregamentoDadosPrincipais}
					onClick={() => {
						alterarFluxo(MODO_VIEW.DESTRUICAOBORDADURA);
					}}
				/>

				<Button
					title="Plantio de Outra Cultura"
					contained={false}
					className={modoFluxo === MODO_VIEW.PLANTIOOUTRACULTURA ? "active" : ''}
					type="button"
					disabled={bloquearAbasPorCarregamentoDadosPrincipais}
					onClick={() => {
						alterarFluxo(MODO_VIEW.PLANTIOOUTRACULTURA);
					}}
				/>

				<Button
					title="Extens&atilde;o Monitoramento"
					contained={false}
					className={modoFluxo === MODO_VIEW.EXTENSAOMONITORAMENTO ? "active" : ''}
					type="button"
					disabled={bloquearAbasPorCarregamentoDadosPrincipais}
					onClick={() => {
						alterarFluxo(MODO_VIEW.EXTENSAOMONITORAMENTO);
					}}
				/>

				<Button
					title="Reportes"
					contained={false}
					className={modoFluxo === MODO_VIEW.REPORTES ? "active" : ''}
					type="button"
					disabled={bloquearAbasPorCarregamentoDadosPrincipais}
					onClick={() => {
						alterarFluxo(MODO_VIEW.REPORTES);
					}}
				/>

				<Button
					title="Anota&ccedil;&otilde;es"
					contained={false}
					className={modoFluxo === MODO_VIEW.ANOTACOES ? "active" : ''}
					type="button"
					disabled={bloquearAbasPorCarregamentoDadosPrincipais}
					onClick={() => {
						alterarFluxo(MODO_VIEW.ANOTACOES);
					}}
				/>

				<Button
					title="Assinatura"
					contained={false}
					className={modoFluxo === MODO_VIEW.ASSINATURA ? "active" : ''}
					type="button"
					disabled={bloquearAbasPorCarregamentoDadosPrincipais}
					onClick={() => {
						alterarFluxo(MODO_VIEW.ASSINATURA);
					}}
				/>
		</div>;

	}

	return <div className="container container-fluid caderno-lpma-edicao">
		<If condition={success && !error}>
			<AlertSuccess
				message={STRINGS.ALERT_MESSAGE_SUCCESS}
			/>
		</If>
		<If condition={loading}>
			<LoadingInForm />
		</If>
		<If condition={!success && error !== null}>
			{error !== null && error.message.length > 0 ? error.message.map((errorE: string) => <AlertError message={errorE} />) : <AlertError message={STRINGS.ALERT_GENERIC_ERROR} />}
		</If>
		<Snackbar
			open={toast.open}
			autoHideDuration={6000}
			onClose={() => setToast((atual) => ({ ...atual, open: false }))}
			anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
		>
			<MuiAlert
				onClose={() => setToast((atual) => ({ ...atual, open: false }))}
				severity={toast.severity}
				variant="filled"
			>
				{toast.message}
			</MuiAlert>
		</Snackbar>
		<h3>{mode === 'edit' ? STRINGS_CADERNOLPMA.EDITION_CADERNO_LPMA : STRINGS_CADERNOLPMA.REGISTER_CADERNO_LPMA}</h3>
		<Form onSubmit={handleSubmit(onSubmit)}>
			<Form.Row className="row-custom">
				<Form.Group as={Col}>
					{ParseButtonsFluxo1}
					{ParseButtonsFluxo2}
				</Form.Group>
			</Form.Row>

			{/*Dados Principais e Documentos*/}
			<If condition={modoFluxo === MODO_VIEW.DADOS_PRINCIPAIS}>
			<If condition={bloquearAbasPorCarregamentoDadosPrincipais}>
				<div className="alert alert-info" role="alert">
					<strong>Carregando dados principais...</strong> Aguarde enquanto os campos Cultura, Nome Científico e Técnico Principal são carregados.
				</div>
			</If>
			<Fieldset disabled={!canEditDadosPrincipais} title={STRINGS_CADERNOLPMA.BLOCK_ONE}>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>Processo Interno BASF (n&uacute;mero xxx/xx)</Form.Label>
							<Form.Control
								ref={registerDefaultInput}
								name="processoInternoBasf"
								placeholder="Insira o n&uacute;mero do processo interno BASF"
								maxLength={100}
								defaultValue={entity?.processoInternoBasf}
							/>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>T&iacute;tulo da LPMA</Form.Label>
							<Form.Control
								ref={registerDefaultInput}
								name="tituloLPMA"
								placeholder="Insira o T&iacute;tulo da LPMA"
								maxLength={200}
								defaultValue={entity?.tituloLPMA}
							/>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>Processo CTNBio</Form.Label>
							<Form.Control
								ref={registerDefaultInput}
								name="processoCTNBio"
								placeholder="Insira o c&oacute;digo do processo"
								maxLength={100}
								defaultValue={entity?.processoCTNBio}
							/>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>Projeto</Form.Label>
							<Form.Control
								ref={registerDefaultInput}
								name="projeto"
								placeholder="Insira o c&oacute;digo do Projeto"
								maxLength={100}
								defaultValue={entity?.projeto}
							/>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Cultura</Form.Label>
							<Controller
								control={defaultForm.control}
								name="cultura"
								defaultValue={entity?.cultura ?? null}
								render={({ onChange, value }) => (
									<Select
										options={culturas.map((culture: any) => ({ label: culture.nome, value: culture.id }))}
										placeholder="Selecione uma cultura/organismo"
										isClearable
										onChange={(selected) => {
											setCulturaSelecionada((selected as SelectOption) ?? null);
											onChange(selected);
										}}
										value={value ?? culturaSelecionada ?? null}
									/>
								)}
							/>
							<If condition={errors.culturaId}>
								<span className="error-field">{errors.culturaId?.message}</span>
							</If>
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Nome Cient&iacute;fico</Form.Label>
							<Controller
								control={defaultForm.control}
								name="nomeCientifico"
								defaultValue={entity?.nomeCientifico ?? null}
								render={({ onChange, value }) => (
									<Select
										placeholder="Selecione o Nome Cientifico"
										options={enumNomeCientifico.map((nomeCientifico) => ({ value: nomeCientifico.key, label: nomeCientifico.value }))}
										isClearable
										onChange={(selected) => {
											setNomeCientificoSelecionado((selected as SelectOption) ?? null);
											onChange(selected);
										}}
										value={value ?? nomeCientificoSelecionado ?? null}
									/>
								)}
							/>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={4}>
							<Form.Label>Local</Form.Label>
							<Controller
								control={defaultForm.control}
								name="local"
								defaultValue={entity?.local ?? ''}
								render={({ onChange, value }) => {
									const selectedOption = value ? { label: value, value } : null;
									return (
										<CreatableSelect
											isClearable
											placeholder="Selecione ou digite o local"
											value={selectedOption}
											options={opcoesLocais}
											noOptionsMessage={() => 'Nenhum local encontrado'}
											formatCreateLabel={(inputValue) => `Adicionar "${inputValue}"`}
											onChange={(selected) => {
												const novoValor = (selected as LocalOption | null)?.value ?? '';
												onChange(novoValor);
												adicionarOpcaoLocalSeNecessario(novoValor);
											}}
											onCreateOption={(inputValue) => {
												const novoOption: LocalOption = { label: inputValue, value: inputValue };
												setOpcoesLocais((prev) => deduplicarOpcoesLocais([...prev, novoOption]));
												onChange(inputValue);
											}}
										/>
									);
								}}
							/>
						</Form.Group>
						<Form.Group as={Col} md={4}>
							<Form.Label>UF (Unidade Federativa)</Form.Label>
							<Form.Control
								ref={registerDefaultInput}
								name="uf"
								placeholder="Selecione ou digite o estado"
								maxLength={30}
								defaultValue={entity?.uf}
							/>
						</Form.Group>
						<Form.Group as={Col} md={4}>
							<Form.Label>Cidade</Form.Label>
							<Form.Control
								ref={registerDefaultInput}
								name="municipio"
								placeholder="Selecione ou digite a cidade"
								defaultValue={entity?.municipio}
							/>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={6}>
							<Form.Label>Respons&aacute;vel T&eacute;cnico pelo ensaio no campo</Form.Label>
							<Controller
								control={defaultForm.control}
								name="responsavelTecnicoEnsaioCampo"
								defaultValue={entity?.responsavelTecnicoEnsaioCampo ?? null}
								render={({ onChange, value }) => (
									<Select
										options={tecnicosResponsaveisCampo.map((tecnico) => ({ label: tecnico.nome, value: tecnico.id }))}
										placeholder="Selecione um respons&aacute;vel t&eacute;cnico"
										isClearable
										onChange={(selected) => {
											setResponsavelTecnicoEnsaioCampoSelecionado((selected as SelectOption) ?? null);
											onChange(selected);
										}}
										value={value ?? responsavelTecnicoEnsaioCampoSelecionado ?? null}
									/>
								)}
							/>
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label>Assistente T&eacute;cnico (AT)</Form.Label>
							<Controller
								control={defaultForm.control}
								name="assistenteTecnicoEnsaioCampo"
								defaultValue={entity?.assistenteTecnicoEnsaioCampo ?? null}
								render={({ onChange, value }) => (
									<Select
										options={tecnicosResponsaveisCampo.map((tecnico) => ({ label: tecnico.nome, value: tecnico.id }))}
										placeholder="Selecione um assistente t&eacute;cnico (opcional)"
										isClearable
										onChange={(selected) => {
											setAssistenteTecnicoEnsaioCampoSelecionado((selected as SelectOption) ?? null);
											onChange(selected);
										}}
										value={value ?? assistenteTecnicoEnsaioCampoSelecionado ?? null}
									/>
								)}
							/>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>T&eacute;cnico Principal</Form.Label>
							<Controller
								control={defaultForm.control}
								name="tecnicoPrincipal"
								defaultValue={entity?.tecnicoPrincipal ?? null}
								render={({ onChange, value }) => (
									<Select
										options={tecnicosPrincipais.map((tecnico) => ({ label: tecnico.nome, value: tecnico.id }))}
										placeholder="Selecione um t&eacute;cnico principal"
										isClearable
										onChange={(selected) => {
											setTecnicoPrincipalSelecionado((selected as SelectOption) ?? null);
											onChange(selected);
										}}
										value={value ?? tecnicoPrincipalSelecionado ?? null}
									/>
								)}
							/>
							<If condition={errors.tecnicoPrincipalId}>
								<span className="error-field">{errors.tecnicoPrincipalId?.message}</span>
							</If>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={4} lg={4}>
							<Form.Label>Total de Ensaios aprovados (selecionar n&uacute;mero)</Form.Label>
							<Form.Control
								ref={registerDefaultInput}
								name="totalEnsaiosAprovados"
								type="number"
								min={0}
								step={1}
								placeholder="Selecione o n&uacute;mero"
								defaultValue={entity?.totalEnsaiosAprovados ?? ''}
							/>
						</Form.Group>
						<Form.Group as={Col} md={4} lg={4}>
							<Form.Label>Total de Ensaios efetivamente plantados</Form.Label>
							<Form.Control
								ref={registerDefaultInput}
								name="totalEnsaios"
								type="number"
								min={0}
								step={1}
								inputMode="numeric"
								pattern="[0-9]*"
								onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
									const teclasNaoPermitidas = ['e', 'E', '+', '-', '.', ','];
									if (teclasNaoPermitidas.includes(event.key)) {
										event.preventDefault();
									}
								}}
								onInput={(event: React.FormEvent<HTMLInputElement>) => {
									const input = event.currentTarget;
									input.value = input.value.replace(/\D/g, '');
								}}
								placeholder="Selecione o n&uacute;mero"
								defaultValue={entity?.totalEnsaios ?? ''}
							/>
						</Form.Group>
						<Form.Group as={Col} md={4} lg={4}>
							<Form.Label>Nome do ensaio efetivamente plantado</Form.Label>
							<Form.Control
								ref={registerDefaultInput}
								name="ensaios"
								placeholder="inserir (ensaio 1, ensaio 2...)"
								defaultValue={entity?.ensaios}
							/>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<FilesUpload
							key={`documentos-principais-${entity?.id ?? 'novo'}-${documentosPrincipaisDefaultValue.map((doc) => `${doc.guid}:${doc.name}`).join('|')}`}
							form={defaultForm}
							name="documentosPrincipaisFilesTemp"
							nameGuid="documentosPrincipaisGuids"
							nameFile="documentosPrincipaisNomes"
							nameValidCheck="documentosPrincipaisGuids"
							label="Documentos principais da LPMA"
							layoutColumns={12}
							required={false}
							multiple={true}
							enabled={canEditDadosPrincipais}
							isInvalidCheck={false}
							defaultValue={documentosPrincipaisDefaultValue}
							accept="application/pdf,.doc,.docx,.xls,.xlsx,.csv,.ods,.txt"
							iconView="ANEXO"
							textButton="+ Adicionar"
							onChange={(arquivos) => setDocumentosPrincipaisUpload(arquivos)}
							ClickFile={(arquivo, isLocal) => {
								if (isLocal) {
									return;
								}

								const guid = (arquivo?.guid ?? '').toString().trim();
								if (!guid) {
									return;
								}

								const documento = obterDocumentosPrincipaisDaEntidade(entity).find(
									(item) => item.arquivoGuid === guid,
								);
								if (documento) {
									baixarDocumentoPrincipal(documento);
								}
							}}
						/>
					</Form.Row>
				</Fieldset>
			</If>

			{/*Transporte de Material*/}
			<If condition={modoFluxo === MODO_VIEW.TRANSPORTE}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_TWO}>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={5}>
							<AddButton
								type="button"
				onClick={() => abrirModalTransporte()}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedTransportes.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedTransportes}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsTransportes,
								}}
							>
								<AgGridColumn headerName="Origem" minWidth={140} flex={1} valueGetter={({ data }: any) => formatarOrigemTransporte(data)} />
								<AgGridColumn headerName="Processo CTNBio" minWidth={140} flex={1} valueGetter={({ data }: any) => textoGradeLpma(data?.processoCIBIO)} />
								<AgGridColumn headerName="Local de origem" minWidth={180} flex={1.4} valueGetter={({ data }: any) => [textoGradeLpma(data?.localOrigemCampo), textoGradeLpma(data?.localOrigemCage)].filter(Boolean).join(' | ')} />
								<AgGridColumn headerName="AAC" minWidth={110} flex={1} valueGetter={({ data }: any) => textoGradeLpma(data?.aac)} />
								<AgGridColumn headerName="AMI" minWidth={120} flex={1} valueGetter={({ data }: any) => formatarAmisTransporte(data)} />
								<AgGridColumn headerName="Responsável" minWidth={150} flex={1} valueGetter={({ data }: any) => textoGradeLpma(data?.responsavel)} />
								<AgGridColumn headerName="Destino" minWidth={160} flex={1} valueGetter={({ data }: any) => formatarDestinoTransporte(data)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>

				</Fieldset>
			</If>

			{/*Registro de Visitas*/}
			<If condition={modoFluxo === MODO_VIEW.REGISTROVISITAS}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_THREE}>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={5}>
							<AddButton
								type="button"
								onClick={() => abrirModalRegistroVisita()}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedRegistroVisitas.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedRegistroVisitas}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsRegistroVisitas,
								}}
							>
								<AgGridColumn headerName="Nome do Visitante" minWidth={200} valueGetter={({ data }: any) => textoGradeLpma(data?.nomeVisitante)} />
								<AgGridColumn headerName="Motivo da Visita" minWidth={220} valueGetter={({ data }: any) => textoGradeLpma(data?.motivoVisita)} />
								<AgGridColumn headerName="Data da Visita" minWidth={150} valueGetter={({ data }: any) => dataGradeLpma(data?.dataVisita)} />
								<AgGridColumn headerName="Formulário anexado" minWidth={160} valueGetter={({ data }: any) => (textoGradeLpma(data?.formularioPreenchidoNomeArquivo) ? 'Sim' : 'Não')} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>

			{/*Plantio*/}
			<If condition={modoFluxo === MODO_VIEW.PLANTIO}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_FOUR}>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							{culturaEhSoja ? (
								<>
									<h4>Formulário – Registro de atividades/informações do Plantio (Soja)</h4>
									<p>
										Preencha o formulário conforme o FCN 20/21, registrando colaboradores, delineamento aprovado x plantado,
										coordenadas geográficas, limpeza de maquinários e checklist de biossegurança do plantio de soja.
									</p>
								</>
							) : culturaEhAlgodao ? (
								<>
									<h4>Formulário – Registro de atividades/informações do Plantio (Algodão)</h4>
									<p>
										Utilize o mesmo formulário detalhado do fluxo de soja para registrar o plantio de algodão,
										incluindo colaboradores, delineamento aprovado x plantado, coordenadas,
										limpeza de maquinários e checklist de biossegurança.
									</p>
								</>
							) : (
								<>
									<h4>Formulário – Registro de atividades/informações do Plantio</h4>
									<p>
										Registre os dados de plantio aplicáveis ao layout atual (milho), incluindo opções de isolamento,
										coordenadas, limpeza e checklists específicos.
									</p>
								</>
							)}
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={5}>
							<AddButton
								type="button"
								onClick={() =>
									culturaUsaPlantioSojaLayout ? abrirModalPlantioSoja() : abrirModalPlantioMilho()
								}
							/>
						</Form.Group>
					</Form.Row>

					<If condition={culturaUsaPlantioSojaLayout}>
						<If condition={selectedPlantiosSoja.length}>
							<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
								<AgGridReact
									rowData={selectedPlantiosSoja}
									defaultColDef={defaultColDefGradeLpma}
									gridOptions={gridOptionsGradeLpma}
									stopEditingWhenGridLosesFocus={true}
									singleClickEdit={true}
									frameworkComponents={{
										actions: actionsPlantiosSoja,
									}}
								>
									<AgGridColumn headerName="Identificação" minWidth={140} valueGetter={({ data }: any) => textoGradeLpma(data?.identificacaoPlantio)} />
									<AgGridColumn headerName="Data início" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataInicioPlantio)} />
									<AgGridColumn headerName="Data término" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataTerminoPlantio)} />
									<AgGridColumn headerName="Talhão" minWidth={120} valueGetter={({ data }: any) => primeiroTextoGradeLpma(data?.talhao, data?.formulario2Talhao)} />
									<AgGridColumn headerName="Tipo realizado" minWidth={140} valueGetter={({ data }: any) => primeiroTextoGradeLpma(formatarTipoPlantio(data?.tipoPlantioRealizadoMecanico, data?.tipoPlantioRealizadoManual), formatarTipoPlantio(data?.formulario2TipoPlantioRealizadoMecanico, data?.formulario2TipoPlantioRealizadoManual))} />
									<AgGridColumn headerName="Área plantada (m²)" minWidth={140} valueGetter={({ data }: any) => primeiroTextoGradeLpma(data?.resumoTotalPlantadoM2, data?.formulario2ResumoTotalPlantadoM2)} />
									<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
								</AgGridReact>
							</div>
						</If>
						<If condition={!selectedPlantiosSoja.length}>
							<p className="text-muted">
								{culturaEhAlgodao
									? 'Nenhum registro de plantio de algodão adicionado.'
									: 'Nenhum registro de plantio de soja adicionado.'}
							</p>
						</If>
					</If>

					<If condition={!culturaUsaPlantioSojaLayout}>
						<If condition={selectedPlantiosMilho.length}>
							<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
								<AgGridReact
									rowData={selectedPlantiosMilho}
									defaultColDef={defaultColDefGradeLpma}
									gridOptions={gridOptionsGradeLpma}
									stopEditingWhenGridLosesFocus={true}
									singleClickEdit={true}
									frameworkComponents={{
										actions: actionsPlantiosMilho,
									}}
								>
									<AgGridColumn headerName="Identificação" minWidth={140} valueGetter={({ data }: any) => textoGradeLpma(data?.identificacaoPlantio)} />
									<AgGridColumn headerName="Data início" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataInicioPlantio)} />
									<AgGridColumn headerName="Data término" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataTerminoPlantio)} />
									<AgGridColumn headerName="Talhão" minWidth={120} valueGetter={({ data }: any) => textoGradeLpma(data?.talhaoPlantio)} />
									<AgGridColumn headerName="Tipo realizado" minWidth={140} valueGetter={({ data }: any) => formatarTipoPlantio(data?.tipoPlantioRealizadoMecanico, data?.tipoPlantioRealizadoManual)} />
									<AgGridColumn headerName="Área plantada (m²)" minWidth={140} valueGetter={({ data }: any) => textoGradeLpma(data?.areaTotalPlantado)} />
									<AgGridColumn headerName="Colaborador" minWidth={150} valueGetter={({ data }: any) => textoGradeLpma(data?.colaboradorResponsavelPlantio)} />
									<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
								</AgGridReact>
							</div>
						</If>
						<If condition={!selectedPlantiosMilho.length}>
							<p className="text-muted">Nenhum registro de plantio adicionado.</p>
						</If>
					</If>

				</Fieldset>
			</If>

			{/*Cultivo*/}
			<If condition={modoFluxo === MODO_VIEW.CULTIVO}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_FIVE}>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							{culturaUsaPlantioSojaLayout ? (
								<>
									<h4>
										Formulário – Registro de atividades/informações do Cultivo ({culturaEhAlgodao ? 'Algodão' : 'Soja'})
									</h4>
									<p>
										Ao clicar em &quot;Adicionar&quot;, será aberto o formulário de cultivo específico dessa cultura,
										onde você registra o acompanhamento das parcelas, intervenções realizadas e observações de
										biossegurança durante o ciclo.
									</p>
								</>
							) : (
								<>
									<h4>Formulário – Registro de atividades/informações do Cultivo</h4>
									<p>
										Use o formulário de cultivo correspondente para documentar o progresso da cultura em
										campo, incluindo registros de manejo, monitoramentos e demais informações obrigatórias.
									</p>
								</>
							)}
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Row className="row-custom">
							<Form.Group as={Col}>
								<AddButton
									type="button"
									onClick={() => abrirModalCultivo()}
								/>
							</Form.Group>
						</Form.Row>
						<If condition={selectedCultivos.length}>
							<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
								<AgGridReact
									rowData={selectedCultivos}
									defaultColDef={defaultColDefGradeLpma}
									gridOptions={gridOptionsGradeLpma}
									stopEditingWhenGridLosesFocus={true}
									singleClickEdit={true}
									frameworkComponents={{
										actions: actionsCultivos	,
									}}
								>
									<AgGridColumn headerName="Talhão" minWidth={120} valueGetter={({ data }: any) => textoGradeLpma(data?.talhaoCultivo)} />
									<AgGridColumn headerName="Experimento" minWidth={140} valueGetter={({ data }: any) => textoGradeLpma(data?.experimentoCultivo)} />
									<AgGridColumn headerName="Data início" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataInicioCultivo)} />
									<AgGridColumn headerName="Data término" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataTerminoCultivo)} />
									<AgGridColumn headerName="Responsável" minWidth={150} valueGetter={({ data }: any) => primeiroTextoGradeLpma(data?.responsavelCultivo, responsaveisLimpezaGradeLpma(data?.limpezaMaquinariosCultivoJson))} />
									<AgGridColumn headerName="Atividades" minWidth={180} valueGetter={({ data }: any) => formatarAtividadesCultivo(data)} />
									<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
								</AgGridReact>
							</div>
						</If>
					</Form.Row>
				</Fieldset>
			</If>

			{/*Colheita*/}
			<If condition={modoFluxo === MODO_VIEW.COLHEITA}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_SIX}>
					<Form.Row className="row-custom">
						<Form.Group as={Col}>
							<AddButton
								type="button"
								onClick={() => setModalColheitas(true)}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedColheitas.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
										rowData={selectedColheitas}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsColheitas,
								}}
							>
								<AgGridColumn
									headerName="Data início"
									minWidth={120}
									valueGetter={({ data }: any) => {
										const { dataInicio } = obterDatasColheitaParaGrid(data);
										return dataGradeLpma(dataInicio);
									}}
								/>
								<AgGridColumn
									headerName="Data término"
									minWidth={120}
									valueGetter={({ data }: any) => {
										const { dataTermino } = obterDatasColheitaParaGrid(data);
										return dataGradeLpma(dataTermino);
									}}
								/>
								<AgGridColumn headerName="Responsável" minWidth={150} valueGetter={({ data }: any) => textoGradeLpma(data?.responsavelColheita)} />
								<AgGridColumn headerName="Tipo" minWidth={120} valueGetter={({ data }: any) => formatTipoColheita(data)} />
								<AgGridColumn headerName="Destino" minWidth={180} valueGetter={({ data }: any) => formatarDestinosColheita(data)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>
			{/*Monitoramento*/}
			<If condition={modoFluxo === MODO_VIEW.MONITORAMENTO}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_SEVEN}>
					<Form.Row className="row-custom">
						<Form.Group as={Col}>
							<AddButton
								type="button"
								onClick={() => setModalMonitoramentos(true)}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedMonitoramentos.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedMonitoramentos}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsMonitoramentos,
								}}
							>
								<AgGridColumn headerName="Talhão" minWidth={120} valueGetter={({ data }: any) => textoGradeLpma(data?.talhaoMonitoramento)} />
								<AgGridColumn headerName="Experimento" minWidth={140} valueGetter={({ data }: any) => textoGradeLpma(data?.experimentoMonitoramento)} />
								<AgGridColumn headerName="Data início" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataInicioMonitoramento)} />
								<AgGridColumn headerName="Data término" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataTerminoMonitoramento)} />
								<AgGridColumn headerName="Responsável" minWidth={150} valueGetter={({ data }: any) => textoGradeLpma(data?.responsavelMonitoramento)} />
								<AgGridColumn headerName="AMI" minWidth={110} valueGetter={({ data }: any) => textoGradeLpma(data?.numeroAMI)} />
								<AgGridColumn headerName="Método" minWidth={160} valueGetter={({ data }: any) => formatarMetodoDestruicaoMonitoramento(data)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>

			{/*Destrui��o*/}
			<If condition={modoFluxo === MODO_VIEW.DESTRUICAO}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_EIGHT}>
					<Form.Row className="row-custom">
						<Form.Group as={Col}>
							<AddButton
								type="button"
								onClick={() => setModalDestruicaoDescartes(true)}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedDestruicaoDescartes.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedDestruicaoDescartes}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsDestruicaoDescartes,
								}}
							>
								<AgGridColumn headerName="Data início" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataInicioDestruicao)} />
								<AgGridColumn headerName="Data término" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataTerminoDestruicao)} />
								<AgGridColumn headerName="Responsável" minWidth={150} valueGetter={({ data }: any) => textoGradeLpma(data?.responsavelDestruicao)} />
								<AgGridColumn headerName="Material" minWidth={160} valueGetter={({ data }: any) => formatarTipoMaterialDestruicao(data)} />
								<AgGridColumn headerName="Peso (kg)" minWidth={110} valueGetter={({ data }: any) => textoGradeLpma(data?.pesoTotalAmostras)} />
								<AgGridColumn headerName="Local" minWidth={140} valueGetter={({ data }: any) => formatarLocalDestruicao(data)} />
								<AgGridColumn headerName="Método" minWidth={150} valueGetter={({ data }: any) => formatarMetodoDestruicaoDescarte(data)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>

			{/*Amostragem*/}
			<If condition={modoFluxo === MODO_VIEW.AMOSTRAGEM}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_NINE}>
					<Form.Row className="row-custom">
						<Form.Group as={Col}>
							<AddButton
								type="button"
								onClick={() => setModalAmostragens(true)}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedAmostragens.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedAmostragens}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsAmostragens,
								}}
							>
								<AgGridColumn headerName="Data início" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataInicioAmostragem)} />
								<AgGridColumn headerName="Data término" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataTerminoAmostragem)} />
								<AgGridColumn headerName="Responsável" minWidth={150} valueGetter={({ data }: any) => textoGradeLpma(data?.responsavelAmostragem)} />
								<AgGridColumn headerName="Material" minWidth={150} valueGetter={({ data }: any) => formatarTipoMaterialAmostragem(data)} />
								<AgGridColumn headerName="AMI" minWidth={110} valueGetter={({ data }: any) => textoGradeLpma(data?.numeroAMI)} />
								<AgGridColumn headerName="Quantidade" minWidth={130} valueGetter={({ data }: any) => textoGradeLpma(data?.quantidadeMaterialAmostrado)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>

			{/*Avalia��es Agron�micas*/}
			<If condition={modoFluxo === MODO_VIEW.AVALIACAOAGRONOMICA}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_TEN}>
					<Form.Row className="row-custom">
						<Form.Group as={Col}>
							<AddButton
								type="button"
								onClick={() => setModalAvaliacoesAgronomicas(true)}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedAvaliacoesAgronomicas.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedAvaliacoesAgronomicas}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsAvaliacoesAgronomicas,
								}}
							>
								<AgGridColumn headerName="Localidade" minWidth={140} valueGetter={({ data }: any) => textoGradeLpma(data?.localidadeAvaliacaoAgronomica)} />
								<AgGridColumn headerName="Data início" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataInicioAvaliacaoAgronomica)} />
								<AgGridColumn headerName="Data término" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataTerminoAvaliacaoAgronomica)} />
								<AgGridColumn headerName="Responsável" minWidth={150} valueGetter={({ data }: any) => primeiroTextoGradeLpma(data?.responsavelAvaliacaoAgronomica, responsaveisLimpezaGradeLpma(data?.limpezaEquipamentosAgronomicaJson, data?.limpezaInstalacoesAgronomicaJson))} />
								<AgGridColumn headerName="Material" minWidth={140} valueGetter={({ data }: any) => formatarTipoMaterialAvaliacao(data)} />
								<AgGridColumn headerName="Avaliação" minWidth={160} valueGetter={({ data }: any) => formatarTipoAvaliacaoAgronomica(data)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>

			{/*Destrui��o Bordadura*/}
			<If condition={modoFluxo === MODO_VIEW.DESTRUICAOBORDADURA}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_ELEVEN}>
					<Form.Row className="row-custom">
						<Form.Group as={Col}>
							<AddButton
								type="button"
								onClick={() => setModalDestruicaoBordaduras(true)}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedDestruicaoBordaduras.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedDestruicaoBordaduras}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsDestruicaoBordaduras,
								}}
							>
								<AgGridColumn headerName="Data inicial" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataDestruicaoBordadura)} />
								<AgGridColumn headerName="Data final" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataFinalDestruicaoBordadura)} />
								<AgGridColumn headerName="Quadra/Talhão" minWidth={130} valueGetter={({ data }: any) => textoGradeLpma(data?.talhaoDestruicaoBordadura)} />
								<AgGridColumn headerName="Estádio" minWidth={120} valueGetter={({ data }: any) => textoGradeLpma(data?.estadioFenologicoDestruicaoBordadura)} />
								<AgGridColumn headerName="Método" minWidth={150} valueGetter={({ data }: any) => formatarMetodoDestruicaoBordadura(data)} />
								<AgGridColumn headerName="Responsável" minWidth={150} valueGetter={({ data }: any) => textoGradeLpma(data?.responsavelDestruicaoBordadura)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>

			{/*Plantio de Outra Cultura*/}
			<If condition={modoFluxo === MODO_VIEW.PLANTIOOUTRACULTURA}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_TWELVE}>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={5}>
							<AddButton
								type="button"
								onClick={() => setModalPlantiosOutraCultura(true)}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedPlantiosOutraCultura.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedPlantiosOutraCultura}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsPlantiosOutraCultura,
								}}
							>
								<AgGridColumn headerName="Data início" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataInicioPlantioOutraCultura)} />
								<AgGridColumn headerName="Data término" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataTerminoPlantioOutraCultura)} />
								<AgGridColumn headerName="Talhão" minWidth={120} valueGetter={({ data }: any) => textoGradeLpma(data?.talhaoPlantioOutraCultura)} />
								<AgGridColumn headerName="Cultura" minWidth={140} valueGetter={({ data }: any) => textoGradeLpma(data?.culturaPlantadaPlantioOutraCultura)} />
								<AgGridColumn headerName="Responsável" minWidth={150} valueGetter={({ data }: any) => primeiroTextoGradeLpma(data?.responsavelPlantioOutraCultura, responsaveisLimpezaGradeLpma(data?.limpezaPlantioOutraCulturaJson))} />
								<AgGridColumn headerName="Previsto na LPMA" minWidth={140} valueGetter={({ data }: any) => formatBooleanDisplay(data?.previsaoLPMA)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>

				</Fieldset>
			</If>

			{/*Extens�o Monitoramento*/}
			<If condition={modoFluxo === MODO_VIEW.EXTENSAOMONITORAMENTO}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_THIRTEEN}>
					<Form.Row className="row-custom">
						<Form.Group as={Col}>
							<AddButton
								type="button"
								onClick={() => setModalExtensaoMonitoramentos(true)}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedExtensaoMonitoramentos.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedExtensaoMonitoramentos}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsExtensaoMonitoramentos,
								}}
							>
								<AgGridColumn headerName="Previsto na LPMA" minWidth={140} valueGetter={({ data }: any) => formatBooleanDisplay(data?.previsaoLPMA)} />
								<AgGridColumn headerName="Data início" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataInicioExtensaoMonitoramento)} />
								<AgGridColumn headerName="Responsável início" minWidth={160} valueGetter={({ data }: any) => textoGradeLpma(data?.responsavelInicioExtensaoMonitoramento)} />
								<AgGridColumn headerName="Data término" minWidth={120} valueGetter={({ data }: any) => dataGradeLpma(data?.dataTerminoExtensaoMonitoramento)} />
								<AgGridColumn headerName="Responsável término" minWidth={160} valueGetter={({ data }: any) => textoGradeLpma(data?.responsavelTerminoExtensaoMonitoramento)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>

			{/*Reportes e Relatórios*/}
			<If condition={modoFluxo === MODO_VIEW.REPORTES}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_FOURTEEN}>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<h4>Formulário - Reportes e Relatórios</h4>
							<p>
								Este formulário deve ser utilizado apenas para reporte de eventos caracterizados como incidentes e/ou acidentes envolvendo material GM regulamentado pela Lei 11.105 de 24 de março de 2005.
							</p>
							<p>
								<strong>Atenção:</strong> em caso de identificação de qualquer evento, contate imediatamente a CIBio antes de registrar as informações no sistema.
							</p>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col}>
							<AddButton
								type="button"
								onClick={() => setModalReportesRelatorios(true)}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedReportesRelatorios.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedReportesRelatorios}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsReportesRelatorios,
								}}
							>
								<AgGridColumn headerName="Colaborador" minWidth={150} valueGetter={({ data }: any) => textoGradeLpma(data?.nomeColaboradorEvento)} />
								<AgGridColumn headerName="Data/Hora" minWidth={140} valueGetter={({ data }: any) => (data?.dataHoraEvento ? moment(data.dataHoraEvento).format('DD/MM/YYYY HH:mm') : '')} />
								<AgGridColumn headerName="Classificação" minWidth={130} valueGetter={({ data }: any) => formatClassificacaoEvento(data?.classificacaoEvento)} />
								<AgGridColumn headerName="Motivo" minWidth={180} valueGetter={({ data }: any) => formatarMotivoReporte(data)} />
								<AgGridColumn headerName="Fora da LPMA?" minWidth={130} valueGetter={({ data }: any) => formatBooleanDisplay(data?.liberacaoForaLPMA)} />
								<AgGridColumn headerName="Reporte à CIBio" minWidth={140} valueGetter={({ data }: any) => dataGradeLpma(data?.dataReporteCIBio)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>

			{/*Anota��es*/}
			<If condition={modoFluxo === MODO_VIEW.ANOTACOES}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_FIFTEEN}>
					<Form.Row className="row-custom">
						<Form.Group as={Col}>
							<AddButton
								type="button"
								onClick={() => abrirModalAnotacoes()}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedAnotacoes.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedAnotacoes}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={gridOptionsGradeLpma}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsAnotacoes,
								}}
							>
								<AgGridColumn headerName="Data" minWidth={140} valueGetter={({ data }: any) => dataGradeLpma(data?.dataAnotacao)} />
								<AgGridColumn headerName="Usuário" minWidth={180} valueGetter={({ data }: any) => textoGradeLpma(data?.usuarioAnotacao)} />
								<AgGridColumn headerName="Texto" minWidth={280} valueGetter={({ data }: any) => textoGradeLpma(data?.textoAnotacao)} />
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>

			{/*Assinatura*/}
			<If condition={modoFluxo === MODO_VIEW.ASSINATURA}>
				<Fieldset disabled={!canEditOutrasAbas} title={STRINGS_CADERNOLPMA.BLOCK_SIXTEEN}>
					<Form.Row className="row-custom">
						<Form.Group as={Col}>
							<AddButton
								type="button"
								onClick={() => abrirModalAssinaturas()}
							/>
						</Form.Group>
					</Form.Row>
					<If condition={selectedAssinaturas.length}>
						<div className="ag-theme-alpine" style={estiloContainerGradeLpma}>
							<AgGridReact
								rowData={selectedAssinaturas}
								defaultColDef={defaultColDefGradeLpma}
								gridOptions={{ ...gridOptionsGradeLpma, rowHeight: 64 }}
								stopEditingWhenGridLosesFocus={true}
								singleClickEdit={true}
								frameworkComponents={{
									actions: actionsAssinaturas,
								}}
							>
								<AgGridColumn headerName="Nome do colaborador" field="nomeColaborador" />
								<AgGridColumn headerName="Cargo" field="cargoColaborador" />
								<AgGridColumn headerName="Data" valueGetter={({ data }: any) => dataGradeLpma(data?.dataAssinatura)} />
								<AgGridColumn
									headerName="Assinatura"
									cellRendererFramework={({ data }: any) => data.assinaturaBase64 ? (
										<img
											src={data.assinaturaBase64}
											alt="Assinatura"
											style={{ maxWidth: '140px', maxHeight: '36px', backgroundColor: '#fff' }}
										/>
									) : ''}
								/>
								<AgGridColumn headerName="Ações" cellRenderer="actions" pinned="right" width={120} minWidth={120} maxWidth={120} flex={0} />
							</AgGridReact>
						</div>
					</If>
				</Fieldset>
			</If>

			<div className="button-position">
				<Button
					title="Voltar"
					contained={false}
					type="button"
					onClick={() => {
						isDirty ? setIsConfirmation(true) :
							history.goBack();
					}}
				/>
				{/*<GenericButton*/}
				{/*	title="Salvar e enviar para regulat&oacute;rio"*/}
				{/*	variant="btn-warning"*/}
				{/*	type="button"*/}
				{/*	loading={loadingSubmit}*/}
				{/*	onClick={() => trigger().then(validated => {*/}
				{/*		if (validated) {*/}
				{/*			setIsRegulatory(validated);*/}
				{/*		} else {*/}
				{/*			window.scrollTo({ top: 0, behavior: 'smooth' });*/}
				{/*		}*/}
				{/*	})}*/}
				{/*/>*/}
				<Button
					title="Salvar rascunho"
					contained={true}
					type="button"
					disabled={!(canEditDadosPrincipais || canEditOutrasAbas)}
					onClick={() => {
						alterarFluxo(modoFluxo);
						onSubmitDraft();
						}}
					isLoading={loadingDraft}
				/>
			</div>
		</Form>
		<If condition={modalTransporte}>
			<TransporteModal
				key={transporteEmEdicao ? `transporte-${transporteEmEdicao.id ?? 0}-${(transporteEmEdicao.localOrigemCampo ?? '').trim()}` : 'transporte-novo'}
				show={modalTransporte}
				onHide={fecharModalTransporte}
				onSubmit={onSubmitTransporte}
				initialData={transporteEmEdicao ?? undefined}
				processoCTNBioReferencia={processoCTNBioReferenciaTransporte}
				assinaturasDisponiveis={selectedAssinaturas}
			/>
		</If>
		<If condition={modalRegistroVisitas}>
			<RegistroVisitasModal
				show={modalRegistroVisitas}
				onHide={fecharModalRegistroVisita}
				onSubmit={onSubmitRegistroVisitas}
				initialData={registroVisitaEmEdicao ?? undefined}
				cadernoLpmaId={entity?.id ?? initialEntity?.id ?? null}
			/>
		</If>

		<If condition={modalPlantiosSoja}>
			<PlantioSojaModal
				show={true}
				onHide={fecharModalPlantioSoja}
				onSubmit={onSubmitPlantioSoja}
				initialData={plantioSojaEmEdicao ?? undefined}
				assinaturasDisponiveis={selectedAssinaturas}
			/>
		</If>
		<If condition={modalPlantiosMilho}>
			<PlantioMilhoModal
				show={true}
				onHide={fecharModalPlantioMilho}
				onSubmit={onSubmitPlantioMilho}
				initialData={plantioMilhoEmEdicao ?? undefined}
			/>
		</If>
		<If condition={modalCultivos}>
			<CultivoModal
				show={true}
				onHide={fecharModalCultivo}
				onSubmit={onSubmitCultivo}
				initialData={cultivoEmEdicao ?? undefined}
				isSoja={culturaUsaPlantioSojaLayout}
				isMilho={culturaEhMilho}
				assinaturasDisponiveis={selectedAssinaturas}
			/>
		</If>
		<If condition={modalColheitas}>
		<ColheitaModalComponent
				show={true}
				onHide={() => {
					setModalColheitas(false);
					setColheitaEmEdicao(null);
					setIndiceColheitaEdicao(null);
				}}
				onSubmit={onSubmitColheita}
				dadosColheita={colheitaEmEdicao}
				cadernoLpmaId={entity?.id ?? initialEntity?.id ?? null}
				assinaturasDisponiveis={selectedAssinaturas}
			/>
		</If>
		<If condition={modalMonitoramentos}>
			<MonitoramentoModal
				show={true}
				onHide={() => {
					setModalMonitoramentos(false);
					setMonitoramentoEmEdicao(null);
					setIndiceMonitoramentoEdicao(null);
				}}
				onSubmit={onSubmitMonitoramento}
				dadosMonitoramento={monitoramentoEmEdicao}
				assinaturasDisponiveis={selectedAssinaturas}
			/>
		</If>
		<If condition={modalDestruicaoDescartes}>
			<DestruicaoDescarteModal
				show={true}
				onHide={() => {
					setModalDestruicaoDescartes(false);
					setDestruicaoDescarteEmEdicao(null);
					setIndiceDestruicaoDescarteEdicao(null);
				}}
				onSubmit={onSubmitDestruicaoDescarte}
				dadosDestruicaoDescarte={destruicaoDescarteEmEdicao}
				assinaturasDisponiveis={selectedAssinaturas}
			/>
		</If>

		<If condition={modalAmostragens}>
			<AmostragemModal
				show={true}
				onHide={() => {
					setModalAmostragens(false);
					setAmostragemEmEdicao(null);
					setIndiceAmostragemEdicao(null);
				}}
				onSubmit={onSubmitAmostragem}
				dadosAmostragem={amostragemEmEdicao}
				cadernoLpmaId={entity?.id ?? initialEntity?.id ?? null}
				assinaturasDisponiveis={selectedAssinaturas}
			/>
		</If>
		<If condition={modalAvaliacoesAgronomicas}>
			<AvaliacaoAgronomicaModal
				show={true}
				onHide={() => {
					setModalAvaliacoesAgronomicas(false);
					setAvaliacaoAgronomicaEmEdicao(null);
					setIndiceAvaliacaoAgronomicaEdicao(null);
				}}
				onSubmit={onSubmitAvaliacaoAgronomica}
				dadosAvaliacaoAgronomica={avaliacaoAgronomicaEmEdicao}
				assinaturasDisponiveis={selectedAssinaturas}
			/>
		</If>
		<If condition={modalDestruicaoBordaduras}>
			<DestruicaoBordaduraModal
				show={true}
				onHide={() => {
					setModalDestruicaoBordaduras(false);
					setDestruicaoBordaduraEmEdicao(null);
					setIndiceDestruicaoBordaduraEdicao(null);
				}}
				onSubmit={onSubmitDestruicaoBordadura}
				dadosDestruicaoBordadura={destruicaoBordaduraEmEdicao}
			/>
		</If>
		<If condition={modalPlantiosOutraCultura}>
			<PlantioOutraCulturaModal
				show={true}
				onHide={() => {
					setModalPlantiosOutraCultura(false);
					setPlantioOutraCulturaEmEdicao(null);
					setIndicePlantioOutraCulturaEdicao(null);
				}}
				onSubmit={onSubmitPlantioOutraCultura}
				dadosPlantioOutraCultura={plantioOutraCulturaEmEdicao}
				cadernoLpmaId={entity?.id ?? initialEntity?.id ?? null}
				assinaturasDisponiveis={selectedAssinaturas}
			/>
		</If>

		<If condition={modalExtensaoMonitoramentos}>
			<ExtensaoMonitoramentoModal
				show={true}
				onHide={() => {
					setModalExtensaoMonitoramentos(false);
					setExtensaoMonitoramentoEmEdicao(null);
					setIndiceExtensaoMonitoramentoEdicao(null);
				}}
				onSubmit={onSubmitExtensaoMonitoramento}
				dadosExtensaoMonitoramento={extensaoMonitoramentoEmEdicao}
			/>
		</If>

		<If condition={modalReportesRelatorios}>
			<ReportesRelatoriosModal
				show={true}
				onHide={() => {
					setModalReportesRelatorios(false);
					setReporteRelatorioEmEdicao(null);
					setIndiceReporteRelatorioEdicao(null);
				}}
				onSubmit={onSubmitReportesRelatorios}
				dadosReportesRelatorios={reporteRelatorioEmEdicao}
				cadernoLpmaId={entity?.id ?? initialEntity?.id ?? null}
				assinaturasDisponiveis={selectedAssinaturas}
			/>
		</If>

		<If condition={modalAssinaturas}>
			<AssinaturaModal
				show={true}
				onHide={fecharModalAssinaturas}
				onSubmit={onSubmitAssinatura}
				initialData={assinaturaEmEdicao ?? undefined}
				cadernoLpmaId={entity?.id ?? initialEntity?.id ?? null}
			/>
		</If>

		<If condition={modalAnotacoes}>
			<Modal
				show={true}
				onHide={fecharModalAnotacoes}
				title={STRINGS_CADERNOLPMA.ANOTACOES_MODAL_TITLE}
				bodyClassName="caderno-lpma-modal-body"
			>
				<Form onSubmit={handleSubmitAnotacoes(onSubmitAnotacoes)} autoComplete="off">
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>Data Anotacao</Form.Label>
							<Controller
								control={controlAnotacoes}
								name="dataAnotacao"
								isInvalid={errorsAnotacoes.dataAnotacao !== undefined}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
							<If condition={errorsAnotacoes.dataAnotacao}>
								<span className="error-field">{errorsAnotacoes.dataAnotacao?.message}</span>
							</If>
						</Form.Group>
						<Form.Group as={Col} md={12}>
							<Form.Label>Usuario Anotacao</Form.Label>
							<Form.Control
								ref={registerAnotacoes}
								name="usuarioAnotacao"
								placeholder="Insira o usuario anotacao"
								maxLength={100}
							/>
							<If condition={errorsAnotacoes.usuarioAnotacao}>
								<span className="error-field">{errorsAnotacoes.usuarioAnotacao?.message}</span>
							</If>
						</Form.Group>
					</Form.Row>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>Texto Anotacao</Form.Label>
							<Form.Control
								ref={registerAnotacoes}
								name="textoAnotacao"
								placeholder="Insira o texto anotacao"
								maxLength={100}
							/>
							<If condition={errorsAnotacoes.textoAnotacao}>
								<span className="error-field">{errorsAnotacoes.textoAnotacao?.message}</span>
							</If>
						</Form.Group>
					</Form.Row>
					<div className="button-position">
						<Button
							title="Cancelar"
							contained={false}
							type="button"
							onClick={fecharModalAnotacoes}
						/>
						<Button
							title="Salvar"
							contained={true}
							type="submit"
						/>
					</div>
				</Form>
			</Modal>
		</If>

		<ConfirmationModal
			show={isConfirmation}
			onHide={() => setIsConfirmation(false)}
			onConfirm={() => {
				history.goBack();
			}}
			title={STRINGS_CADERNOLPMA.EDITION_TITLE_CONFIRMATION}
			confirmationMsg={STRINGS_CADERNOLPMA.CONFIRM_LEFT_EDITION}
		>
			<p>{STRINGS_CADERNOLPMA.EDITION_REGISTER_CONFIRMATION_CANCEL}</p>
		</ConfirmationModal>
	</div>
}